<script setup lang="ts" generic="T extends Record<string, unknown>">
import {
  AddSquareIcon,
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
  ChevronRightIcon,
  DismissIcon,
  FilterDismissIcon,
  SaveIcon,
  SettingsIcon,
  CheckmarkCircleIcon,
  ShapeIntersectIcon,
  ShapeUnionIcon,
} from '@blpde/nx-icons';
import {
  computed,
  h,
  useSlots,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  shallowRef,
  useId,
  watch,
  watchEffect,
  type DirectiveBinding,
  type Ref,
  type ComponentPublicInstance,
  type WatchHandle,
} from 'vue';
import { NxButton } from '../Button';
import { NxConfirmDeleteDialog } from '../Dialog';
import { NxEmptyState } from '../EmptyState';
import { useNativeDropTarget } from '../FileDrop/useNativeDropTarget';
import { i18nInjectionKey } from '../internal';
import NxContextMenuSeparator from '../ContextMenu/NxContextMenuSeparator.vue';
import { NxMenuDropdown, NxMenuItem } from '../Menu';
import { NxTooltip } from '../Tooltip';
import { NxDropdownList, NxDropdownListItem } from './../Dropdown';
import { buildCsv, csvFileName, downloadCsvFile } from './composables/csv';
import { useColumnDrag } from './composables/useColumnDrag';
import { useColumnResize } from './composables/useColumnResize';
import { useRowDrag } from './composables/useRowDrag';
import { useDragSelection } from './composables/useDragSelection';
import { rowDetailKey, useRowExpansion } from './composables/useRowExpansion';
import { useTableFilterState } from './composables/useTableFilterState';
import { useTableNavigation } from './composables/useTableNavigation';
import { useTablePagination } from './composables/useTablePagination';
import { useTableSelection } from './composables/useTableSelection';
import { useTableSort } from './composables/useTableSort';
import { useVirtualScroll } from './composables/useVirtualScroll';
import { cssSafeId } from './composables/utils';
import {
  debounce,
  FILTER_ROW_ID,
  INDICATOR_COLUMN_ID,
  NxTableEmits,
  NxTableExpose,
  NxTableProps,
  OrNull,
  PaginationConfig,
  PartialOrNull,
  RefetchOptions,
  registerColumKey,
  resolveCellMode,
  ROW_ACTION_COLUMN_ID,
  ROW_DENSITY_HEIGHTS,
  SELECT_COLUMN_ID,
  EXPAND_COLUMN_ID,
  SETTINGS_ROW_ID,
  TableMetadataState,
  TableState,
  ValuesInMap,
  type CellNavigationIntent,
  type FocusFrom,
  type NxTableFilterGroup,
  type NxTableRowDensity,
  type NxTableGroupHeaderSlotProps,
  type NxTableRowDetailSlotProps,
  type ColumnRegistryUpdate,
  type TableColumnConfig,
} from './internal';
import NxTableColumnDialog from './NxTableColumnDialog.vue';
import NxTableCsvExportDialog from './NxTableCsvExportDialog.vue';
// Imported by path rather than through the barrels: NxTable is itself part of
// ui/src/index.ts, so going back through it would close an import cycle.
import NxConditionalWrapper from '../lib/NxConditionalWrapper.vue';
import NxLoadingWrapper from '../Inputs/NxLoadingWrapper.vue';
import NxInputActionsAlignProvider from '../Inputs/NxInputActionsAlignProvider.vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import NxTableFilterChips from './NxTableFilterChips.vue';
import NxTableFilterSaveDialog from './NxTableFilterSaveDialog.vue';
import NxTableFilterWizard from './NxTableFilterWizard.vue';
import NxTableHeaderFilter from './NxTableHeaderFilter.vue';
import NxTablePreferencesDialog from './NxTablePreferencesDialog.vue';
import NxTablePager from './NxTablePager.vue';
import {
  resolveTableSummary,
  summaryDataIsComplete,
  type NxTableSummaryResult,
} from './tableSummary';
import NxTableFilterManageDialog from './NxTableFilterManageDialog.vue';
import { useTableWheel } from './composables/useTableWheel';
import { deriveTree, dragUnitOf, validTargets } from './composables/rowTree';
import { createTableFocusScope } from './focusScope';
import { useScrollState } from './composables/useScrollState';
import { useContentRight } from './composables/useContentRight';
import { useTableFocusOwnership } from './composables/useTableFocusOwnership';
import { moveToPositionTarget } from './composables/moveToPosition';
import { useRowGroups } from './composables/useRowGroups';
import type { RowGroupHeader } from './composables/rowGroups';
import NxTableMoveToPositionDialog from './NxTableMoveToPositionDialog.vue';
import NxTableGroupRow from './NxTableGroupRow.vue';
import NxTableSelectAllCheckbox from './NxTableSelectAllCheckbox.vue';
import { useNxSettings } from '../Settings/settingsSource';
import {
  NX_TABLE_SETTINGS_FIELDS,
  NX_TABLE_SETTINGS_KEY,
  tableChromeOf,
  tableOwnSettingsOf,
  tableSettingsFor,
  tablesWithOwnSettings,
  type NxTableOwnSettings,
  type NxTableSettings,
} from './tableSettings';
import {
  createRowOpen,
  NX_ROW_OPEN_TILES_KEY,
  NX_ROW_OPEN_BESIDE_KEY,
  NX_ROW_OPENER_KEY,
  rowOpenOf,
  type NxRowOpenPlace,
  type NxRowOpenTarget,
} from './rowOpen';

const props = withDefaults(defineProps<NxTableProps<T>>(), {
  totalCount: undefined,
  summaryDataComplete: undefined,
  onTableStateChange: undefined,

  onExportData: undefined,

  onCreate: undefined,
  onEdit: undefined,
  onDelete: undefined,
  deleteGuard: undefined,
  rowDeletable: undefined,
  cellSaveStatus: undefined,

  onRowMoved: undefined,
  rowMovable: undefined,
  onRowDragStart: undefined,
  onRowDragEnd: undefined,
  canDropRow: undefined,
  onRowDrop: undefined,
  rowDropLabel: undefined,
  rowParentId: undefined,

  paginationTriggerRemainingItemsCount: 3,
  paginationMode: 'infinite',
  paginationPageSizes: () => [],

  editMode: false,
  lazyEditCells: false,
  isPlaceholderRow: undefined,
  allowRowSelection: false,
  rowSelectionMode: 'cells',
  dragSelection: false,
  expandableRows: false,
  rowCheckboxLabel: undefined,
  selectAllLabel: undefined,
  wheelRequiresFocus: false,
  overscrollY: 'auto',

  csvFilenamePrefix: undefined,
  firstSortOrder: 'asc',
  initialRenderLimit: 50,

  hideCreateButton: false,
  createFromArrowNav: undefined,
  createFromTabNav: undefined,
  allowNavigationCreate: true,
  minColWidth: 80,
  maxColWidth: 800,
  columnGrow: undefined,
  headerRowHeight: 'calc(2.5em / 0.875)',
  // Unset: the height comes from `rowDensity` (ROW_DENSITY_HEIGHTS, em-based
  // so rows breathe with the table font/scale). An explicit value wins.
  bodyRowHeight: undefined,
  rowDensity: 'normal',
  // Unset: the user's `table.zebra` decides; `true`/`false` fix this table's stripes.
  zebra: undefined,
  rowSizing: 'auto',

  baseFilter: undefined,
  baseFilterMode: 'merge',

  rowStyle: undefined,
  rowClass: undefined,
  rowHref: undefined,

  hideHeader: false,
  layout: 'grid',
  fill: false,

  settingsKey: undefined,

  emptyTitle: undefined,
  emptyDescription: undefined,

  groupBy: undefined,
  groups: undefined,
  ungroupedLabel: 'Ohne Gruppe',
  showEmptyGroups: false,
  groupCountLabel: (count: number) => (count === 1 ? '1 Eintrag' : `${count} Einträge`),
});

// as onPagination, onChange, ... need to be async, they need to be exposed as props instead of emits
const emit = defineEmits<NxTableEmits<T>>();

// The empty body is rendered by a function in both summary layouts, so its
// public slot must remain declared even without a literal <slot> in the template.
defineSlots<{
  default?(): unknown;
  settingsDropdown?(): unknown;
  empty?(context: { hasActiveFilters: boolean; resetFilters: () => void }): unknown;
  'group-header'?(context: NxTableGroupHeaderSlotProps<T>): unknown;
  'row-detail'?(context: NxTableRowDetailSlotProps<T>): unknown;
}>();

// `allowRowSelection` is the established editing-grid switch. The explicit
// list modes are a separate opt-in so existing tables keep their current click
// and arrow behavior unchanged.
const rowSelectionEnabled = computed(
  () =>
    props.allowRowSelection ||
    props.rowSelectionMode === 'rows' ||
    props.rowSelectionMode === 'checkbox'
);
/** Checkbox lists: the active row and the selection are separate state. */
const checkboxSelection = computed(() => props.rowSelectionMode === 'checkbox');
/** Rows open a detail below themselves (`expandableRows`): the expand column exists. */
const rowExpansionEnabled = computed(() => !!props.expandableRows);
/** List modes own a row cursor and row clicks even on a read-only table. */
const listSelection = computed(
  () => props.rowSelectionMode === 'rows' || props.rowSelectionMode === 'checkbox'
);

const i18nInjection = inject(i18nInjectionKey)!;
const nativeRowDrop = useNativeDropTarget<T>({
  enabled: () => Boolean(props.canDropRow && props.onRowDrop),
  key: (row) => props.rowIdKey(row),
  canDrop: (event, row) =>
    !props.isPlaceholderRow?.(row) && (props.canDropRow?.(event, row) ?? false),
  onDrop: (event, row) => props.onRowDrop?.(event, row),
  label: (row) => props.rowDropLabel?.(row) ?? props.rowIdKey(row),
});
/** Teleported floating content opened from this table (see `focusScope.ts`). */
const focusScope = createTableFocusScope();

// ---- Column registration state ----
const columnConfigs = ref<Map<string, Omit<TableColumnConfig<T>, 'id'>>>(new Map());
const columnOrder = ref<string[]>([]);
const columnVisible = ref<Map<string, boolean>>(new Map());
const columnWidths = ref<Map<string, number>>(new Map());
/** Rendered auto widths held still for a drag, never saved as user preferences. */
const frozenAutoColumnWidths = new Set<string>();

function releaseFrozenAutoColumnWidths(): void {
  for (const columnId of frozenAutoColumnWidths) columnWidths.value.delete(columnId);
  frozenAutoColumnWidths.clear();
}

const userVisibleColumnOrder = computed(() => {
  return columnOrder.value.filter((c) => columnVisible.value.get(c));
});

// Includes the virtual sentinel columns so `useTableNavigation` walks them.
// Template v-for loops over *user* columns must use `userVisibleColumnOrder`.
const visibleColumnOrder = computed(() => [
  INDICATOR_COLUMN_ID,
  // The expand toggle lives in the indicator column's cell: a stop of its own
  // for the arrow keys, right after the row anchor.
  ...(rowExpansionEnabled.value ? [EXPAND_COLUMN_ID] : []),
  ...(checkboxSelection.value ? [SELECT_COLUMN_ID] : []),
  ...userVisibleColumnOrder.value,
  ROW_ACTION_COLUMN_ID,
]);

/** Grid columns in front of the user columns: the indicator, plus the checkbox column. */
const leadingColumnCount = computed(() => (checkboxSelection.value ? 2 : 1));
/** `aria-colindex` of the user column at `idx` in `userVisibleColumnOrder`. */
function userColumnAriaIndex(idx: number): number {
  return idx + 1 + leadingColumnCount.value;
}
/** `aria-colindex` of the trailing row-action column. */
const lastColumnAriaIndex = computed(
  () => userVisibleColumnOrder.value.length + 1 + leadingColumnCount.value
);

// Per-column width floor: the header label must always be fully visible with
// a little air front and back — resizing and stored widths never go below it.
const columnMinWidths = ref<Map<string, number>>(new Map());
/** Reserved for the sort number + caret so an appearing indicator never squeezes. */
const SORT_INDICATOR_ALLOWANCE_PX = 30;
/** Breathing room so the label never looks crammed against the edges. */
const HEADER_BREATHING_PX = 16;

function measureHeaderFloor(columnId: string): void {
  const thEl = thRefs.value.get(columnId);
  const config = columnConfigs.value.get(columnId);
  if (!thEl || !config) return;
  let content = 0;
  for (const child of Array.from(thEl.children) as HTMLElement[]) {
    // The resize handle is an absolute overlay; the sort indicator is covered
    // by its fixed allowance (it may not be rendered while unsorted).
    if (child.classList.contains('resize-handle') || child.classList.contains('sort-indicator')) {
      continue;
    }
    content += child.scrollWidth;
  }
  if (content === 0) return;
  const styles = getComputedStyle(thEl);
  const floor = Math.ceil(
    content +
      (parseFloat(styles.paddingLeft) || 0) +
      (parseFloat(styles.paddingRight) || 0) +
      (config.sortable ? SORT_INDICATOR_ALLOWANCE_PX : 0) +
      HEADER_BREATHING_PX
  );
  if ((columnMinWidths.value.get(columnId) ?? 0) < floor) {
    columnMinWidths.value.set(columnId, floor);
  }
}

function minWidthOf(columnId: string): number {
  const fromHeader = columnMinWidths.value.get(columnId) ?? 0;
  const fromConfig = columnConfigs.value.get(columnId)?.minWidth ?? 0;
  return Math.max(props.minColWidth, fromHeader, fromConfig);
}

/**
 * The user columns' width together, as their cells render it: the committed or
 * live width between the floor and `maxColWidth` (a column without a width is
 * content-sized; its floor stands in). A placeholder row's fill takes it, so
 * its row-action cell stands where the real rows' does, right after the last
 * column.
 */
const userColumnsWidth = computed(() => {
  const parts = userVisibleColumnOrder.value.map((columnId) => {
    const min = minWidthOf(columnId);
    const w = columnWidths.value.get(columnId) ?? min;
    const live = `var(--col-w-${cssSafeId(columnId)}, ${w}px)`;
    return hasColumnGrowth(columnId)
      ? `max(${min}px, ${live})`
      : `clamp(${min}px, ${live}, ${props.maxColWidth}px)`;
  });
  return parts.length ? `calc(${parts.join(' + ')})` : '0px';
});

type CellSizeStyle = {
  width: string;
  minWidth: string;
  maxWidth: string;
  transform: string;
  flexGrow?: number;
  position?: 'sticky';
  left?: string;
  zIndex?: string;
  backgroundColor?: string;
  borderRight?: string;
};

/**
 * Left offsets of the pinned columns (`stickyColumnIds`): the leading run of the
 * visible order, each one stacked behind the indicator column plus the widths
 * of the pinned columns before it. The offset reads the same `--col-w-*` var
 * as the width, so a live resize drag moves the next pinned column along.
 */
/** User pin state from the column dialog; unset falls back to `stickyColumnIds`. */
const columnSticky = ref<Map<string, boolean>>(new Map());
function isColumnSticky(columnId: string): boolean {
  return columnSticky.value.get(columnId) ?? props.stickyColumnIds?.includes(columnId) ?? false;
}

/**
 * How far the pinned cells of `row` reach into the scroller from its left edge:
 * the right edge of the last of indicator, checkbox and pinned data cells. A
 * pinned cell itself needs no horizontal scrolling. Read from the row, so it
 * works without a header row too.
 */
function leadingStickyInset(row: Element | null, columnId: string): number {
  if (stickyColumnLefts.value.has(columnId)) return 0;
  const table = tableRef.value;
  if (!table || !row) return firstColRef.value?.getBoundingClientRect().width ?? 0;
  const left = table.getBoundingClientRect().left;
  let edge = left;
  for (const cell of Array.from(row.children) as HTMLElement[]) {
    const pinned =
      cell.classList.contains('first-col') ||
      cell.classList.contains('select-col') ||
      stickyColumnLefts.value.has(cell.dataset.columnId ?? '');
    if (pinned) edge = Math.max(edge, cell.getBoundingClientRect().right);
  }
  return Math.max(0, edge - left);
}

const stickyColumnLefts = computed(() => {
  const lefts = new Map<string, string>();
  // The indicator column (.first-col) and the checkbox column (.select-col)
  // are `--nxt-table--gutter-w` wide each — the same variable the CSS sizes them with.
  let offset = checkboxSelection.value
    ? 'calc(2 * var(--nxt-table--gutter-w))'
    : 'var(--nxt-table--gutter-w)';
  for (const columnId of userVisibleColumnOrder.value) {
    if (!isColumnSticky(columnId)) break;
    lefts.set(columnId, offset);
    // Exactly the width the cell renders with: its `width` (live var or the
    // committed px) floored by its `min-width` — a stored width below the header
    // floor would otherwise leave the next pin overlapping (I-2172).
    const min = minWidthOf(columnId);
    const w = columnWidths.value.get(columnId) ?? min;
    offset = `calc(${offset} + max(var(--col-w-${cssSafeId(columnId)}, ${w}px), ${min}px))`;
  }
  return lefts;
});

function hasColumnGrowth(columnId: string): boolean {
  const weight = props.columnGrow?.[columnId];
  return props.layout !== 'list' && weight !== undefined && Number.isFinite(weight) && weight > 0;
}

function maxWidthOf(columnId: string): number {
  return hasColumnGrowth(columnId) ? Infinity : props.maxColWidth;
}

/** Grow only automatic, scrollable columns; explicit widths always win. */
const columnGrowth = computed(() => {
  const weights = new Map<string, number>();
  if (props.layout === 'list') return weights;
  for (const columnId of userVisibleColumnOrder.value) {
    if (
      hasColumnGrowth(columnId) &&
      !columnWidths.value.has(columnId) &&
      !stickyColumnLefts.value.has(columnId)
    ) {
      weights.set(columnId, props.columnGrow![columnId]!);
    }
  }
  if (!weights.size) return weights;
  // CSS leaves space unused when all flex-grow values add up to less than 1.
  // Normalise relative weights so even a single fractional share fills the row.
  const largest = Math.max(...weights.values());
  const total = [...weights.values()].reduce((sum, weight) => sum + weight / largest, 0);
  for (const [columnId, weight] of weights) {
    weights.set(columnId, (weight / largest / total) * weights.size);
  }
  return weights;
});

const cellSizeStyles = computed(() => {
  const map = new Map<string, CellSizeStyle>();
  const maxWidthStr = `${props.maxColWidth}px`;
  const lefts = stickyColumnLefts.value;
  const lastPinned = [...lefts.keys()].at(-1);
  for (const columnId of columnConfigs.value.keys()) {
    if (props.layout === 'list') {
      // The column is the card: it takes the whole row, whatever its width state says.
      map.set(columnId, { width: '100%', minWidth: '0', maxWidth: 'none', transform: 'none' });
      continue;
    }
    const w = columnWidths.value.get(columnId);
    const grow = columnGrowth.value.get(columnId) ?? 0;
    const style: CellSizeStyle = {
      // The var carries the LIVE width during a resize drag (set imperatively
      // on the wrapper); at rest it is unset and the committed width applies.
      width:
        w !== undefined || hasColumnGrowth(columnId)
          ? `var(--col-w-${cssSafeId(columnId)}, ${w ?? minWidthOf(columnId)}px)`
          : 'fit-content',
      minWidth: `${minWidthOf(columnId)}px`,
      maxWidth: hasColumnGrowth(columnId) ? 'none' : maxWidthStr,
      ...(grow ? { flexGrow: grow } : {}),
      transform: `translateX(var(--col-offset-${cssSafeId(columnId)}, 0px))`,
    };
    const left = lefts.get(columnId);
    if (left !== undefined) {
      style.position = 'sticky';
      style.left = left;
      style.zIndex = 'var(--nxt-z-above)';
      // The row paints its fill (zebra, hover, selection, header); a pinned
      // cell has to be opaque or the scrolled cells show through it.
      style.backgroundColor = 'inherit';
      if (columnId === lastPinned) {
        style.borderRight = 'var(--nxt-hairline) solid var(--nxt-table--border)';
      }
    }
    map.set(columnId, style);
  }
  return map;
});
const cellLayoutClasses = computed(() => {
  const map = new Map<string, 'layout-row' | 'layout-column'>();
  for (const [columnId, config] of columnConfigs.value.entries()) {
    map.set(columnId, config.layout === 'row' ? 'layout-row' : 'layout-column');
  }
  return map;
});

// Per-column horizontal alignment class. `align: 'end'` right-aligns the header
// label, the display cell and the edit input; everything else stays start-aligned
// (no class), so this is purely additive for existing columns.
const cellAlignClasses = computed(() => {
  const map = new Map<string, 'align-end'>();
  for (const [columnId, config] of columnConfigs.value.entries()) {
    if (config.align === 'end') {
      map.set(columnId, 'align-end');
    }
  }
  return map;
});

// refs to calculate the initial size
const thRefs = ref<Map<string, HTMLElement>>(new Map());
const thFilterRefs = ref<Map<string, HTMLElement>>(new Map());
const tdRefs = ref<Map<string, HTMLElement>>(new Map());
const renderedBodyCells = new Map<string, HTMLElement>();

const renderedCellKey = (rowId: string, columnId: string) => JSON.stringify([rowId, columnId]);

function setRenderedBodyCell(rowId: string, columnId: string, el: Element | null): void {
  const key = renderedCellKey(rowId, columnId);
  if (el instanceof HTMLElement) renderedBodyCells.set(key, el);
  else renderedBodyCells.delete(key);
}

/** Resolve only inside this NxTable instance; row/column ids may repeat in sibling grids. */
function findRenderedCell(rowId: string, columnId: string): HTMLElement | null {
  if (rowId === FILTER_ROW_ID) return thFilterRefs.value.get(columnId) ?? null;
  if (rowId === SETTINGS_ROW_ID && columnId === INDICATOR_COLUMN_ID) {
    return firstColRef.value ?? null;
  }
  return renderedBodyCells.get(renderedCellKey(rowId, columnId)) ?? null;
}

// ---- Template refs ----
const wrapperRef = ref<HTMLElement>();
const tableRef = ref<HTMLElement>();
const detectorRef = ref<HTMLElement>();
const tableHeadRef = ref<HTMLElement>();
const summaryRowRef = ref<HTMLElement>();
const summaryHeight = ref(0);
const summaryBottomInset = computed(() => (props.summary ? summaryHeight.value : 0));
let summaryResizeObserver: ResizeObserver | undefined;
watch(summaryRowRef, (element) => {
  summaryResizeObserver?.disconnect();
  summaryResizeObserver = undefined;
  summaryHeight.value = element?.getBoundingClientRect().height ?? 0;
  if (!element || typeof ResizeObserver === 'undefined') return;
  summaryResizeObserver = new ResizeObserver(() => {
    summaryHeight.value = element.getBoundingClientRect().height;
  });
  summaryResizeObserver.observe(element);
});
onUnmounted(() => summaryResizeObserver?.disconnect());
const firstColRef = ref<HTMLElement>();
const lastColRef = ref<HTMLElement>();
const createButtonRef = ref<HTMLButtonElement>();

const pageTabStopSelector = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'summary',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

function isRenderedPageTabStop(el: HTMLElement): boolean {
  if (el.tabIndex < 0 || el.matches(':disabled') || el.closest('[inert]')) return false;
  for (let current: HTMLElement | null = el; current; current = current.parentElement) {
    if (current.hidden || current.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (current === document.body) break;
  }
  return true;
}

/** Focus the closest native page tab stop before/after this table, never table chrome. */
function focusAdjacentPageTabStop(direction: -1 | 1): boolean {
  const wrap = wrapperRef.value;
  if (!wrap) return false;
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(pageTabStopSelector)).filter(
    (candidate) =>
      candidate !== wrap &&
      !wrap.contains(candidate) &&
      !candidate.contains(wrap) &&
      isRenderedPageTabStop(candidate)
  );
  const directional = candidates.filter((candidate) => {
    const relation = wrap.compareDocumentPosition(candidate);
    return direction === 1
      ? (relation & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
      : (relation & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
  });
  const target = direction === 1 ? directional[0] : directional.at(-1);
  if (!target) return false;
  target.focus();
  const active = document.activeElement;
  return active instanceof Element && active !== document.body && !wrap.contains(active);
}

// ---- Orchestration state ----
const isLoading = ref<boolean>(false);
const isCreationActive = ref<boolean>(false);
const isColumnDialogOpen = ref<boolean>(false);
const isCsvExportDialogOpen = ref<boolean>(false);
const isDeleteDialogOpen = ref<boolean>(false);
/** The gear dropdown menu (unchanged classic entry point). */
const isSettingsMenuOpen = ref<boolean>(false);
/** Filter management modal (saved filters / wizard entry). */
const isFilterManageOpen = ref<boolean>(false);
/** Table preferences modal (search-row / scrollbar toggles). */
const isPreferencesOpen = ref<boolean>(false);
const isFilterWizardOpen = ref<boolean>(false);
const isFilterSaveDialogOpen = ref<boolean>(false);
/** Table pulled into a viewport-filling overlay (gear → Vollbild). */
const isFullscreen = ref(false);
let savedBodyOverflow: string | null = null;
/**
 * The table sits in a dialog (an NxModal). There it offers no Vollbild: the
 * overlay would open BEHIND the modal dialog, and sizing belongs to the dialog
 * (maximize, double click on its head) — BLP/nx#2819. Read when the gear menu
 * opens, the only moment the answer is needed.
 */
const insideDialog = ref(false);
watch(isSettingsMenuOpen, (open) => {
  if (open) insideDialog.value = wrapperRef.value?.closest('dialog') != null;
});

function openFullscreen() {
  isSettingsMenuOpen.value = false;
  isFullscreen.value = true;
}

function closeFullscreen() {
  isFullscreen.value = false;
}

function onFullscreenKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !isFullscreen.value) return;
  if (document.querySelector('dialog[open]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  closeFullscreen();
}

watch(isFullscreen, (on) => {
  if (typeof document === 'undefined') return;
  if (on) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onFullscreenKeydown, true);
  } else {
    document.body.style.overflow = savedBodyOverflow ?? '';
    savedBodyOverflow = null;
    document.removeEventListener('keydown', onFullscreenKeydown, true);
  }
});
// ---- Table settings (I-2510) ----
// Zebra, grid lines, search row, scrollbars and row density are the user's
// table defaults (settings key `table`, one set for every table) — read and
// written through the kit's settings contract; the gear's „Einstellungen"
// dialog edits them. Without a provided source they live as long as the table.
const settings = useNxSettings();
const tableSettings = settings.get<NxTableSettings>(NX_TABLE_SETTINGS_KEY);

// ---- Opening a record (`recordList`, `table.row_open`) ----
const rowOpener = inject(NX_ROW_OPENER_KEY, null);
const rowOpenTiles = inject(NX_ROW_OPEN_TILES_KEY, null);
const rowOpenBeside = inject(NX_ROW_OPEN_BESIDE_KEY, null);
const rowOpen = createRowOpen<T>({
  enabled: () => !!props.recordList && !props.editMode,
  actions: () => rowOpenOf(tableSettingsFor(tableSettings.value, props.settingsKey).row_open),
  opener: rowOpener,
  open: (rowId, columnId, row) => emit('cell-double-click', rowId, columnId, row),
});
/** The table settings a tenant or company locked: the gear shows them, greyed out. */
const lockedTableFields =
  settings.locked?.(NX_TABLE_SETTINGS_KEY) ?? shallowRef<readonly string[]>([]);
// A table with a settings key may set its own over everybody's (`tables.<key>`).
const chrome = computed(() =>
  tableChromeOf(tableSettingsFor(tableSettings.value, props.settingsKey))
);

function patchTableSettings(patch: { [K in keyof NxTableSettings]?: NxTableSettings[K] | null }) {
  settings.patch(NX_TABLE_SETTINGS_KEY, patch);
}

/**
 * What the gear's dialog edits (I-2510): this table's own settings
 * (`tables.<settingsKey>`) — the default where the table has a key, so a
 * change in one table stays there — or every table's (`table`).
 */
const preferencesScope = ref<'table' | 'all'>(props.settingsKey ? 'table' : 'all');
/** The fields this table sets over everybody's. */
const ownTableSettings = computed(() => tableOwnSettingsOf(tableSettings.value, props.settingsKey));
const ownTableSettingsCount = computed(() => Object.keys(ownTableSettings.value).length);
const tablesWithOwn = computed(() => tablesWithOwnSettings(tableSettings.value));

/**
 * Writes one choice of the dialog where its scope says: into this table's own
 * settings, or into every table's — and then drops this table's own value of
 * the field, so the change shows here too.
 */
function patchTablePreference(patch: {
  [K in keyof NxTableOwnSettings]?: NxTableOwnSettings[K] | null;
}) {
  const key = props.settingsKey;
  if (preferencesScope.value === 'table' && key) {
    patchTableSettings({ tables: { [key]: patch } as NxTableSettings['tables'] });
    return;
  }
  const own = ownTableSettings.value;
  const clear = Object.keys(patch).filter((field) => field in own);
  patchTableSettings({
    ...patch,
    ...(key && clear.length
      ? {
          tables: {
            [key]: Object.fromEntries(clear.map((field) => [field, null])),
          } as NxTableSettings['tables'],
        }
      : {}),
  });
}

/** `setTableState({ searchRowVisible })` of this instance; the user's own choice clears it. */
const searchRowOverride = ref<boolean | null>(null);
/**
 * Whether the quick filter row ("Suchleiste") is shown — visible by default,
 * toggled in Einstellungen; the header funnels are the primary filter UI.
 */
const showQuickFilterRow = computed(() => searchRowOverride.value ?? chrome.value.searchRowVisible);
/** Classic always-visible scrollbars on `.table` (on by default). */
const alwaysShowScrollbars = computed(() => chrome.value.alwaysShowScrollbars);
/** Alternating body-row fill: the table's own fixed value, else the user's setting (on by default). */
const zebraStripes = computed(() => props.zebra ?? chrome.value.zebraStripes);
/** Horizontal separators between body rows (on by default). */
const horizontalLines = computed(() => chrome.value.horizontalLines);
/** Vertical rules between data columns (off by default). */
const verticalLines = computed(() => chrome.value.verticalLines);
/** The header row filled with the brand colour (`table.header`, off by default). */
const headerBrand = computed(() => chrome.value.headerBrand);
/** Cells at the top of a taller row instead of its middle (`table.cell_align`). */
const cellsTop = computed(() => chrome.value.cellsTop);
/** The table's own row height step: the prop, or what `setTableState` put in. */
const ownRowDensity = ref<NxTableRowDensity>(props.rowDensity);
watch(
  () => props.rowDensity,
  (v) => {
    ownRowDensity.value = v;
  }
);
/** The row height step: the user's choice for every table, else the table's own. */
const rowDensity = computed<NxTableRowDensity>(
  () => chrome.value.rowDensity ?? ownRowDensity.value
);
/** The body row height every consumer reads: an explicit prop wins over the density. */
const effectiveBodyRowHeight = computed(
  () => props.bodyRowHeight ?? ROW_DENSITY_HEIGHTS[rowDensity.value]
);

const pendingDelete = ref<{
  id: string;
  index: number;
  inSelection: boolean;
} | null>(null);
const creationData = ref({}) as Ref<PartialOrNull<T>>;
const uniqueId = useId();

// ---- Row grouping ----
// Everything row-index based below (navigation, selection, the virtual list)
// runs on the rows in DISPLAY order. Ungrouped, that is `props.data` itself and
// every index is an index in `data`; grouped, indices handed to the consumer go
// through `dataIndexOf` (and `viewIndexOf` on the way in).
const grouping = useRowGroups<T>({
  data: computed(() => props.data),
  rowIdKey: props.rowIdKey,
  groupBy: computed(() => props.groupBy),
  groups: computed(() => props.groups),
  ungroupedLabel: computed(() => props.ungroupedLabel),
  showEmptyGroups: computed(() => props.showEmptyGroups),
  rowParentId: computed(() => props.rowParentId),
  isPlaceholderRow: (row) => props.isPlaceholderRow?.(row) === true,
  suspended: computed(() => !!props.onRowMoved),
});
const {
  toDataIndex: dataIndexOf,
  toViewIndex: viewIndexOf,
  headersAt: groupHeadersAt,
  trailingHeaders: groupTrailingHeaders,
} = grouping;
const dataRef = grouping.viewRows;

/** Display position of the row with `rowId`, -1 when it is not loaded. */
function viewIndexOfId(rowId: string): number {
  return dataRef.value.findIndex((row) => props.rowIdKey(row) === rowId);
}

/**
 * The box of a body, create or empty row — the one place that turns
 * `bodyRowHeight` and `rowSizing` into CSS. `uniform` rows, placeholders
 * included, are exactly `bodyRowHeight` tall: a placeholder follows the height
 * contract instead of the virtualizer's estimate, so it can never feed a wrong
 * height back into it (I-2172). An `auto` placeholder keeps the estimate as its
 * minimum, standing in for a row of average height.
 */
function rowBoxStyle(placeholder = false): Record<string, string | undefined> {
  if (props.rowSizing === 'uniform') {
    return {
      minHeight: effectiveBodyRowHeight.value,
      height: effectiveBodyRowHeight.value,
      maxHeight: effectiveBodyRowHeight.value,
    };
  }
  return {
    minHeight:
      placeholder && estimate.value > 0 ? `${estimate.value}px` : effectiveBodyRowHeight.value,
  };
}

/**
 * Maps the consumer's `rowStyle(row)` return value onto the CSS custom
 * properties the row template reads. Only sets a var when the corresponding
 * field is provided, so omitted fields fall back to the `.tr` defaults
 * (base/zebra background, no top border, 1px bottom separator).
 *
 * `bgColor` is written to `--row-bg-override` (not `--row-bg` directly) so that
 * hover — which sets `--row-bg` in the stylesheet — still wins over the custom
 * color; selection/cursor/active win via a direct `background-color`.
 * `fontWeight` is written to `--row-font-weight`, which the `.tr` rule reads.
 */
function rowStyleVars(row: T): Record<string, string> {
  const style = props.rowStyle?.(row);
  if (!style) return {};
  const vars: Record<string, string> = {};
  if (style.bgColor !== undefined) vars['--row-bg-override'] = style.bgColor;
  if (style.fontWeight !== undefined) vars['--row-font-weight'] = style.fontWeight;
  if (style.border?.top !== undefined) vars['--row-border-top'] = `${style.border.top}px`;
  if (style.border?.bottom !== undefined) vars['--row-border-bottom'] = `${style.border.bottom}px`;
  return vars;
}

// ---- Composables ----

// Sort
const { sortPrio, sortInfo, onSort } = useTableSort({
  firstSortOrder: computed(() => props.firstSortOrder),
  onSortChanged: (newSorts) => {
    emit('table-sort-changed', newSorts);
    debouncedOnChange({ limit: paginationLimitInternal.value, offset: 0 });
  },
});
// numbers to display / show the sortPrio with
const sortOrderIndices = computed(() => new Map(sortPrio.value.map((v, idx) => [v, idx + 1])));

function headerAriaSort(columnId: string): 'ascending' | 'descending' | 'none' | undefined {
  if (!columnConfigs.value.get(columnId)?.sortable) return undefined;
  const order = sortInfo.value.get(columnId);
  if (order === 'asc') return 'ascending';
  if (order === 'desc') return 'descending';
  return 'none';
}

function onHeaderSortKeydown(event: KeyboardEvent, columnId: string): void {
  // Nested filter/action controls own their keys; only the columnheader itself
  // activates sorting.
  if (event.target !== event.currentTarget) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (!columnConfigs.value.get(columnId)?.sortable) return;
  event.preventDefault();
  event.stopPropagation();
  onSort(columnId, event.ctrlKey || event.metaKey);
}

// Filters — quick, structured, advanced, base and named sets
// (see `composables/useTableFilterState.ts`).
const {
  filterModifier,
  filterInfo,
  onFilter,
  hasFilters,
  columnFilters,
  advancedFilter,
  pendingWizardTree,
  filterColumnMeta,
  filterableColumnMeta,
  filterValueSourceFor,
  hasActiveFilters,
  applyHeaderFilter,
  removeQuickFilter,
  removeColumnCondition,
  applyAdvancedFilter,
  clearFilterState,
  resetAllFilters,
  restoreQuickFilters,
  savedFilters,
  deleteFilter,
  saveNamedFilter,
  applySavedFilter,
  baseFilterValues,
  lockedColumns,
} = useTableFilterState<T>({
  // `ref()` unwraps the generic config type; the map holds exactly these configs.
  columnConfigs: columnConfigs as unknown as {
    readonly value: ReadonlyMap<string, Omit<TableColumnConfig<T>, 'id'>>;
  },
  columnOrder,
  data: () => props.data,
  baseFilter: () => props.baseFilter,
  baseFilterMode: () => props.baseFilterMode,
  settingsKey: () => props.settingsKey,
  onFilterChange: () => triggerFilterChange(),
});
/** The AND/OR toggle of the quick filter row names what it does and its state. */
const filterModifierLabel = computed(() =>
  i18nInjection.t('common.table.filter.combine', {
    mode: i18nInjection.t(
      filterModifier.value === 'and' ? 'common.table.filter.and' : 'common.table.filter.or'
    ),
  })
);

const headerGridRows = computed(() => (props.hideHeader ? 0 : 1));
const visibleFilterGridRows = computed(() =>
  !props.hideHeader && hasFilters.value && showQuickFilterRow.value ? 1 : 0
);
const gridAriaRowCount = computed(
  () =>
    (props.totalCount ?? props.data.length) +
    headerGridRows.value +
    visibleFilterGridRows.value +
    (isCreationActive.value ? 1 : 0) +
    grouping.headerCount.value +
    expansion.expandedCount.value +
    (props.summary ? 1 : 0)
);
// One summary-only configuration for both label discovery and aggregation.
// Table-owned metadata reaches even adapters that cannot forward column attrs.
const summaryColumnConfigs = computed(
  () =>
    new Map(
      [...columnConfigs.value].map(([key, column]) => [
        key,
        {
          ...column,
          ...Object.fromEntries(
            Object.entries(props.columnSummaries?.[key] ?? {}).filter(
              ([, value]) => value !== undefined
            )
          ),
        },
      ])
    )
);
const summaryLabelText = computed(
  () => props.summaryLabel ?? i18nInjection.t('common.table.summary.label')
);
const summaryCells = computed<Map<string, NxTableSummaryResult>>((previous) => {
  const cells = new Map<string, NxTableSummaryResult>();
  if (!props.summary) return cells;
  const complete = summaryDataIsComplete({
    rows: props.data,
    server: !!props.onTableStateChange,
    total: props.totalCount,
    complete: props.summaryDataComplete,
    placeholder: props.isPlaceholderRow,
  });
  for (const key of userVisibleColumnOrder.value) {
    const column = summaryColumnConfigs.value.get(key)!;
    cells.set(
      key,
      resolveTableSummary({
        rows: props.data,
        column: { ...column, columnId: key },
        values: props.summaryValues,
        complete,
        loading: props.summaryLoading || isLoading.value,
        error: props.summaryError,
        locale: i18nInjection.locale?.value ?? 'en',
      })
    );
  }
  // Inline column callbacks can be recreated while this table renders its
  // default slot. Keep the computed identity when their resolved behavior is
  // unchanged, so that recreation cannot schedule a recursive render.
  if (previous && JSON.stringify([...previous]) === JSON.stringify([...cells])) return previous;
  return cells;
});
function summaryExplanation(columnId: string): string | undefined {
  const state = summaryCells.value.get(columnId)?.state;
  return state && !['none', 'value', 'unavailable'].includes(state)
    ? i18nInjection.t(`common.table.summary.${state}`)
    : undefined;
}

/**
 * The caption belongs to the summary ROW, not to a column (André, 19.10.2026):
 * it starts where the data columns start and may run over the leading summary
 * cells that stay empty, so a narrow first column does not cut it. The totals
 * stay in their columns. Its room is measured on screen — up to the right edge
 * of the last leading empty cell, which a horizontal scroll moves under the
 * pinned gutter. Without room for the whole caption (a total in the first
 * column, a narrow empty run) it collapses to „Σ“ in the indicator gutter with
 * the caption as tooltip. Unmeasured (no layout) it stays shown.
 */
const summaryLabelRef = ref<HTMLElement>();
// A stable function ref: an inline one is re-run on every render.
function setSummaryLabelRef(element: unknown): void {
  const label = element instanceof HTMLElement ? element : undefined;
  if (summaryLabelRef.value !== label) summaryLabelRef.value = label;
}
const summaryLabelCollapsed = ref(false);
let summaryLabelFrame = 0;
let summaryLabelObserver: ResizeObserver | undefined;
let summaryRowMutations: MutationObserver | undefined;
let summaryLabelObserved = new WeakSet<Element>();
/**
 * Watch the size of every cell the row has NOW. The cells arrive after the row
 * (the columns register once mounted): cells observed only when the row came
 * left them out, and a column resize went unnoticed (the showcase, 19.10.2026).
 */
function observeSummaryRow(row: HTMLElement): void {
  if (!summaryLabelObserver) return;
  for (const element of [row, ...Array.from(row.children), summaryLabelRef.value]) {
    if (!element || summaryLabelObserved.has(element)) continue;
    summaryLabelObserved.add(element);
    summaryLabelObserver.observe(element);
  }
}
function measureSummaryLabel(): void {
  summaryLabelFrame = 0;
  const row = summaryRowRef.value;
  const label = summaryLabelRef.value;
  if (!row || !label) return;
  observeSummaryRow(row);
  const gutter = label.parentElement;
  const cells = new Map<string, HTMLElement>();
  let start: number | undefined;
  for (const cell of Array.from(row.children) as HTMLElement[]) {
    if (cell.classList.contains('first-col') || cell.classList.contains('select-col')) {
      start = cell.getBoundingClientRect().right;
    } else if (cell.dataset.columnId !== undefined) {
      cells.set(cell.dataset.columnId, cell);
    }
  }
  if (start === undefined || !gutter) return;
  // Flush with the first data column, past the gutter's border and the checkbox column.
  label.style.left = `${start - gutter.getBoundingClientRect().left - gutter.clientLeft}px`;
  let end = start;
  for (const columnId of userVisibleColumnOrder.value) {
    if (summaryCells.value.get(columnId)?.state !== 'none') break;
    const cell = cells.get(columnId);
    if (cell) end = Math.max(end, cell.getBoundingClientRect().right);
  }
  // Natural width against the room: nowrap content overflows the sized box.
  label.style.width = `${Math.max(0, end - start)}px`;
  summaryLabelCollapsed.value = label.scrollWidth > label.clientWidth;
}
function scheduleSummaryLabel(): void {
  if (summaryLabelFrame || typeof requestAnimationFrame === 'undefined') return;
  summaryLabelFrame = requestAnimationFrame(measureSummaryLabel);
}
watch(
  [summaryRowRef, summaryLabelRef],
  ([row]) => {
    summaryLabelObserver?.disconnect();
    summaryRowMutations?.disconnect();
    summaryLabelObserver = undefined;
    summaryRowMutations = undefined;
    summaryLabelObserved = new WeakSet();
    if (!row) return;
    if (typeof ResizeObserver !== 'undefined') {
      summaryLabelObserver = new ResizeObserver(scheduleSummaryLabel);
    }
    // A cell that comes or goes (register, reorder, hide) is measured and observed.
    if (typeof MutationObserver !== 'undefined') {
      summaryRowMutations = new MutationObserver(scheduleSummaryLabel);
      summaryRowMutations.observe(row, { childList: true });
    }
    observeSummaryRow(row);
    scheduleSummaryLabel();
  },
  { flush: 'post' }
);
watch(
  () =>
    [
      summaryLabelText.value,
      checkboxSelection.value,
      ...userVisibleColumnOrder.value.map((key) => `${key}:${summaryCells.value.get(key)?.state}`),
    ].join('|'),
  scheduleSummaryLabel,
  { flush: 'post' }
);
watch(
  tableRef,
  (scroller, previous) => {
    previous?.removeEventListener('scroll', scheduleSummaryLabel);
    scroller?.addEventListener('scroll', scheduleSummaryLabel, { passive: true });
  },
  { immediate: true }
);
onUnmounted(() => {
  summaryLabelObserver?.disconnect();
  summaryRowMutations?.disconnect();
  tableRef.value?.removeEventListener('scroll', scheduleSummaryLabel);
  if (summaryLabelFrame) cancelAnimationFrame(summaryLabelFrame);
});

const createRowAriaIndex = computed(() => 1 + headerGridRows.value + visibleFilterGridRows.value);
/** Rows of the grid above the first body row (header, filter and create rows). */
const bodyAriaOffset = computed(
  () => 1 + headerGridRows.value + visibleFilterGridRows.value + (isCreationActive.value ? 1 : 0)
);
function bodyRowAriaIndex(rowIdx: number): number {
  // Group header rows and open details above this one count as grid rows too.
  return (
    rowIdx +
    bodyAriaOffset.value +
    grouping.headersThrough(rowIdx) +
    expansion.expandedBefore(rowIdx)
  );
}
function groupHeaderAriaIndex(header: RowGroupHeader<T>): number {
  return header.position + bodyAriaOffset.value + expansion.expandedBefore(header.anchor);
}
const gridAriaColCount = computed(
  () => userVisibleColumnOrder.value.length + (hasLastCol.value ? 1 : 0) + leadingColumnCount.value
);

function rowSelectorAriaLabel(rowIdx: number, selected: boolean): string {
  return i18nInjection.t(
    selected ? 'common.table.row_selector.deselect' : 'common.table.row_selector.select',
    { row: rowIdx + 1 }
  );
}

/**
 * No rows and nothing in flight → show the shared empty state under the
 * header instead of a bare table body (same look on every table). With
 * active filters it becomes "no matches" + a reset action.
 */
const showEmptyState = computed(
  () => props.data.length === 0 && !isLoading.value && !isCreationActive.value
);

const tableSlots = useSlots();
/** Shared empty body at the established location, or inside a summary table's scroller. */
const renderEmptyBody = () => {
  if (tableSlots.empty)
    return tableSlots.empty({
      hasActiveFilters: hasActiveFilters.value,
      resetFilters: resetAllFilters,
    });
  if (hasActiveFilters.value)
    return h(
      NxEmptyState,
      {
        icon: FilterDismissIcon,
        title: i18nInjection.t('common.table.empty.filtered_title'),
        description: i18nInjection.t('common.table.empty.filtered_description'),
      },
      {
        default: () =>
          h(NxButton, { variant: 'outline', height: 's', onClick: resetAllFilters }, () =>
            i18nInjection.t('common.table.filter.reset_all')
          ),
      }
    );
  return h(NxEmptyState, {
    title: props.emptyTitle ?? i18nInjection.t('common.table.empty.title'),
    description: props.emptyDescription ?? i18nInjection.t('common.table.empty.description'),
  });
};

function triggerFilterChange() {
  debouncedOnChange({ limit: paginationLimitInternal.value, offset: 0 });
}

/**
 * Restore factory column layout (order, visibility, auto widths, no sort)
 * and clear filters. DataTable listens to `reset-layout` to wipe persisted
 * prefs and re-seed from the catalog descriptor. The user's table defaults
 * (`table`) belong to every table and stay; the dialog resets those.
 */
function resetLayout(): void {
  searchRowOverride.value = null;
  ownRowDensity.value = props.rowDensity;
  if (defaultColumnOrder.value.length > 0) {
    columnOrder.value = [...defaultColumnOrder.value];
  }
  for (const [columnId, cfg] of columnConfigs.value) {
    columnVisible.value.set(columnId, cfg.defaultVisible);
  }
  columnWidths.value = new Map();
  frozenAutoColumnWidths.clear();
  sortPrio.value = [];
  sortInfo.value = new Map();
  clearFilterState();
  nextTick(() => {
    for (const columnId of columnVisible.value.keys()) {
      measureColumnWidth(columnId);
    }
  });
  emit('reset-layout');
  triggerFilterChange();
}

// The preferences dialog writes the user's table defaults — for every table.
function onSearchRowVisibleUpdate(visible: boolean): void {
  searchRowOverride.value = null;
  patchTablePreference({ search_row: visible });
}

function onAlwaysShowScrollbarsUpdate(visible: boolean): void {
  patchTablePreference({ always_show_scrollbars: visible });
}

function onZebraStripesUpdate(on: boolean): void {
  patchTablePreference({ zebra: on ? 'on' : 'off' });
}

function onHorizontalLinesUpdate(on: boolean): void {
  patchTablePreference({ horizontal_lines: on });
}

function onVerticalLinesUpdate(on: boolean): void {
  patchTablePreference({ vertical_lines: on });
}

function onHeaderBrandUpdate(on: boolean) {
  patchTablePreference({ header: on ? 'brand' : 'subtle' });
}

function onCellsTopUpdate(on: boolean) {
  patchTablePreference({ cell_align: on ? 'top' : 'middle' });
}

function onRowDensityUpdate(density: NxTableRowDensity): void {
  patchTablePreference({ row_density: density });
}

/**
 * „Standard wiederherstellen": in this table's scope it drops the table's own
 * settings (everybody's apply again), for every table the user's own table
 * defaults (the organisation's apply again).
 */
function onTablePreferencesReset(): void {
  searchRowOverride.value = null;
  const key = props.settingsKey;
  if (preferencesScope.value === 'table' && key) {
    patchTableSettings({ tables: { [key]: null } as unknown as NxTableSettings['tables'] });
    return;
  }
  // A locked field stays: master refuses its removal (and the whole patch with it).
  const free = NX_TABLE_SETTINGS_FIELDS.filter((field) => !lockedTableFields.value.includes(field));
  patchTableSettings(Object.fromEntries(free.map((field) => [field, null])));
}

/** „Überall übernehmen": every table's own settings go, everybody's apply in every table. */
function onTablePreferencesApplyEverywhere(): void {
  patchTableSettings({ tables: null });
}

/**
 * Gear → „Breiten zurücksetzen": every column back to its measured auto
 * width, nothing else of the layout changes. `column-widths-reset` lets a
 * consumer that persists widths drop them.
 */
function resetColumnWidths(): void {
  columnWidths.value = new Map();
  frozenAutoColumnWidths.clear();
  isSettingsMenuOpen.value = false;
  nextTick(() => {
    for (const columnId of columnVisible.value.keys()) {
      measureColumnWidth(columnId);
    }
  });
  emit('column-widths-reset');
}

// ---- Named filter sets: the wizard parks its tree until the save dialog names it ----
function onWizardSaveRequest(tree: NxTableFilterGroup | null) {
  if (!tree) return;
  pendingWizardTree.value = tree;
  isFilterSaveDialogOpen.value = true;
}

// Save dialog dismissed without saving → drop the parked wizard tree.
watch(isFilterSaveDialogOpen, (open) => {
  if (!open) pendingWizardTree.value = null;
});

// Resize
const { isResizeActive, onResizeStart } = useColumnResize({
  columnConfigs: columnConfigs as Ref<Map<string, { resizeable: boolean; [k: string]: unknown }>>,
  columnWidths,
  minColWidth: computed(() => props.minColWidth),
  // Never below the full header label (+ air) — see measureHeaderFloor.
  minColWidthFor: minWidthOf,
  maxColWidth: computed(() => props.maxColWidth),
  maxColWidthFor: maxWidthOf,
  onBeforeResize: (resizedColumnId) => {
    // Read every box before writing: changing a flex basis redistributes all
    // remaining space. Freeze the current view so only the dragged edge moves.
    const widths = [...columnGrowth.value.keys()].map(
      (columnId) =>
        [
          columnId,
          thRefs.value.get(columnId)?.getBoundingClientRect().width ?? minWidthOf(columnId),
        ] as const
    );
    for (const [columnId, width] of widths) {
      columnWidths.value.set(columnId, width);
      frozenAutoColumnWidths.add(columnId);
    }
    // The dragged column becomes explicit, even if an earlier drag froze it.
    frozenAutoColumnWidths.delete(resizedColumnId);
  },
  // Drag frames bypass Vue: the width rides a CSS variable on the wrapper
  // (cellSizeStyles reads `var(--col-w-*, <committed>px)`), so a mousemove is
  // a style recalc instead of a full table re-render.
  liveResize: (columnId, px) => {
    const key = `--col-w-${cssSafeId(columnId)}`;
    if (px === null) wrapperRef.value?.style.removeProperty(key);
    else wrapperRef.value?.style.setProperty(key, `${px}px`);
  },
  onResized: (columnId, newWidth) => emit('column-resize', columnId, newWidth),
});

// Keep neighbours still on mouseup. Only a real change of available width
// releases their temporary pixels; row-height/scrollbar notifications do not.
let autoWidthsObserver: ResizeObserver | undefined;
let autoWidthsContainerWidth: number | undefined;
let releaseAutoWidthsAfterDrag = false;
watch(
  wrapperRef,
  (element) => {
    autoWidthsObserver?.disconnect();
    autoWidthsObserver = undefined;
    if (!element) return;
    const onContainerResize = () => {
      const width = element.getBoundingClientRect().width;
      if (width <= 0) return;
      if (autoWidthsContainerWidth !== undefined && width !== autoWidthsContainerWidth) {
        if (isResizeActive.value) releaseAutoWidthsAfterDrag = true;
        else releaseFrozenAutoColumnWidths();
      }
      autoWidthsContainerWidth = width;
    };
    onContainerResize();
    if (typeof ResizeObserver === 'undefined') return;
    autoWidthsObserver = new ResizeObserver(onContainerResize);
    autoWidthsObserver.observe(element, { box: 'border-box' });
  },
  { flush: 'post' }
);
watch(isResizeActive, (active) => {
  if (active || !releaseAutoWidthsAfterDrag) return;
  releaseAutoWidthsAfterDrag = false;
  releaseFrozenAutoColumnWidths();
});
onUnmounted(() => autoWidthsObserver?.disconnect());

const draggedRow = ref<number | null>(null);

// ---- Row details (`expandableRows`) ----
function isRowExpandable(row: T): boolean {
  if (!row || props.isPlaceholderRow?.(row)) return false;
  return typeof props.expandableRows === 'function' ? props.expandableRows(row) : true;
}
const expansion = useRowExpansion<T>({
  data: dataRef,
  rowIdKey: props.rowIdKey,
  enabled: rowExpansionEnabled,
  isExpandable: isRowExpandable,
  onChange: (ids) => emit('row-expanded-change', ids),
});
const { isExpanded: isRowExpanded, setRowExpanded, toggleRowExpanded, collapseAllRows } = expansion;

function expandToggleAriaLabel(rowIdx: number, open: boolean): string {
  return i18nInjection.t(open ? 'common.table.row_detail.close' : 'common.table.row_detail.open', {
    row: rowIdx + 1,
  });
}

/** The detail cell spans the visible table, not the whole scroll width. */
const tableViewWidth = ref<number | null>(null);
let tableViewRo: ResizeObserver | undefined;
watch(
  [tableRef, rowExpansionEnabled],
  ([el, on]) => {
    tableViewRo?.disconnect();
    tableViewRo = undefined;
    if (!on || !el || typeof ResizeObserver === 'undefined') return;
    tableViewRo = new ResizeObserver(() => {
      tableViewWidth.value = el.clientWidth;
    });
    tableViewRo.observe(el);
    tableViewWidth.value = el.clientWidth;
  },
  { immediate: true }
);
onUnmounted(() => tableViewRo?.disconnect());

// Virtual Scroll
const { estimate, scrollTop, virtualWindow, visibleRows, offsetOf, measureRow, measureLeading } =
  useVirtualScroll<T>({
    tableRef,
    bottomInset: summaryBottomInset,
    data: dataRef,
    rowIdKey: props.rowIdKey,
    initialRenderLimit: computed(() => props.initialRenderLimit),
    bodyRowHeight: effectiveBodyRowHeight,
    rowSizing: computed(() => props.rowSizing),
    isPlaceholderRow: (row) => props.isPlaceholderRow?.(row) === true,
    draggedRow,
    // Column-resize drags churn every row's height each frame; buffering the
    // re-measures until mouseup keeps the drag at native speed.
    suspendMeasure: computed(() => isResizeActive.value),
    onVisibleRangeChange: (range) => emit('visible-range-change', range),
    // Group header rows ride in the slot of the row below them.
    leadingKeysOf: grouping.leadingKeysOf,
    hasLeadingRows: grouping.hasHeaders,
    // An open row's detail rides in the row's own slot, below it.
    trailingKeysOf: expansion.trailingKeysOf,
    hasTrailingRows: expansion.hasExpanded,
  });

/**
 * The rendered body rows with what their template needs of each — the row id
 * and the placeholder flag — computed once per row instead of per binding (the
 * body template asked `rowIdKey` 40+ times per row and render).
 */
const bodyRows = computed(() =>
  visibleRows.value.map(({ row, rowIdx }) => ({
    row,
    rowIdx,
    rowId: props.rowIdKey(row),
    isPlaceholder: props.isPlaceholderRow?.(row) === true,
  }))
);

/** Measure an open row's detail for the virtual list (it sits in its row's slot). */
function measureDetailRow(rowId: string, el: HTMLElement | null): void {
  measureLeading(rowDetailKey(rowId), el);
}

/** Measure a group header row for the virtual list (it sits in its row's slot). */
function measureGroupHeader(key: string, inst: unknown): void {
  const el = (inst as ComponentPublicInstance | null)?.$el;
  measureLeading(key, el instanceof HTMLElement ? el : null);
}

/** Zebra parity: counted inside the row's group, so every group starts alike. */
function rowStripes(rowIdx: number): boolean {
  return grouping.stripeIndex(rowIdx) % 2 === 1;
}

// Pagination
const {
  paginationInfo,
  paginationEndReached,
  paginationHandleRef,
  setPaginationHandleRef,
  paginationLimitInternal,
  paginate,
  ensureFullyLoaded,
} = useTablePagination<T>({
  data: dataRef,
  totalCount: computed(() => props.totalCount),
  paginationLimit: computed(() => props.paginationLimit),
  paginationMode: computed(() => props.paginationMode),
  isLoading,
  wrapperRef,
  getTableMetadata: () => getTableMetadata(),
  onTableStateChange: props.onTableStateChange,
});

function onRowClick(rowIdx: number, event: MouseEvent) {
  navigationOnRowClick(rowIdx, event);
}

function seedCreateCursor(rowId: string, preferredColumnId?: string): void {
  const seedCol =
    preferredColumnId && visibleColumnOrder.value.includes(preferredColumnId)
      ? preferredColumnId
      : (userVisibleColumnOrder.value[0] ?? INDICATOR_COLUMN_ID);
  setCursor(rowId, seedCol);
}

/**
 * After create, land the cursor first so `lazyEditCells` mounts this row's
 * edit widgets, then wait until the target input has registered a focus
 * handler (virtual list + Vue mount). Prefer `focusColumnId` when ready —
 * first-focusable would steal the Typ chip instead of the article/text input.
 */
async function focusNewRowCell(rowId: string, preferredColumnId?: string): Promise<void> {
  const id = String(rowId);
  if (preferredColumnId && isBodyCellNavigable(id, preferredColumnId)) {
    await focusCellIntern(id, preferredColumnId, 'click');
    return;
  }
  // The controller knows which metadata-defined cells can mount an editor;
  // focusCellIntern performs the one render-registration wait. No rAF polling.
  const colIdx = firstFocusableColIdx(id, true);
  if (colIdx !== -1) {
    await focusCellIntern(id, visibleColumnOrder.value[colIdx], 'click');
  }
}

/**
 * Creates a new row and handles the focus afterwards. `false` means the
 * consumer refused the write (see `onCreate`) — the caller keeps the create row
 * as it is instead of clearing it.
 */
async function createNewRow(
  rowToCreate: Partial<OrNull<T>>,
  options?: { focusColumnId?: string }
): Promise<boolean> {
  const newRowIdx = await props.onCreate?.(rowToCreate);
  if (newRowIdx === undefined) return false;
  const newRowId = String(props.rowIdKey(props.data[newRowIdx]));
  // Cursor first — lazy-edit rows only mount widgets on the cursor row, so
  // the virtual list can paint the new line already in edit after scroll.
  seedCreateCursor(newRowId, options?.focusColumnId);
  await nextTick();
  // Creates land at the document end on position grids. Prefer the hard
  // bottom snap — pure index offsets can undershoot while virtual heights
  // settle after a sparse-array grow (felt like scrolling upward forever).
  // A grouped table shows the new row inside its group, not at the end.
  const newViewIdx = viewIndexOf(newRowIdx);
  if (newViewIdx >= dataRef.value.length - 1) {
    await scrollToViewIndex('bottom');
  } else {
    await scrollToViewIndex(newViewIdx);
  }
  await nextTick();
  await focusNewRowCell(newRowId, options?.focusColumnId);
  // A checkbox list marks rows only on request; the new row just becomes active.
  if (!checkboxSelection.value) selectRow(newRowId);
  return true;
}

// Guards against duplicate `tryCreateNewRow` calls when the user hits
// ArrowDown past the last row faster than the consumer can append a new row.
let createRowEmitPending = false;
async function tryCreateNewRowFromNavigation(
  factory?: () => { row?: Partial<OrNull<T>>; focusColumnId?: string }
) {
  if (createRowEmitPending) return;
  createRowEmitPending = true;
  try {
    const spec = factory?.() ?? {};
    await createNewRow((spec.row ?? {}) as Partial<OrNull<T>>, {
      focusColumnId: spec.focusColumnId,
    });
  } catch (error) {
    // A REFUSED create no longer rejects (see `onCreate`) — this catches what is
    // left: a genuine fault inside the consumer's create. Nothing awaits a
    // keyboard-driven create, so rethrowing would only add an unhandled
    // rejection. The row stays where it was.
    console.error('[NxTable] row creation failed', error);
  } finally {
    createRowEmitPending = false;
  }
}

async function tryCreateNewRowArrowNav() {
  if (!props.allowNavigationCreate) return;
  await tryCreateNewRowFromNavigation(props.createFromArrowNav);
}

function tryCreateNewRowTabNav(): boolean {
  if (!props.allowNavigationCreate) return false;
  if (!props.createFromTabNav || !props.onCreate) return false;
  void tryCreateNewRowFromNavigation(props.createFromTabNav);
  return true;
}

/**
 * Escape left a cell's editor for the table itself (`leaveCellByEscape`): the
 * next Tab or Shift+Tab leaves the table for the page. Cleared when the
 * table's own focus moves on, so a wrapper focused any other way (a click on
 * the scrollbar) keeps entering the grid on Tab.
 */
let parkedByEscape = false;

/**
 * The text a body cell's editor showed before the typing in it began — what
 * Escape puts back (`leaveCellByEscape`). Noted on the first `beforeinput`
 * since the editor got the focus, after any focus-time formatting, and dropped
 * when the editor commits (`change`, Enter) or loses the focus.
 */
const editStartText = new WeakMap<HTMLElement, string>();

/** The editor Escape is discarding: its `change` during the leave is swallowed. */
let discardingEditor: HTMLElement | null = null;

function isCellTextEditor(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  return (
    (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
    !!el.closest('.tbody .cell-edit')
  );
}

function noteEditStart(event: Event) {
  const el = event.target;
  if (isCellTextEditor(el) && !editStartText.has(el)) editStartText.set(el, el.value);
}

function onCellChangeCapture(event: Event) {
  const el = event.target as HTMLElement | null;
  if (!el) return;
  if (el === discardingEditor) {
    // Capture phase on an ancestor: the editor's own `change` listener
    // (`v-model.lazy`) never runs, so nothing is committed.
    event.stopImmediatePropagation();
    return;
  }
  editStartText.delete(el);
}

/**
 * Escape in a body cell's editor (I-2510, I-2833) — the way out of a grid whose
 * Tab walks every cell (and creates a row past the last one): the typing is
 * discarded — the editor shows the value it had when the typing began, and no
 * `change` and no save happen — the focus moves onto the table itself, and the
 * next Tab or Shift+Tab leaves the table for the page; Enter or an arrow goes
 * back into the cell. An editor that uses Escape itself (an open list, a
 * picker) prevents it first and keeps it.
 */
function leaveCellByEscape(event: KeyboardEvent, target: HTMLElement): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) return false;
  if (event.ctrlKey || event.metaKey || event.shiftKey) return false;
  if (!target.closest('.tbody .cell-edit')) return false;
  event.preventDefault();
  event.stopPropagation();
  if (isCellTextEditor(target)) {
    const start = editStartText.get(target);
    editStartText.delete(target);
    if (start !== undefined && target.value !== start) {
      target.value = start;
      // Editors that keep the typed text in their own state (currency, date,
      // time) take the restored text over, so their blur commits nothing new.
      // A `v-model.lazy` editor ignores `input`.
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
    discardingEditor = target;
  }
  try {
    parkTableFocus();
  } finally {
    discardingEditor = null;
  }
  parkedByEscape = true;
  return true;
}

/** The keys of a table parked by Escape: Tab leaves it, Enter or an arrow goes back in. */
function onParkedTableKey(event: KeyboardEvent): boolean {
  if (!parkedByEscape || event.target !== wrapperRef.value) return false;
  if (event.key === 'Tab' && !event.ctrlKey && !event.metaKey) {
    event.preventDefault();
    parkedByEscape = false;
    focusAdjacentPageTabStop(event.shiftKey ? -1 : 1);
    return true;
  }
  if (event.key === 'Enter' || event.key.startsWith('Arrow')) {
    event.preventDefault();
    parkedByEscape = false;
    focusFirstCell();
    return true;
  }
  return false;
}

function onWrapperFocusOut(event: FocusEvent) {
  if (event.target === wrapperRef.value) parkedByEscape = false;
  else if (event.target instanceof HTMLElement) editStartText.delete(event.target);
  onWrapperFocusInOut(event);
}

/**
 * When the last Tab key went: Shift+Tab from the page after the grid enters it
 * through the last control in its DOM — the header's filter and sort buttons.
 * Tab forward enters the cells (the wrapper's own focus), so Shift+Tab does
 * too (I-2510). Only the Tab key is noted; the listener lives as the table does.
 */
let lastTabKey: { shift: boolean; at: number } | null = null;
function noteTabKey(event: KeyboardEvent) {
  if (event.key === 'Tab') lastTabKey = { shift: event.shiftKey, at: performance.now() };
}
onMounted(() => document.addEventListener('keydown', noteTabKey, true));
onUnmounted(() => document.removeEventListener('keydown', noteTabKey, true));

/**
 * Shift+Tab into an edit grid from outside lands in a cell, like Tab forward:
 * the cell data entry left (`lastBodyFocus`), else the last editor of the last
 * row. A click or a Tab forward onto a header control keeps it. A view grid
 * whose Tab walk runs through its body (filter row → cells → page) is entered
 * the same way, so Shift+Tab retraces that walk (I-2833).
 */
function onWrapperFocusIn(event: FocusEvent) {
  onWrapperFocusInOut(event);
  const wrap = wrapperRef.value;
  const target = event.target as HTMLElement | null;
  if (!props.editMode && !viewTabWalksBody()) return;
  if (!wrap || !target || target === wrap) return;
  const from = event.relatedTarget as Node | null;
  if (from && wrap.contains(from)) return;
  if (!target.closest('.thead')) return;
  const tab = lastTabKey;
  if (!tab?.shift || performance.now() - tab.at > 500) return;
  lastTabKey = null;
  const previous = lastBodyFocus.value;
  if (previous && isBodyCellNavigable(previous.rowId, previous.columnId)) {
    void focusCell(previous.rowId, previous.columnId, { from: 'tab-back' });
    return;
  }
  focusLastTabCell();
}

async function onTableKeydown(event: KeyboardEvent) {
  // Accept Mac's Cmd as an alias for Ctrl; still bail on Alt.
  const ctrlLike = event.ctrlKey || event.metaKey;
  if (event.altKey) return;

  const target = event.target as HTMLElement | null;
  // An open row's detail is the consumer's content: its keys are its own.
  if (target?.closest('.detail-row')) return;
  const inInput = !!target?.closest('input, textarea, select, [contenteditable="true"]');
  // Enter confirms an editor's text (a date field commits it in place): it is
  // the text an Escape after it keeps.
  if (inInput && target && event.key === 'Enter') editStartText.delete(target);
  if (inInput && target && leaveCellByEscape(event, target)) return;
  if (onParkedTableKey(event)) return;

  // Spreadsheet convention: Ctrl/Cmd+Space toggles the active row without
  // moving the cell cursor, including while an editor owns DOM focus.
  if (ctrlLike && event.key === ' ' && rowSelectionEnabled.value && cursor.value) {
    const c = cursor.value;
    if (c.rowId !== SETTINGS_ROW_ID && c.rowId !== FILTER_ROW_ID && c.rowIdx >= 0) {
      event.preventDefault();
      event.stopPropagation();
      toggleRowSelection(c.rowIdx, { ctrlKey: true, shiftKey: false });
      return;
    }
  }

  // Checkbox lists: plain Space ticks the active row's box (Shift+Space: the
  // range from the last ticked row). A focused control — the box itself, a
  // button in a cell, an editor — keeps its own Space.
  if (
    checkboxSelection.value &&
    event.key === ' ' &&
    !ctrlLike &&
    cursor.value &&
    cursor.value.rowIdx >= 0 &&
    cursor.value.rowId !== SETTINGS_ROW_ID &&
    cursor.value.rowId !== FILTER_ROW_ID &&
    !isInteractiveTarget(target)
  ) {
    event.preventDefault();
    event.stopPropagation();
    tickRow(cursor.value.rowIdx, event.shiftKey);
    return;
  }

  // View-mode Enter → emit cell-double-click (arrows are handled per-cell).
  if (!props.editMode && event.key === 'Enter') {
    const c = cursor.value;
    const row = c ? dataRef.value[c.rowIdx] : null;
    if (c && row && c.rowId !== FILTER_ROW_ID) {
      // Focus on a row link (`rowHref`): Ctrl/Cmd/Shift+Enter is the browser's (new tab /
      // window) and emits nothing; a plain Enter is cancelled so the browser fires no
      // activation click on top of the emit.
      const onLink = !!target?.closest('[data-nx-row-link]');
      if (onLink && (ctrlLike || event.shiftKey)) return;
      if (onLink) event.preventDefault();
      emit('cell-double-click', c.rowId, c.columnId, row);
    }
    return;
  }

  // Home/End/PageUp/PageDown — jump-to-edge nav. Both modes, outside input only
  // (parallel to Del/Esc/Ctrl+A pattern).
  if (
    !inInput &&
    cursor.value &&
    (event.key === 'Home' ||
      event.key === 'End' ||
      event.key === 'PageUp' ||
      event.key === 'PageDown')
  ) {
    const c = cursor.value;
    const cols = visibleColumnOrder.value;
    if (event.key === 'Home') {
      const fb = firstFocusableColIdx(c.rowId);
      if (fb >= 0) {
        event.preventDefault();
        event.stopPropagation();
        focusCellIntern(c.rowId, cols[fb], 'top');
      }
      return;
    }
    if (event.key === 'End') {
      const lb = nextFocusableColIdx(c.rowId, cols.length, -1);
      if (lb >= 0) {
        event.preventDefault();
        event.stopPropagation();
        focusCellIntern(c.rowId, cols[lb], 'top');
      }
      return;
    }
    if (event.key === 'PageUp' && dataRef.value.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      const firstRowId = props.rowIdKey(dataRef.value[0]);
      // Scroll target row into the virtual window first so its cells
      // mount and register focus handlers — only then can
      // firstFocusableColIdx find them.
      setCursor(firstRowId, INDICATOR_COLUMN_ID);
      await scrollCursorIntoView();
      const fb = firstFocusableColIdx(firstRowId);
      const targetCol = isBodyCellNavigable(firstRowId, c.columnId)
        ? c.columnId
        : fb >= 0
          ? cols[fb]
          : null;
      if (targetCol) focusCellIntern(firstRowId, targetCol, 'top');
      return;
    }
    if (event.key === 'PageDown' && dataRef.value.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      let lastIdx = dataRef.value.length - 1;
      if (props.isPlaceholderRow) {
        while (lastIdx > 0 && props.isPlaceholderRow(dataRef.value[lastIdx])) lastIdx -= 1;
      }
      const lastRowId = props.rowIdKey(dataRef.value[lastIdx]);
      setCursor(lastRowId, INDICATOR_COLUMN_ID);
      await scrollCursorIntoView();
      const fb = firstFocusableColIdx(lastRowId);
      const targetCol = isBodyCellNavigable(lastRowId, c.columnId)
        ? c.columnId
        : fb >= 0
          ? cols[fb]
          : null;
      if (targetCol) focusCellIntern(lastRowId, targetCol, 'top');
      return;
    }
  }

  // Delete key: editable grids retain the built-in confirmation flow. A
  // read-only/list consumer owns deletion and receives the selected ids.
  if (
    !inInput &&
    rowSelectionEnabled.value &&
    selectedIds.value.size > 0 &&
    (event.key === 'Delete' || event.key === 'Backspace')
  ) {
    if (props.onDelete && !isDeleteDialogOpen.value) {
      event.preventDefault();
      event.stopPropagation();
      requestRowDeletion();
      return;
    }
    if (!props.onDelete) {
      event.preventDefault();
      event.stopPropagation();
      emit('row-delete-request', [...selectedIds.value]);
      return;
    }
  }

  // ESC: clear multi-selection. Only when focus is outside an input
  if (event.key === 'Escape') {
    if (!inInput && rowSelectionEnabled.value && selectedIds.value.size > 0) {
      event.preventDefault();
      event.stopPropagation();
      // A checkbox list drops every mark; the active row is no mark to keep.
      if (checkboxSelection.value) {
        deselectAll();
        return;
      }
      const rowId = cursor.value?.rowId;
      if (rowId) selectRow(rowId);
      return;
    }
  }

  // Ctrl/Cmd+A: select all rows. Only when focus is outside an input so we
  // don't hijack the native "select all text" inside editing cells.
  if (ctrlLike && (event.key === 'a' || event.key === 'A')) {
    if (!inInput && rowSelectionEnabled.value && props.data.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      selectAll();
      return;
    }
  }
}

// ---- Virtual column focus registration ----

function bindIndicatorAnchor(rowId: string, el: HTMLElement | null) {
  if (!props.editMode && !listSelection.value) return;
  const reg = getRegisterFocus(rowId, INDICATOR_COLUMN_ID);
  reg(el ? () => el.focus() : null);
}

function onVirtualColumnKeyDown(rowId: string, columnId: string, event: KeyboardEvent) {
  // Ctrl/Cmd + arrow: do nothing (jump-to-edge is on Home/End/PageUp/PageDown).
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (
    event.key === ' ' &&
    columnId === INDICATOR_COLUMN_ID &&
    rowId !== SETTINGS_ROW_ID &&
    rowId !== FILTER_ROW_ID &&
    rowSelectionEnabled.value
  ) {
    const rowIdx = viewIndexOfId(rowId);
    if (rowIdx >= 0) {
      event.preventDefault();
      event.stopPropagation();
      toggleRowSelection(rowIdx, { ctrlKey: true, shiftKey: false });
    }
    return;
  }
  // Enter or Space on the expand toggle opens or closes the row's detail.
  if ((event.key === 'Enter' || event.key === ' ') && columnId === EXPAND_COLUMN_ID) {
    event.preventDefault();
    event.stopPropagation();
    toggleRowExpanded(rowId);
    return;
  }
  // Enter on a row's checkbox keeps the grid meaning (open the row) instead of
  // ticking the box — a native checkbox does not react to Enter either.
  if (event.key === 'Enter' && columnId === SELECT_COLUMN_ID) {
    event.preventDefault();
    return;
  }
  const cellNav = getCellNav(rowId, columnId);
  let nav: ((intent?: CellNavigationIntent) => boolean | void) | null = null;
  switch (event.key) {
    case 'ArrowUp':
      nav = cellNav.focusAbove;
      break;
    case 'ArrowDown':
      nav = cellNav.focusBelow;
      break;
    case 'ArrowLeft':
      nav = cellNav.focusLeft;
      break;
    case 'ArrowRight':
      nav = cellNav.focusRight;
      break;
    case 'Tab':
      nav = event.shiftKey ? cellNav.focusLeft : cellNav.focusRight;
      break;
    default:
      return;
  }
  // Virtual-column Shift+Arrow extends from the selection's existing anchor.
  // Checkbox lists keep every arrow key a pure cursor move.
  const isShiftArrow =
    event.shiftKey &&
    (event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight');
  const anchorBefore =
    isShiftArrow && rowSelectionEnabled.value && !checkboxSelection.value
      ? lastSelectedIdx.value
      : null;
  if (event.key === 'Tab') {
    // Tab has its own content-only order and page-boundary escape. If no target
    // exists, leave the event native instead of trapping focus in this anchor.
    if (nav('tab') === false) return;
  } else {
    nav();
  }
  event.preventDefault();
  event.stopPropagation();
  if (
    rowSelectionEnabled.value &&
    cursor.value &&
    cursor.value.rowId !== FILTER_ROW_ID &&
    cursor.value.rowId !== SETTINGS_ROW_ID
  ) {
    if (anchorBefore !== null) {
      lastSelectedIdx.value = anchorBefore;
      toggleRowSelection(cursor.value.rowIdx, { ctrlKey: false, shiftKey: true });
    } else if (props.rowSelectionMode === 'rows' && !event.shiftKey) {
      // List semantics: arrows move the active row and collapse selection to
      // it. Legacy cell grids keep cursor and selection independent.
      selectRow(cursor.value.rowId);
    }
  }
  // View-mode emits cell-single-click on keyboard nav (parity with click path).
  if (
    !props.editMode &&
    cursor.value &&
    cursor.value.rowId !== SETTINGS_ROW_ID &&
    cursor.value.rowId !== FILTER_ROW_ID
  ) {
    const row = dataRef.value[cursor.value.rowIdx];
    emit('cell-single-click', cursor.value.rowId, cursor.value.columnId, row, event);
  }
}

/** The expand toggle is reached with the arrow keys like the other chrome columns. */
function bindExpandToggle(rowId: string, el: HTMLButtonElement | null) {
  const reg = getRegisterFocus(rowId, EXPAND_COLUMN_ID);
  if (el) reg(() => el.focus());
  else reg(null);
}

function bindRowActionButton(rowId: string, el: HTMLButtonElement | null) {
  const reg = getRegisterFocus(rowId, ROW_ACTION_COLUMN_ID);
  if (el) reg(() => el.focus());
  else reg(null);
}

// ---- Row link (`rowHref`) ----

/**
 * The address of a row's page for one of its cells; none in edit mode (cells there
 * are fields, not text) and none for a column that opts out (`rowLinkDisabled`).
 */
function rowLinkOf(row: T, columnId: string): string | null {
  if (!props.rowHref || props.editMode) return null;
  if (columnConfigs.value.get(columnId)?.rowLinkDisabled) return null;
  return props.rowHref(row) || null;
}

/** Attributes of the cell anchor of a row that has an address; `{}` otherwise. */
function rowLinkAttrs(row: T, columnId: string): Record<string, string> {
  const href = rowLinkOf(row, columnId);
  return href ? { href, draggable: 'false', 'data-nx-row-link': '' } : {};
}

/**
 * Click on a cell anchor (a row link only; the plain `div` ignores it).
 *  - Ctrl/Cmd/Shift: the browser's (new tab / window). The table's own handler is
 *    skipped, so the click neither toggles nor extends the row selection — but the
 *    table cursor follows the click (DOM focus is on the anchor already), so Enter and
 *    the arrow keys act on the clicked cell.
 *  - Alt: the browser's, untouched.
 *  - Plain click: never follows the link; the cursor, selection and double-click
 *    handling of the table run as on any cell.
 *  - Plain click with `detail === 0` (keyboard or assistive-technology activation of
 *    the link, e.g. a screen reader's browse mode): "open" — one `cell-double-click`,
 *    the table's click handler skipped. A plain Enter never gets here: its keydown is
 *    cancelled (`onTableKeydown`), so the browser sends no click for it.
 * The middle button is an `auxclick`, not a `click`, and opens a new tab natively.
 */
function onRowLinkClick(event: MouseEvent, rowId: string, columnId: string, row: T): void {
  const anchor = event.currentTarget as HTMLElement | null;
  if (!anchor?.hasAttribute('data-nx-row-link')) return;
  if (event.button !== 0 || event.altKey) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    event.stopPropagation();
    setCursor(rowId, columnId);
    return;
  }
  event.preventDefault();
  if (event.detail === 0) {
    event.stopPropagation();
    emit('cell-double-click', rowId, columnId, row);
  }
}

// User-content cell anchor (view-mode only). Each cell wraps its content
// in a `tabindex="-1"` div that registers as the cell's focus target so
// `navigateFocus` can walk into body cells and clicks on cell padding
// land focus inside the table (keeping `tableFocused` true).
function bindUserCellAnchor(rowId: string, columnId: string, el: HTMLElement | null) {
  if (props.editMode) return;
  const reg = getRegisterFocus(rowId, columnId);
  if (el) reg(() => el.focus());
  else reg(null);
}

function bindSettings(el: HTMLElement | null) {
  const reg = getRegisterFocus(SETTINGS_ROW_ID, INDICATOR_COLUMN_ID);
  if (el) reg(() => el.focus());
  else reg(null);
}

function bindFilterRowIndicator(el: HTMLButtonElement | null) {
  const reg = getRegisterFocus(FILTER_ROW_ID, INDICATOR_COLUMN_ID);
  if (el) reg(() => el.focus());
  else reg(null);
}

function bindFilterRowAction(el: HTMLButtonElement | null) {
  const reg = getRegisterFocus(FILTER_ROW_ID, ROW_ACTION_COLUMN_ID);
  if (el) reg(() => el.focus());
  else reg(null);
}

// Selection (row-level multi/range select state).
const selection = useTableSelection<T>({
  data: dataRef,
  rowIdKey: props.rowIdKey,
  allowRowSelection: rowSelectionEnabled,
  onSelectionChange: (ids, rows) => emit('selection-change', ids, rows),
  isPlaceholderRow: (row) => props.isPlaceholderRow?.(row) === true,
  isRowSelectable: (row) => props.isRowSelectable?.(row) !== false,
});
const {
  selectedIds,
  selectedData,
  anchorIndex: lastSelectedIdx,
  isSelected,
  isRowSelectable,
  selectableIds,
  toggleRowSelection,
  setRangeSelected,
  selectRange,
  selectRow,
  selectAll,
  deselectAll,
  setSelectedIds,
} = selection;

/**
 * Checkbox lists: tick or untick the row at display position `rowIdx`. With
 * `range`, every row from the last ticked one to it takes its new state.
 */
function tickRow(rowIdx: number, range: boolean): void {
  const row = dataRef.value[rowIdx];
  if (!isRowSelectable(row)) return;
  if (range) setRangeSelected(rowIdx, !isSelected(props.rowIdKey(row)));
  else toggleRowSelection(rowIdx, { ctrlKey: true, shiftKey: false });
}

// The navigation applies the established click rules — a view grid selects on
// every click. A checkbox list keeps a plain click for the active row and lets
// only a modifier click mark rows.
const navigationSelection: typeof selection = {
  ...selection,
  toggleRowSelection: (rowIdx, mods) => {
    if (!checkboxSelection.value) {
      toggleRowSelection(rowIdx, mods);
      return;
    }
    if (mods.ctrlKey || mods.shiftKey) tickRow(rowIdx, mods.shiftKey);
  },
};

/** Controls inside the table that own their Space key (see onTableKeydown). */
const interactiveTargetSelector = [
  'input',
  'textarea',
  'select',
  'button',
  // A row link (`rowHref`) is the cell itself, not a control inside it.
  'a[href]:not([data-nx-row-link])',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="switch"]',
].join(',');

function isInteractiveTarget(target: HTMLElement | null): boolean {
  const control = target?.closest(interactiveTargetSelector);
  return !!control && control !== wrapperRef.value && !!wrapperRef.value?.contains(control);
}

// ---- Checkbox column (rowSelectionMode: 'checkbox') ----

/**
 * Register a row's box as the focus target of its checkbox cell. Like the row
 * actions it is reached with the arrow keys and is no Tab stop of its own —
 * the grid owns the Tab order, one stop per row would bury the page behind it.
 * NxCheckbox renders its `role="checkbox"` button inside a wrapper, hence the
 * lookup; its template binds no tabindex, so the value set here stays.
 */
function bindRowCheckbox(rowId: string, inst: unknown): void {
  const reg = getRegisterFocus(rowId, SELECT_COLUMN_ID);
  const root = (inst as ComponentPublicInstance | null)?.$el as unknown;
  const box = root instanceof Element ? root.querySelector<HTMLElement>('[role="checkbox"]') : null;
  if (!box || box.matches(':disabled')) {
    reg(null);
    return;
  }
  box.tabIndex = -1;
  reg(() => box.focus());
}

/**
 * A click on a row's box (or its cell) ticks that row only — the active row
 * stays where it is. Shift sets the range from the last ticked row.
 */
function onRowCheckboxClick(rowIdx: number, event: MouseEvent): void {
  if (isCreationActive.value) return;
  tickRow(rowIdx, event.shiftKey);
}

/** Shift+click would otherwise stretch the page's text selection up to the box. */
function onSelectCellMouseDown(event: MouseEvent): void {
  if (event.shiftKey) event.preventDefault();
}

/** The table owns the box's state (`isSelected`); the box's own model write is dropped. */
function keepCheckboxControlled(): void {
  // Selection changes arrive through onRowCheckboxClick, which also sees Shift.
}

function rowCheckboxAriaLabel(row: T, rowIdx: number): string {
  return (
    props.rowCheckboxLabel?.(row, dataIndexOf(rowIdx)) ??
    i18nInjection.t('common.table.row_selector.select', { row: rowIdx + 1 })
  );
}

const selectAllAriaLabel = computed(
  () => props.selectAllLabel ?? i18nInjection.t('common.table.menu.select_all')
);

/** State of the header box over eligible rows of `data`. */
const selectAllState = computed<'none' | 'some' | 'all'>(() => {
  let selected = 0;
  for (const id of selectableIds.value) {
    if (selectedIds.value.has(id)) selected++;
  }
  if (selected === 0) return 'none';
  return selected === selectableIds.value.size ? 'all' : 'some';
});

const hasSelectableRows = computed(() => selectableIds.value.size > 0);

/** Header box: select every row of `data` (the filtered set), or clear them once all are. */
function onSelectAllToggle(): void {
  if (selectAllState.value === 'all') deselectAll();
  else selectAll();
}

function isBodyCellNavigable(rowId: string, columnId: string): boolean {
  if (!props.editMode || columnId === INDICATOR_COLUMN_ID || columnId === ROW_ACTION_COLUMN_ID) {
    return false;
  }
  const config = columnConfigs.value.get(columnId);
  if (!config?.editCell || !visibleColumnOrder.value.includes(columnId)) return false;
  const row = props.data.find((candidate) => props.rowIdKey(candidate) === rowId);
  if (!row || props.isPlaceholderRow?.(row)) return false;
  return resolveCellMode(config, true, !!props.onEdit, row) === 'edit';
}

/** Columns may stay pointer/arrow reachable while opting out of rapid Tab entry. */
function isBodyCellTabNavigable(_rowId: string, columnId: string): boolean {
  return columnConfigs.value.get(columnId)?.tabNavigationDisabled !== true;
}

// Navigation (cursor + cell-focus registry + cell-nav). All directional
// cell navigation (`getCellNav`, `navigateFocus`) lives in the composable;
// the table only contributes DOM-bound hooks (row-creation past last row).
const {
  cursor,
  focusTransitionPending,
  isCursor,
  setCursor,
  clearCursor,
  onCellClickProxy: navigationOnCellClickProxy,
  onRowClick: navigationOnRowClick,
  scrollCursorIntoView,
  isKeyboardActive,
  getRegisterFocus,
  isCellFocusable,
  firstFocusableColIdx,
  nextFocusableColIdx,
  getCellNav,
  focusCell: focusCellIntern,
  focusLastTabCell,
  focusFirstTabCell,
  viewTabWalksBody,
} = useTableNavigation<T>({
  data: dataRef,
  rowIdKey: props.rowIdKey,
  mode: computed(() => (props.editMode ? 'edit' : 'view')),
  ignoreClicks: computed(() => isCreationActive.value),
  visibleColumnOrder,
  offsetOf,
  virtualWindow,
  scrollTop,
  tableRef,
  tableHeadRef,
  detectorRef,
  firstColRef,
  lastColRef,
  leadingInset: leadingStickyInset,
  bottomInset: summaryBottomInset,
  getCellElement: findRenderedCell,
  isCellNavigable: isBodyCellNavigable,
  isCellTabNavigable: isBodyCellTabNavigable,
  selection: navigationSelection,
  onCellSingleClick: (rowId, columnId, rowData, event) => {
    emit('cell-single-click', rowId, columnId, rowData, event);
    const extendsSelection = event?.ctrlKey || event?.metaKey || event?.shiftKey;
    if (!extendsSelection) rowOpen.single(rowId, columnId, rowData);
  },
  onCellDoubleClick: (rowId, columnId, rowData) => {
    if (!rowOpen.double(rowId, columnId, rowData)) {
      emit('cell-double-click', rowId, columnId, rowData);
    }
  },
  focusHooks: {
    onPastLastRow: () => tryCreateNewRowArrowNav(),
    onTabPastLastRow: tryCreateNewRowTabNav,
    onTabEscape: focusAdjacentPageTabStop,
    onEscapeAbove: () => {
      // no-op
    },
  },
  isPlaceholderRow: (row) => props.isPlaceholderRow?.(row) === true,
});

// A footer can grow independently of the data (slot content, density, fonts,
// or runtime opt-in). Recheck the cursor after its new geometry is committed.
watch(
  [summaryBottomInset, effectiveBodyRowHeight],
  () => {
    if (props.summary) void nextTick(scrollCursorIntoView);
  },
  { flush: 'post' }
);

const lastBodyFocus = ref<{ rowId: string; columnId: string } | null>(null);
let lastEmittedActiveRowId: string | null | undefined;
watch(cursor, (current) => {
  const row =
    current && current.rowId !== SETTINGS_ROW_ID && current.rowId !== FILTER_ROW_ID
      ? dataRef.value[current.rowIdx]
      : null;
  const activeRowId = row && !props.isPlaceholderRow?.(row) ? props.rowIdKey(row) : null;
  if (activeRowId !== lastEmittedActiveRowId) {
    lastEmittedActiveRowId = activeRowId;
    emit('active-row-change', activeRowId, activeRowId ? row : null);
  }
  if (
    current &&
    current.rowId !== SETTINGS_ROW_ID &&
    current.rowId !== FILTER_ROW_ID &&
    current.columnId !== INDICATOR_COLUMN_ID &&
    current.columnId !== SELECT_COLUMN_ID &&
    current.columnId !== EXPAND_COLUMN_ID &&
    current.columnId !== ROW_ACTION_COLUMN_ID
  ) {
    lastBodyFocus.value = { rowId: current.rowId, columnId: current.columnId };
  }
});

// ---- Focus ownership and leaving edit mode ----
// One decision for "is the user still editing here?" — see
// `composables/useTableFocusOwnership.ts` for its causes and their deferrals.
const {
  tableFocused,
  claimTableFocus,
  parkTableFocus,
  releaseTableFocus,
  onWrapperFocus,
  onWrapperFocusInOut,
  onWrapperPointerDown,
} = useTableFocusOwnership({
  wrapperRef,
  focusScope,
  cursor,
  clearCursor,
  transitionPending: focusTransitionPending,
  onKeyboardEntry: () => enterByKeyboard(),
  onRelease: () => releaseWheelLatch(),
});

// ---- Drag selection (`dragSelection`) ----
const {
  dragSelecting,
  onRowMouseDown: dragSelectionMouseDown,
  isPending: dragSelectionPending,
} = useDragSelection({
  enabled: () => props.dragSelection && rowSelectionEnabled.value && !isCreationActive.value,
  scroller: tableRef,
  header: tableHeadRef,
  rowIndexOf: viewIndexOfId,
  selectedIds,
  selectRange,
  setSelectedIds,
  onEnd: onDragSelectionEnd,
});

/**
 * Mouse button on a body row. The reorder handle (`onRowMoved`) and a native
 * row drag from a marked row (`onRowDragStart` drags the selection) keep their
 * own gesture.
 */
function onRowMouseDown(event: MouseEvent, row: T): void {
  if (!isRowSelectable(row)) return;
  const rowId = props.rowIdKey(row);
  const target = event.target instanceof Element ? event.target : null;
  if (props.onRowMoved && !props.onRowDragStart && target?.closest('.first-col')) return;
  if (props.onRowDragStart && isSelected(rowId)) return;
  dragSelectionMouseDown(event, rowId);
}

/**
 * Like a Shift+click on the row the drag ended on, the cursor follows the end
 * of the range — on the column the drag started in, on the row indicator in
 * edit mode (no editor opens). A checkbox list marks rows without opening one,
 * so its active row stays.
 */
function onDragSelectionEnd(endIdx: number, startColumnId: string | null): void {
  emit('drag-selection-end', new Set(selectedIds.value), selectedData.value);
  if (checkboxSelection.value) return;
  const row = dataRef.value[endIdx];
  if (!row || props.isPlaceholderRow?.(row)) return;
  const rowId = props.rowIdKey(row);
  if (!props.editMode && startColumnId && isBodyCellNavigable(rowId, startColumnId)) {
    void focusCellIntern(rowId, startColumnId, 'click');
  } else if (props.editMode || listSelection.value) {
    void focusCellIntern(rowId, INDICATOR_COLUMN_ID, 'click');
  }
}

const { onTableWheel, releaseWheelLatch } = useTableWheel({
  tableRef,
  wrapperRef,
  tableFocused,
  wheelRequiresFocus: () => props.wheelRequiresFocus,
  onUserInteract: () => {
    claimTableFocus();
    // A virtual scroll can unmount the focused cell after the wheel handler
    // returns. Reclaim the wrapper after Vue has rendered the new window so
    // the next wheel event still belongs to this table.
    requestAnimationFrame(() => claimTableFocus());
  },
});

onUnmounted(() => {
  document.removeEventListener('pointerdown', onRowContextPointerDown, true);
  document.removeEventListener('keydown', onFullscreenKeydown, true);
  if (savedBodyOverflow != null && typeof document !== 'undefined') {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = null;
  }
});

/**
 * Save state is written to the DOM by a directive, never by a template binding.
 *
 * Binding it meant calling the consumer's lookup inside the row/column loops,
 * which made their status store a dependency of the one render effect that draws
 * the visible body. One successful save walks four states (begin → succeed →
 * fade → clear), so it cost four full body renders — measured at 94.6 ms in a
 * 33 x 8 grid, two of them firing seconds after the save while the user has
 * already moved on (I-995).
 *
 * A directive owns one effect per element, so a status change re-runs the two
 * attribute writes for the cells it actually touched and the body is not
 * rendered again. This is deliberately imperative: the attributes are no longer
 * part of any vnode, so Vue neither re-creates nor clears them on unrelated
 * re-renders.
 *
 * `cellSaveStatusProps` below stays an ordinary template call on purpose. It is
 * evaluated inside `NxInputActionsAlignProvider`'s slot, and slot content runs
 * during that child's render — so the read is collected by the provider's effect
 * and was never part of this component's. Moving it into a component of its own
 * would buy one avoided provider update on the single cell hosting an editor.
 */
const saveStateEffects = new WeakMap<HTMLElement, WatchHandle>();

/**
 * An effect created from a directive hook has no owning component instance —
 * `invokeDirectiveHook` does not set one — so Vue would send a throw from the
 * consumer's lookup to `logError` instead of `errorCaptured` and
 * `app.config.errorHandler`. That is where it went while the status was read
 * during render, so route it back there through an instance-owned watcher.
 * Wrapped in a fresh object so two identical errors still trigger it.
 */
const saveStatePaintError = shallowRef<{ error: unknown } | null>(null);
watch(saveStatePaintError, (thrown) => {
  if (!thrown) return;
  // Dropped before rethrowing so the table does not keep the last error and its
  // stack reachable for its whole lifetime.
  saveStatePaintError.value = null;
  throw thrown.error;
});

function bindSaveStateEffect(el: HTMLElement, paint: () => void) {
  stopSaveStateEffect(el);
  const guardedPaint = () => {
    try {
      paint();
    } catch (error) {
      saveStatePaintError.value = { error };
    }
  };
  // Tables that do not pass `cellSaveStatus` — 22 of the 25 in this repo — get no
  // effect at all. Nothing can ever change for them, so paying an effect per row
  // and per cell on mount and on every row scrolled into the window is waste.
  if (!props.cellSaveStatus) {
    guardedPaint();
    return;
  }
  saveStateEffects.set(el, watchEffect(guardedPaint));
}

function stopSaveStateEffect(el: HTMLElement) {
  saveStateEffects.get(el)?.();
  saveStateEffects.delete(el);
}

function setOptionalAttribute(el: HTMLElement, name: string, value: string | undefined) {
  if (value === undefined) el.removeAttribute(name);
  else el.setAttribute(name, value);
}

function paintCellSaveState(el: HTMLElement, rowId: string, columnId: string) {
  const status = props.cellSaveStatus?.(rowId, columnId);
  const state = status?.error ? 'error' : status?.loading ? 'loading' : undefined;
  setOptionalAttribute(el, 'data-cell-save-status', state);
}

// ---- Saved check (top-right corner) ----
/** How long the saved check stays after the last `showSaved()`. */
const SAVED_CHECK_MS = 1500;
const savedVisible = ref(false);
let savedTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * A write landed. Instead of painting cells green, the table flashes one check
 * in its top-right corner, opposite the settings gear; every further save
 * restarts the moment.
 */
function showSaved(): void {
  savedVisible.value = true;
  if (savedTimer) clearTimeout(savedTimer);
  savedTimer = setTimeout(() => {
    savedVisible.value = false;
    savedTimer = undefined;
  }, SAVED_CHECK_MS);
}
onUnmounted(() => {
  if (savedTimer) clearTimeout(savedTimer);
});

/**
 * A failed aggregate write belongs to the row, so the row scans its columns —
 * all of them, including the ones currently hidden, exactly as the template
 * binding did. The scan now runs when a status changes rather than on every
 * render of the row.
 */
function paintRowSaveState(el: HTMLElement, rowId: string) {
  let error = false;
  if (props.cellSaveStatus) {
    for (const columnId of columnConfigs.value.keys()) {
      if (props.cellSaveStatus(rowId, columnId)?.error) {
        error = true;
        break;
      }
    }
  }
  setOptionalAttribute(el, 'data-row-save-status', error ? 'error' : undefined);
  setOptionalAttribute(el, 'aria-invalid', error ? 'true' : undefined);
}

/**
 * The identity behind an element never changes: both keys embed it
 * (`row-<rowId>`, `td-<columnId>-<rowId>`), so Vue can only reuse an element for
 * the same row and column. `updated` therefore has exactly one job — pick up a
 * consumer that starts or stops passing `cellSaveStatus` at runtime, which is
 * what decides whether this element owns an effect at all.
 */
function saveStateBindingIsCurrent(el: HTMLElement) {
  return saveStateEffects.has(el) === !!props.cellSaveStatus;
}

const vCellSaveState = {
  mounted(el: HTMLElement, binding: DirectiveBinding<[string, string]>) {
    const [rowId, columnId] = binding.value;
    bindSaveStateEffect(el, () => paintCellSaveState(el, rowId, columnId));
  },
  updated(el: HTMLElement, binding: DirectiveBinding<[string, string]>) {
    if (saveStateBindingIsCurrent(el)) return;
    const [rowId, columnId] = binding.value;
    bindSaveStateEffect(el, () => paintCellSaveState(el, rowId, columnId));
  },
  unmounted: stopSaveStateEffect,
};

const vRowSaveState = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string>) {
    const rowId = binding.value;
    bindSaveStateEffect(el, () => paintRowSaveState(el, rowId));
  },
  updated(el: HTMLElement, binding: DirectiveBinding<string>) {
    if (saveStateBindingIsCurrent(el)) return;
    const rowId = binding.value;
    bindSaveStateEffect(el, () => paintRowSaveState(el, rowId));
  },
  unmounted: stopSaveStateEffect,
};

/**
 * NxLoadingWrapper props for one edit cell. Both flags are required there, and an
 * idle cell is simply the absence of an entry — the consumer only reports what is
 * pending or failed.
 */
function cellSaveStatusProps(rowId: string, columnId: string) {
  const status = props.cellSaveStatus?.(rowId, columnId);
  return {
    loading: status?.loading ?? false,
    error: status?.error ?? false,
    errorMessage: status?.errorMessage,
    successPresentation: 'border' as const,
    errorPresentation: 'border' as const,
  };
}

// Column Drag
const {
  draggedColumn,
  columnDropZoneIds,
  columnBridgeZone,
  onColDragStart,
  onColDragEnter,
  onColDrop,
  onColDragEnd,
} = useColumnDrag({
  tableRef,
  columnOrder,
  // Column drag only concerns real user columns; virtual sentinels have no width entry.
  visibleColumnOrder: userVisibleColumnOrder,
  columnWidths,
  getColumnWidth: (columnId) =>
    thRefs.value.get(columnId)?.getBoundingClientRect().width ?? minWidthOf(columnId),
  onColumnMoved: (newPositions) => emit('column-moved', newPositions),
});

// Row Drag
const {
  waitForRowMove,
  rowDropZones,
  rowBridgeZone,
  rowTransformStyles,
  isRowInDragUnit,
  onRowDragStart: onInternalRowDragStart,
  onRowDragEnter,
  onRowBridgeEnter,
  onRowDrop,
  onRowDragEnd,
} = useRowDrag<T>({
  tableRef,
  data: dataRef,
  rowIdKey: props.rowIdKey,
  onRowMoved: computed(() => {
    return async (fromPosition: number, toPosition: number, movedCount?: number) => {
      // A row `rowMovable` refuses is never moved, whichever entry asked.
      if (!isRowMovableAt(fromPosition)) return;
      // Reordering switches grouping off, so display and data order agree here.
      const lastSelectedId =
        lastSelectedIdx.value !== null
          ? props.rowIdKey(dataRef.value[lastSelectedIdx.value])
          : null;
      await props.onRowMoved?.(fromPosition, toPosition, movedCount);
      if (lastSelectedId) {
        lastSelectedIdx.value = viewIndexOfId(lastSelectedId);
      }
    };
  }),
  virtualWindow,
  draggedRow,
  rowParentId: computed(() => props.rowParentId),
});

/** Whether `rowMovable` lets the row at display index `rowIdx` be moved (default: yes). */
function isRowMovableAt(rowIdx: number): boolean {
  if (!props.rowMovable) return true;
  const row = dataRef.value[rowIdx];
  return !!row && props.rowMovable(row, dataIndexOf(rowIdx));
}

/** The reorder handle's dragstart: a row `rowMovable` refuses does not start a drag. */
function onReorderDragStart(event: DragEvent, rowIdx: number): void {
  if (!isRowMovableAt(rowIdx)) {
    event.preventDefault();
    return;
  }
  onInternalRowDragStart(event, rowIdx);
}

function onConsumerRowDragStart(event: DragEvent, rowIdx: number): void {
  if (!props.onRowDragStart) return;
  // The press started a drag selection (an unmarked row): no native drag.
  if (dragSelectionPending()) {
    event.preventDefault();
    return;
  }
  const draggedRowData = dataRef.value[rowIdx];
  if (!draggedRowData || props.isPlaceholderRow?.(draggedRowData)) {
    event.preventDefault();
    return;
  }

  const draggedRowId = props.rowIdKey(draggedRowData);
  const rows = selectedIds.value.has(draggedRowId)
    ? dataRef.value
        .filter(
          (row) => !props.isPlaceholderRow?.(row) && selectedIds.value.has(props.rowIdKey(row))
        )
        .map((row) => ({ id: props.rowIdKey(row), data: row }))
    : [{ id: draggedRowId, data: draggedRowData }];

  if (props.onRowDragStart?.(rows, event) === false) {
    event.preventDefault();
  }
}

function onConsumerRowDragEnd(event: DragEvent): void {
  nativeRowDrop.reset();
  props.onRowDragEnd?.(event);
}

const rowContextOpen = ref(false);
const rowContextIdx = ref<number | null>(null);
/** The row the menu's open entries act on (`recordList`). */
const rowContextRow = ref<T | null>(null) as Ref<T | null>;
const rowContextX = ref(0);
const rowContextY = ref(0);
const moveToPosOpen = ref(false);
const moveFromIdx = ref<number | null>(null);

function closeRowContext() {
  rowContextOpen.value = false;
  rowContextIdx.value = null;
  rowContextRow.value = null;
}

function onRowContextMenu(event: MouseEvent, rowIdx: number, row: T) {
  if (props.isPlaceholderRow?.(row)) return;
  emit('row-context-menu', {
    rowId: props.rowIdKey(row),
    rowData: row,
    x: event.clientX,
    y: event.clientY,
    event,
  });
  // Reordering retains its own menu. Without it — or when the row offers
  // nothing in it (`rowMovable` and `rowDeletable` refuse it) — leave the
  // browser's default menu untouched so a listener may decide whether to
  // suppress it itself.
  const offersOpen = !!props.recordList && !props.editMode;
  const offersMove = !!props.onRowMoved && (isRowMovableAt(rowIdx) || canOfferDeleteAt(rowIdx));
  if (!offersOpen && !offersMove) return;
  event.preventDefault();
  event.stopPropagation();
  rowContextRow.value = offersOpen ? row : null;
  rowContextIdx.value = props.onRowMoved ? rowIdx : null;
  rowContextX.value = event.clientX;
  rowContextY.value = event.clientY;
  rowContextOpen.value = true;
}

function onOpenMenuSelect(target: NxRowOpenTarget | NxRowOpenPlace) {
  const row = rowContextRow.value;
  closeRowContext();
  if (row) rowOpen.openAt(target, props.rowIdKey(row), '', row);
}

/** The middle button on a cell: a chosen target replaces the browser's own. */
function onCellAuxClick(event: MouseEvent, rowId: string, columnId: string, row: T): void {
  if (event.button !== 1 || props.isPlaceholderRow?.(row)) return;
  if (!rowOpen.middle(rowId, columnId, row)) return;
  event.preventDefault();
  event.stopPropagation();
}

function onCellMiddleDown(event: MouseEvent): void {
  // The browser would start autoscroll before the auxclick arrives.
  if (event.button === 1 && props.recordList && !props.editMode) event.preventDefault();
}

function onMoveToPositionMenuSelect() {
  moveFromIdx.value = rowContextIdx.value;
  closeRowContext();
  moveToPosOpen.value = true;
}

// Same confirm flow as the row's delete button: a row inside the selection
// deletes the whole selection, any other row only itself.
function onDeleteMenuSelect() {
  const idx = rowContextIdx.value;
  closeRowContext();
  if (idx != null) requestRowDeletion(idx);
}

function rowMoveUnitAt(rowIdx: number): { start: number; size: number } {
  const parentIdOf = props.rowParentId;
  if (!parentIdOf) return { start: rowIdx, size: 1 };
  const info = deriveTree(props.data, props.rowIdKey, parentIdOf);
  if (rowIdx < 0 || rowIdx >= info.sizeOf.length) return { start: rowIdx, size: 1 };
  return dragUnitOf(info, rowIdx);
}

const moveToPosMax = computed(() => {
  const from = moveFromIdx.value;
  if (from == null) return Math.max(1, props.data.length);
  const unit = rowMoveUnitAt(from);
  return Math.max(1, props.data.length - unit.size + 1);
});

async function applyMoveToPosition(desiredPos1: number) {
  const clicked = moveFromIdx.value;
  if (clicked == null) return;
  if (!isRowMovableAt(clicked)) {
    moveToPosOpen.value = false;
    moveFromIdx.value = null;
    return;
  }
  const unit = rowMoveUnitAt(clicked);
  const from = unit.start;
  const parentIdOf = props.rowParentId;
  const valid = parentIdOf
    ? validTargets(deriveTree(props.data, props.rowIdKey, parentIdOf), from, props.data.length)
    : null;
  const to = moveToPositionTarget(from, desiredPos1, unit.size, props.data.length, valid);
  moveToPosOpen.value = false;
  moveFromIdx.value = null;
  if (to == null) return;
  if (parentIdOf) await props.onRowMoved?.(from, to, unit.size);
  else await props.onRowMoved?.(from, to);
}

function onRowContextPointerDown(event: PointerEvent) {
  if (!rowContextOpen.value) return;
  const t = event.target;
  if (t instanceof Element && t.closest('.row-context-host, .nx-menu-panel')) return;
  closeRowContext();
}

watch(rowContextOpen, (open) => {
  if (open) document.addEventListener('pointerdown', onRowContextPointerDown, true);
  else document.removeEventListener('pointerdown', onRowContextPointerDown, true);
});

// ---- Remaining NxTable-local logic ----

const hasLastCol = computed(
  () =>
    (!props.hideHeader && hasFilters.value) ||
    !!props.onDelete ||
    (!!props.onCreate && !props.hideCreateButton) ||
    // A table that saves shows its saved check in the last header cell.
    (!props.hideHeader && !!props.cellSaveStatus)
);

function measureColumnWidth(columnId: string): void {
  if (columnWidths.value.has(columnId) || !columnVisible.value.get(columnId)) {
    return;
  }
  // An absent width preserves responsive sizing across state round trips.
  // Only an explicit width (stored or resized) switches a column to fixed px.
  if (hasColumnGrowth(columnId)) return;
  const thEl = thRefs.value.get(columnId);
  if (!thEl) return;

  columnWidths.value.set(
    columnId,
    Math.max(
      thEl.clientWidth,
      thFilterRefs.value.get(columnId)?.clientWidth ?? 0,
      tdRefs.value.get(columnId)?.clientWidth ?? 0
    )
  );
}

const setThRef = (columnId: string, el: unknown) => {
  if (el) {
    if (thRefs.value.get(columnId) !== el) {
      thRefs.value.set(columnId, el as HTMLElement);
      nextTick(() => {
        measureColumnWidth(columnId);
        measureHeaderFloor(columnId);
      });
    }
  } else if (thRefs.value.has(columnId)) {
    thRefs.value.delete(columnId);
  }
};
const setThFilterRef = (columnId: string, el: unknown) => {
  if (el) {
    if (thFilterRefs.value.get(columnId) !== el) {
      thFilterRefs.value.set(columnId, el as HTMLElement);
    }
  } else if (thFilterRefs.value.has(columnId)) {
    thFilterRefs.value.delete(columnId);
  }
};

// ---- Column registration ----
const columnRegistrationCount = ref<Map<string, number>>(new Map());

function unregisterColum(columnId: string): void {
  columnConfigs.value.delete(columnId);
  columnOrder.value = columnOrder.value.filter((v) => v !== columnId);
  columnVisible.value.delete(columnId);
  columnWidths.value.delete(columnId);
  frozenAutoColumnWidths.delete(columnId);
  sortPrio.value = sortPrio.value.filter((v) => v !== columnId);
  sortInfo.value.delete(columnId);
  filterInfo.value.delete(columnId);
  columnFilters.value.delete(columnId);
}

/** First-seen column order — the factory layout reset restores this. */
const defaultColumnOrder = ref<string[]>([]);

function rememberDefaultColumn(columnId: string): void {
  if (!defaultColumnOrder.value.includes(columnId)) {
    defaultColumnOrder.value.push(columnId);
  }
}

function registerColum(column: TableColumnConfig<T, string>) {
  const registrations = (columnRegistrationCount.value.get(column.columnId) ?? 0) + 1;
  columnRegistrationCount.value.set(column.columnId, registrations);

  if (registrations === 1) {
    if (!columnOrder.value.includes(column.columnId)) {
      columnOrder.value.push(column.columnId);
    }
    if (!columnVisible.value.has(column.columnId)) {
      columnVisible.value.set(column.columnId, column.defaultVisible);
    }
    rememberDefaultColumn(column.columnId);
  }
  columnConfigs.value.set(column.columnId, column);
  const floor = minWidthOf(column.columnId);
  const current = columnWidths.value.get(column.columnId);
  if (current != null && current < floor) {
    columnWidths.value.set(column.columnId, floor);
  }

  return () => {
    const remaining = (columnRegistrationCount.value.get(column.columnId) ?? 1) - 1;
    if (remaining <= 0) {
      columnRegistrationCount.value.delete(column.columnId);
      unregisterColum(column.columnId);
      return;
    }
    columnRegistrationCount.value.set(column.columnId, remaining);
  };
}
function updateRegister(column: ColumnRegistryUpdate<T, string>) {
  if (!columnConfigs.value.has(column.columnId)) {
    return;
  }
  for (const key of Object.keys(column)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    columnConfigs.value.get(column.columnId)[key] = column[key];
  }
}
// watcher for initial width — thRefs may not exist yet on the same tick; setThRef
// calls measureColumnWidth once the header cell mounts.
watch(
  () => [...columnVisible.value.keys(), columnOrder.value.length],
  () => {
    nextTick(() => {
      for (const columnId of columnVisible.value.keys()) {
        measureColumnWidth(columnId);
      }
    });
  },
  { flush: 'post' }
);
provide(registerColumKey, {
  registerColum: registerColum as (
    column: TableColumnConfig<Record<string, unknown>>
  ) => () => void,
  updateRegisterValue: updateRegister as (
    column: ColumnRegistryUpdate<Record<string, unknown>>
  ) => () => void,
});

/**
 * Apply a new column visibility/order list (from the settings modal). Hidden
 * columns lose their filters and sorts; the list order becomes the column
 * order (the visibility list supports drag-reorder).
 */
function applyColumnVisibility(
  newVisibilities: { displayName: string; visible: boolean; sticky?: boolean }[]
) {
  const displayNameToId = new Map(
    columnOrder.value.map((columnId) => [
      columnConfigs.value.get(columnId)!.displayName ?? columnId,
      columnId,
    ])
  );
  const newVisibilitiesForEmit: NxTableEmits<T>['column-visibility-changed']['0'] = new Map();
  const newSorts: NxTableEmits<T>['column-visibility-changed']['1'] = new Map();
  const oldSortPrio = [...sortPrio.value];
  const oldOrder = [...columnOrder.value];
  // Pinned columns move to the front: only the leading run sticks, so a pinned
  // column left behind an unpinned one would silently scroll away.
  const newStickyForEmit = new Map<string, boolean>();
  newVisibilities.forEach(({ displayName, sticky }) => {
    if (sticky === undefined) return;
    const columnId = displayNameToId.get(displayName)!;
    if (isColumnSticky(columnId) !== sticky) newStickyForEmit.set(columnId, sticky);
    columnSticky.value.set(columnId, sticky);
  });
  const dialogOrder = newVisibilities.map(({ displayName }) => displayNameToId.get(displayName)!);
  const newOrder = [
    ...dialogOrder.filter((columnId) => isColumnSticky(columnId)),
    ...dialogOrder.filter((columnId) => !isColumnSticky(columnId)),
  ];
  newVisibilities.forEach(({ displayName, visible }) => {
    const columnId = displayNameToId.get(displayName)!;
    if (columnVisible.value.get(columnId) !== visible) {
      newVisibilitiesForEmit.set(columnId, visible);
    }
    columnVisible.value.set(columnId, visible);
    if (!visible) {
      // reset all filter/sort info
      filterInfo.value.set(columnId, []);
      columnFilters.value.delete(columnId);
      sortInfo.value.delete(columnId);
      sortPrio.value = sortPrio.value.filter((colId) => colId !== columnId);
    }
  });
  const newSortPrio = new Map(sortPrio.value.map((columnId, idx) => [columnId, idx]));
  oldSortPrio.forEach((columnId, oldPrio) => {
    if (!newSortPrio.has(columnId)) {
      newSorts.set(columnId, { order: null, prio: null });
      return;
    }
    const newPrio = newSortPrio.get(columnId);
    if (newPrio !== oldPrio) {
      newSorts.set(columnId, { prio: newPrio });
    }
  });
  const orderChanged = newOrder.some((colId, idx) => oldOrder[idx] !== colId);
  if (orderChanged) {
    columnOrder.value = newOrder;
    emit('column-moved', new Map(newOrder.map((colId, idx) => [colId, idx])));
  }
  if (newStickyForEmit.size > 0) emit('column-sticky-changed', newStickyForEmit);
  emit('column-visibility-changed', newVisibilitiesForEmit, newSorts);
  // load exactly the same amount of data, but with new columns
  debouncedOnChange({ limit: props.data.length, offset: 0 });
}

// ---- Orchestration ----
function getTableMetadata(): TableMetadataState<T> {
  const positionLookup = new Map(columnOrder.value.map((columnId, idx) => [columnId, idx]));
  const prioLookup = new Map(sortPrio.value.map((columnId, idx) => [columnId, idx]));
  return {
    filterModifier: filterModifier.value,
    pageSize: paginationLimitInternal.value,
    filterValues: filterInfo.value,
    baseFilterValues: baseFilterValues.value,
    columnFilters: new Map(columnFilters.value),
    advancedFilter: advancedFilter.value,
    pagination: paginationInfo.value,
    columns: [...columnConfigs.value.keys()].map((columnId) => {
      return {
        columnId,
        visible: columnVisible.value.get(columnId)!,
        width: frozenAutoColumnWidths.has(columnId) ? undefined : columnWidths.value.get(columnId),
        position: positionLookup.get(columnId)!,
        sortOrder: sortInfo.value.get(columnId) ?? null,
        sortPrio: prioLookup.get(columnId) ?? null,
        sticky: isColumnSticky(columnId),
      };
    }),
  };
}

// ---- Classic pager (paginationMode="pages") ----
const isPagesMode = computed(() => props.paginationMode === 'pages');
const pagerPage = computed(
  () => Math.floor(paginationInfo.value.offset / Math.max(1, paginationInfo.value.limit)) + 1
);
function goToPage(page: number) {
  const limit = paginationLimitInternal.value;
  const target = Math.max(1, page);
  void applyPaginationChange({ limit, offset: (target - 1) * limit });
}
function changePageSize(pageSize: number) {
  paginationLimitInternal.value = pageSize;
  void applyPaginationChange({ limit: pageSize, offset: 0 });
}

const debouncedOnChange = debounce(
  (pagination: PaginationConfig) => void applyPaginationChange(pagination),
  300
);

async function applyPaginationChange(pagination: PaginationConfig) {
  if (!props.onTableStateChange) {
    return;
  }
  isLoading.value = true;
  paginationInfo.value = pagination;
  paginationEndReached.value = false;

  try {
    await props.onTableStateChange(getTableMetadata());
    tableRef.value?.scrollTo({ top: 0 });

    // After new data: re-check end-reached. Prefer explicit totalCount; fall back
    // to "page came back smaller than the requested limit" when totalCount is absent.
    const total = props.totalCount;
    if (total !== undefined) {
      if (props.data.length >= total) {
        paginationEndReached.value = true;
      }
    } else if (props.data.length < pagination.limit) {
      paginationEndReached.value = true;
    }
  } finally {
    isLoading.value = false;
  }

  // After new data: check if pagination handle is already visible
  const wrapperRect = wrapperRef.value?.getBoundingClientRect();
  const handleRect = paginationHandleRef.value?.getBoundingClientRect();
  if (handleRect && wrapperRect && handleRect.top < wrapperRect.bottom) {
    paginate();
  }
}

async function doRefetch(options?: RefetchOptions) {
  const {
    resetPagination: resetPag = true,
    resetFilters: resetFil = false,
    resetSort: resetSrt = false,
  } = options ?? {};

  if (resetPag) {
    paginationEndReached.value = false;
    paginationInfo.value = { limit: paginationLimitInternal.value, offset: 0 };
  }
  if (resetFil) {
    filterInfo.value = new Map();
    columnFilters.value = new Map();
    advancedFilter.value = null;
    columnOrder.value.forEach((cID) => columnConfigs.value.get(cID)!.onQueryLoaded?.([]));
  }
  if (resetSrt) {
    sortPrio.value = [];
    sortInfo.value = new Map();
  }

  if (!props.onTableStateChange) return;

  isLoading.value = true;
  try {
    await props.onTableStateChange(getTableMetadata());
    const total = props.totalCount;
    if (total !== undefined) {
      if (props.data.length >= total) {
        paginationEndReached.value = true;
      }
    } else if (props.data.length < paginationLimitInternal.value) {
      paginationEndReached.value = true;
    }
    if (resetPag || resetFil || resetSrt) {
      tableRef.value?.scrollTo({ top: 0 });
    }
  } finally {
    isLoading.value = false;
  }
}

async function focusCell(rowId: string, columnId: string | null, options?: { from?: FocusFrom }) {
  const colId = columnId ?? INDICATOR_COLUMN_ID;
  // The filter row is no data row, so it is not in `props.data`: looking it up
  // there made this a silent no-op for it — and `focusFirstCell`, which starts
  // in the filter row, with it. A lookup dialog starts typing there
  // (BLP/nx#2819).
  if (rowId === FILTER_ROW_ID) {
    if (isCellFocusable(rowId, colId))
      await focusCellIntern(rowId, colId, options?.from ?? 'click');
    return;
  }
  const row = props.data.find((candidate) => props.rowIdKey(candidate) === rowId);
  if (!row || props.isPlaceholderRow?.(row)) return;
  if (colId === INDICATOR_COLUMN_ID) {
    if ((!props.editMode && !listSelection.value) || !isCellFocusable(rowId, colId)) return;
  } else if (!props.editMode) {
    // View grids own a cursor as well: a click assigns it directly (`onCellClickProxy`,
    // view branch) without consulting `isBodyCellNavigable` — that predicate is
    // edit-only by definition, its first line returns false whenever `editMode` is off.
    // Applying it here too made the exposed focusCell a silent no-op in every view
    // grid, so a caller mirroring an outside selection could never move the marking and
    // could not tell "row not loaded" from "grid refused the request" (I-1028). Follow
    // the click rule instead: the row is checked above, and `setCursor` itself refuses a
    // column outside `visibleColumnOrder` — no second copy of that rule here.
    //
    // Cursor only, no DOM focus. Painting the marking is what the caller asked for; a
    // view cell has no editor to enter, and grabbing the focus would pull it off the
    // record navigator's button in the index header — its second click would then land
    // on an unfocused button and keyboard stepping would break after one record. A
    // caller that does want the focus has `claimTableFocus` / `focusFirstCell`.
    setCursor(rowId, colId);
    // A refused request must not scroll: `scrollCursorIntoView` moves whatever the cursor
    // is NOW, so it would yank the viewport to the OLD marking the caller just failed to
    // replace.
    if (!isCursor(rowId, colId)) return;
    await scrollCursorIntoView();
    return;
  } else if (!isBodyCellNavigable(rowId, colId)) {
    return;
  }
  await focusCellIntern(rowId, colId, options?.from ?? 'click');
}

/**
 * Tab or Shift+Tab from the page onto the wrapper. An edit grid enters its
 * remembered or first cell. A view grid with a filter row stays on the
 * wrapper: the table is its own tab stop, and the next Tab walks the header,
 * the filter row and the cells, Shift+Tab the same stops back (I-2833). Only
 * a caller that asks for it — a lookup dialog typing into its filter
 * (BLP/nx#2819) — sends the focus into the filter row via `focusFirstCell`.
 */
function enterByKeyboard() {
  if (!props.editMode && !props.hideHeader && firstFocusableColIdx(FILTER_ROW_ID, true) !== -1) {
    return;
  }
  focusFirstCell();
}

function focusFirstCell() {
  // Edit grids enter at the last useful body cell, not at filter/settings
  // chrome. This makes Tab-out/Tab-back resume data entry where the user left
  // it while still unmounting lazy editors when focus is outside the table.
  if (props.editMode) {
    const previous = lastBodyFocus.value;
    if (previous && isBodyCellNavigable(previous.rowId, previous.columnId)) {
      void focusCell(previous.rowId, previous.columnId, { from: 'click' });
      return;
    }
    // The first cell Tab walks, the one Shift+Tab leaves the grid from — not a
    // column that opts out of Tab (I-2833).
    if (focusFirstTabCell()) return;
    for (const row of dataRef.value) {
      if (props.isPlaceholderRow?.(row)) continue;
      const rowId = props.rowIdKey(row);
      const bodyColIdx = firstFocusableColIdx(rowId, true);
      if (bodyColIdx !== -1) {
        void focusCell(rowId, visibleColumnOrder.value[bodyColIdx], { from: 'click' });
        return;
      }
    }
    if (createButtonRef.value) {
      createButtonRef.value.focus();
      return;
    }
  }
  if (props.hideHeader) {
    const firstRow = dataRef.value.find((row) => !props.isPlaceholderRow?.(row));
    const firstColumn = userVisibleColumnOrder.value[0] ?? INDICATOR_COLUMN_ID;
    if (firstRow) void focusCellIntern(props.rowIdKey(firstRow), firstColumn, 'click');
    return;
  }
  let colIdx = firstFocusableColIdx(FILTER_ROW_ID, true);
  if (colIdx !== -1) {
    focusCell(FILTER_ROW_ID, visibleColumnOrder.value[colIdx], {
      from: 'click',
    });
    return;
  }
  if (dataRef.value.length === 0) return;
  const firstId = props.rowIdKey(dataRef.value[0]);
  colIdx = firstFocusableColIdx(firstId, true);
  if (colIdx !== -1) {
    focusCell(firstId, visibleColumnOrder.value[colIdx], {
      from: 'click',
    });
  }
}

/** Scroll to a DISPLAY position (the index in `data` unless grouped). */
function scrollToViewIndex(rowIdx: number | 'top' | 'bottom') {
  return new Promise<void>((resolve) => {
    const el = tableRef.value;
    if (!el || props.data.length === 0) {
      resolve();
      return;
    }
    if (rowIdx === 'bottom') {
      // Virtual body end + hard DOM max. offsetOf alone misses sticky header /
      // late-measured pads, so a pure index scroll often stops short of true end.
      const virtualEnd = offsetOf(props.data.length);
      el.scrollTo({ top: Math.max(virtualEnd, el.scrollHeight - el.clientHeight) });
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        nextTick(() => {
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
            resolve();
          });
        });
      });
      return;
    }
    const index = rowIdx === 'top' ? 0 : rowIdx;
    el.scrollTo({ top: offsetOf(index) });
    requestAnimationFrame(() => {
      nextTick(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  });
}

/** Exposed `scrollTo`: the consumer names a row by its index in `data`. */
function scrollTo(rowIdx: number | 'top' | 'bottom') {
  return scrollToViewIndex(typeof rowIdx === 'number' ? viewIndexOf(rowIdx) : rowIdx);
}

const scrollState = useScrollState(tableRef);
/** Cells run on under the pinned row-action column (the table scrolls sideways). */
const contentRight = useContentRight(tableRef);

defineExpose<NxTableExpose<T>>({
  refetch: doRefetch,
  scrollState,
  showSaved,
  getCellElement: (rowId: string, columnId?: string) => {
    if (columnId && !columnVisible.value.has(columnId)) {
      return null;
    }
    const columnId_ =
      columnId ?? columnOrder.value.filter((cId) => columnVisible.value.get(cId))[0];
    if (!columnId_) {
      return null;
    }
    return findRenderedCell(rowId, columnId_);
  },
  focusedCell: computed(() =>
    cursor.value && cursor.value.columnId && cursor.value.columnIdx != null
      ? {
          rowId: cursor.value.rowId,
          // Body rows: the index in `data`; header rows keep their sentinel (< 0).
          rowIdx: cursor.value.rowIdx >= 0 ? dataIndexOf(cursor.value.rowIdx) : cursor.value.rowIdx,
          columnId: cursor.value.columnId,
          columnIdx: cursor.value.columnIdx,
        }
      : null
  ),
  rowDensity,
  focusCell,
  clearCursor,
  focusFirstCell,
  claimTableFocus,
  releaseTableFocus,
  getTableState: () => {
    return getTableMetadata();
  },
  setTableState: (state: Parameters<NxTableExpose<T>['setTableState']>[0]) => {
    if (state.pageSize) {
      paginationLimitInternal.value = state.pageSize;
    }
    if (state.filterModifier) {
      filterModifier.value = state.filterModifier;
    }
    if (state.searchRowVisible !== undefined) {
      searchRowOverride.value = state.searchRowVisible;
    }
    if (state.rowDensity !== undefined) {
      ownRowDensity.value = state.rowDensity;
    }
    if (state.filterValues) {
      restoreQuickFilters(state.filterValues);
    }
    if (state.columnFilters) {
      columnFilters.value = new Map(state.columnFilters);
    }
    if (state.advancedFilter !== undefined) {
      advancedFilter.value = state.advancedFilter;
    }
    if (state.columns) {
      const diff = new Set(state.columns.map((v) => v.columnId)).symmetricDifference(
        new Set(columnOrder.value)
      );
      if (diff.size > 0) {
        console.error(
          `You can't set column definitions for only a subset or superset of all columns! (${[...diff].join(', ')})`
        );
        throw Error(
          `You can't set column definitions for only a subset or superset of all columns! (${[...diff].join(', ')})`
        );
      }
      if (state.columns.some((col) => col.position !== undefined)) {
        if (!state.columns.every((col) => col.position !== undefined)) {
          console.error("You can't set the column position of only a subset of all columns");
          throw Error("You can't set the column position of only a subset of all columns");
        }
        columnOrder.value = state.columns
          .toSorted((a, b) => a.position! - b.position!)
          .map((col) => col.columnId) as string[];
      }
      state.columns
        .filter((col) => col.visible !== undefined)
        .forEach((col) => columnVisible.value.set(col.columnId as string, col.visible!));
      // A column without `sticky` falls back to `stickyColumnIds` — so a reset
      // layout (no saved pin) drops the user's old pins instead of keeping them.
      state.columns.forEach((col) => {
        if (col.sticky === undefined) columnSticky.value.delete(col.columnId as string);
        else columnSticky.value.set(col.columnId as string, col.sticky);
      });
      state.columns.forEach((col) => {
        const id = col.columnId as string;
        frozenAutoColumnWidths.delete(id);
        if (col.width !== undefined) {
          columnWidths.value.set(
            id,
            Math.min(maxWidthOf(id), Math.max(minWidthOf(id), col.width!))
          );
        } else {
          columnWidths.value.delete(id);
        }
      });
      if (state.columns.some((col) => col.sortOrder !== undefined)) {
        state.columns.forEach((col) => {
          if (
            (col.sortPrio !== undefined && col.sortPrio !== null && !col.sortOrder) ||
            (col.sortOrder && (col.sortPrio === undefined || col.sortPrio === null))
          ) {
            console.error(
              "You can't set the column sortPrio without setting the sortOrder and vice versa"
            );
            throw Error(
              "You can't set the column sortPrio without setting the sortOrder and vice versa"
            );
          }
        });
        const sortColumns = state.columns
          .filter((col) => col.sortOrder)
          .sort((a, b) => a.sortPrio! - b.sortPrio!);
        sortPrio.value = sortColumns.map((col) => col.columnId as string);
        sortInfo.value = new Map(
          sortColumns.map((col) => [col.columnId as string, col.sortOrder!])
        );
      }
    }
    if (state.pagination) {
      paginationInfo.value = state.pagination;
    }
  },
  startCreation: () => {
    isCreationActive.value = true;
  },
  doCreation: async (rowToCreate: PartialOrNull<T>, options?: { focusColumnId?: string }) => {
    if (!props.onCreate) {
      throw new Error('No `onCreate` function was provided!');
    }
    // A refused write is NOT an exception here (I-1935): it resolves `false`.
    // Nearly every caller is a click handler (`@click="() => tableRef?.doCreation({})"`),
    // and a rejection from one reaches `LayoutComponentSlot`'s error containment,
    // which retires the whole section until the page is reloaded. A caller that
    // needs the outcome reads the boolean; the reason travels on the consumer's
    // own channel (the ERP grid's toast and its `create-failed` emit).
    return await createNewRow(rowToCreate, options);
  },
  askDoDeletionOfSelected: () => {
    if (!props.onDelete) {
      throw new Error('No `onDelete` function was provided!');
    }
    requestRowDeletion();
  },
  scrollTo,
  ensureFullyLoaded,
  virtualWindow: computed(() => virtualWindow.value),
  selectedIds: computed(() => selectedIds.value),
  selectedData,
  selectAll,
  selectRow,
  deselectAll,
  setSelectedIds,
  expandedRowIds: computed(() => expansion.expandedIds.value),
  setRowExpanded,
  toggleRowExpanded,
  collapseAllRows,
});

// ---- CSV export ----
/** Columns offered in the export dialog, in the current order and visibility. */
const csvColumnVisibility = computed(() =>
  columnOrder.value.map((columnId) => ({
    columnId,
    displayName: columnConfigs.value.get(columnId)!.displayName ?? columnId,
    visible: columnVisible.value.get(columnId)!,
  }))
);

/** First rows a full export fetches (see issue #6162 for lifting the cap). */
const CSV_FULL_EXPORT_LIMIT = 10000;

function downloadCsv(rows: T[], columns: string[], delimiter: string): void {
  const csv = buildCsv(rows, columns, delimiter, {
    header: (columnId) => columnConfigs.value.get(columnId)!.displayName ?? columnId,
    cell: (columnId, row, rowIndex) =>
      columnConfigs.value.get(columnId)!.toCsvString(row, rowIndex),
  });
  downloadCsvFile(csvFileName(props.csvFilenamePrefix ?? 'table'), csv);
}

/**
 * Exports the loaded rows, or asks the consumer (`onExportData`) for a full
 * set when the export needs more than what is loaded: all rows, other
 * columns, or a windowed table with placeholder slots. A grouped table
 * exports its rows in display order, without the header rows.
 */
async function exportCsv({
  delimiter,
  columns,
  fullData,
}: {
  delimiter: string;
  columns: string[];
  fullData: boolean;
}): Promise<void> {
  const isRealRow = (row: T) => !props.isPlaceholderRow?.(row);
  const currentConfig = getTableMetadata();
  const visible = new Set(
    currentConfig.columns.filter((col) => col.visible).map((col) => col.columnId as string)
  );
  const hasMissingColumns = columns.some((columnId) => !visible.has(columnId));
  const hasPlaceholders = !!props.isPlaceholderRow && props.data.some((row) => !isRealRow(row));
  const needFullFetch = hasPlaceholders || (fullData && !paginationEndReached.value);

  if (!needFullFetch && !hasMissingColumns) {
    downloadCsv(grouping.orderRows(props.data), columns, delimiter);
    return;
  }
  if (!props.onExportData) {
    // No export callback registered → fall back to the loaded rows.
    downloadCsv(grouping.orderRows(props.data.filter(isRealRow)), columns, delimiter);
    return;
  }
  const exportRows = await props.onExportData({
    ...currentConfig,
    pagination: needFullFetch
      ? { limit: CSV_FULL_EXPORT_LIMIT, offset: 0 }
      : { offset: 0, limit: props.data.length },
  });
  downloadCsv(grouping.orderRows(exportRows.filter(isRealRow)), columns, delimiter);
}

/**
 * Opens the delete confirmation — the one entry of the Delete key, the row's
 * delete button, the row menu and `askDoDeletionOfSelected`. `rowIdx` (a
 * display index) names a row: a row inside the selection deletes the whole
 * selection, any other row only itself. Without it the selection is deleted.
 */
function requestRowDeletion(rowIdx?: number): void {
  if (!props.onDelete || isDeleteDialogOpen.value) return;
  let pending: { id: string; index: number; inSelection: boolean };
  if (rowIdx === undefined) {
    // Only the deletable part of a selection goes to the confirmation.
    const id = deletableSelectedIds.value[0];
    if (id === undefined) return;
    const index = props.data.findIndex((row) => props.rowIdKey(row) === id);
    pending = { id, index, inSelection: true };
  } else {
    if (!canOfferDeleteAt(rowIdx)) return;
    const row = dataRef.value[rowIdx];
    if (!row) return;
    const id = props.rowIdKey(row);
    pending = { id, index: dataIndexOf(rowIdx), inSelection: isSelected(id) };
  }
  // A refusal the screen can tell from the rows comes before the question, not
  // after it (`deleteGuard`).
  if (props.deleteGuard) {
    const rows = pending.inSelection
      ? props.data.filter((row) => deletableSelectedIds.value.includes(props.rowIdKey(row)))
      : props.data.filter((row) => props.rowIdKey(row) === pending.id);
    const reason = props.deleteGuard(rows);
    if (reason) {
      emit('delete-refused', reason);
      return;
    }
  }
  pendingDelete.value = pending;
  isDeleteDialogOpen.value = true;
}

/** Whether `rowDeletable` lets the row at display index `rowIdx` be deleted (default: yes). */
function isRowDeletableAt(rowIdx: number): boolean {
  if (!props.rowDeletable) return true;
  const row = dataRef.value[rowIdx];
  return !!row && props.rowDeletable(row, dataIndexOf(rowIdx));
}

/**
 * The selected ids a delete acts on: every selected row `rowDeletable` allows.
 * An id without a loaded row stays in, as before `rowDeletable` existed.
 */
const deletableSelectedIds = computed<string[]>(() => {
  const ids = [...selectedIds.value];
  const allowed = props.rowDeletable;
  if (!allowed) return ids;
  return ids.filter((id) => {
    const index = props.data.findIndex((row) => props.rowIdKey(row) === id);
    return index < 0 || allowed(props.data[index], index);
  });
});

/**
 * Whether a delete started at display index `rowIdx` (button, row menu) has
 * something to delete: the row itself, or — inside the selection — any
 * deletable selected row.
 */
function canOfferDeleteAt(rowIdx: number): boolean {
  if (!props.onDelete) return false;
  if (!props.rowDeletable) return true;
  const row = dataRef.value[rowIdx];
  if (!row) return false;
  if (isSelected(props.rowIdKey(row))) return deletableSelectedIds.value.length > 0;
  return isRowDeletableAt(rowIdx);
}

const Promise_all = Promise.all.bind(Promise);

async function onDeleteDialogSubmit() {
  if (!pendingDelete.value) return;

  // Anchor for post-delete selection: single-row dismiss-button deletes use the
  // clicked row's index (the button click doesn't update cursor — see the
  // focus-in bypass). Selection-wide deletes anchor on the cursor (or the first
  // pending id if cursor was never set). A display position: the successor is
  // the next row on screen, also in a grouped table.
  const anchorIdx = pendingDelete.value.inSelection
    ? cursor.value && cursor.value.rowId !== FILTER_ROW_ID
      ? cursor.value.rowIdx
      : viewIndexOfId(pendingDelete.value.id)
    : viewIndexOf(pendingDelete.value.index);
  const anchorColumnId = cursor.value?.columnId ?? null;
  const anchorRowId = cursor.value?.rowId ?? null;

  const restoreDeleteAnchor = async (): Promise<void> => {
    if (!anchorRowId || !anchorColumnId) return;
    const rowStillExists = props.data.some((row) => props.rowIdKey(row) === anchorRowId);
    if (!rowStillExists || !isBodyCellNavigable(anchorRowId, anchorColumnId)) return;
    // Closing the confirm dialog briefly moves DOM focus outside the table.
    // Reclaim it before the deferred focusout cleanup can clear the cursor and
    // unmount a lazy editor; a failed write must leave the user where they were.
    claimTableFocus({ focus: false });
    await nextTick();
    await focusCellIntern(anchorRowId, anchorColumnId, 'click');
  };

  const deleteRow = async (row: { id: string; index: number }): Promise<boolean> => {
    try {
      return (await props.onDelete!(row)) !== false;
    } catch (error) {
      // The consumer owns user-facing error reporting (DataTable uses its ERP
      // toast). Never let a rejected row write crash the complete table/widget.
      console.error('[NxTable] row deletion failed', error);
      return false;
    }
  };

  if (pendingDelete.value.inSelection) {
    const results = await Promise_all(
      deletableSelectedIds.value.map((id) => {
        const index = props.data.findIndex((row) => props.rowIdKey(row) === id);
        return deleteRow({ id, index });
      })
    );
    // A handled or rejected failure leaves pendingDelete, cursor, and the
    // complete selection intact so the user can inspect/retry the operation.
    if (results.some((deleted) => !deleted)) {
      await restoreDeleteAnchor();
      return;
    }
    deselectAll();
  } else {
    const deleted = await deleteRow({
      id: pendingDelete.value.id,
      index: pendingDelete.value.index,
    });
    if (!deleted) {
      await restoreDeleteAnchor();
      return;
    }
  }
  pendingDelete.value = null;

  // Restore the user's working column at the successor row (or predecessor
  // when the deleted row was last). Falling back to the first focusable cell
  // is only for a column that is hidden/read-only on the destination row.
  await nextTick();
  if (dataRef.value.length === 0 || anchorIdx < 0) return;
  const newRowIdx = Math.min(anchorIdx, dataRef.value.length - 1);
  const newRowId = props.rowIdKey(dataRef.value[newRowIdx]);
  const preferredColumn =
    anchorColumnId && isBodyCellNavigable(newRowId, anchorColumnId) ? anchorColumnId : null;
  const cIdx = preferredColumn ? -1 : firstFocusableColIdx(newRowId, true);
  const targetColumn = preferredColumn ?? (cIdx !== -1 ? visibleColumnOrder.value[cIdx] : null);
  if (targetColumn) {
    focusCellIntern(newRowId, targetColumn, 'click');
    // A checkbox list moves only the active row; marks are the user's to set.
    if (rowSelectionEnabled.value && !checkboxSelection.value) selectRow(newRowId);
  }
}
</script>

<template>
  <!-- children -->
  <slot></slot>

  <Teleport to="body" :disabled="!isFullscreen">
    <div
      class="fs-shell"
      data-nx-scope="table"
      :class="{ 'is-fullscreen': isFullscreen }"
      :role="isFullscreen ? 'dialog' : undefined"
      :aria-modal="isFullscreen ? 'true' : undefined"
      :aria-label="isFullscreen ? i18nInjection.t('common.table.fullscreen.title') : undefined"
    >
      <div v-if="isFullscreen" class="fs-scrim" aria-hidden="true" />
      <button
        v-if="isFullscreen"
        type="button"
        class="fs-close"
        :aria-label="i18nInjection.t('common.table.fullscreen.close')"
        @click="closeFullscreen"
      >
        <DismissIcon class="nxt-icon-sm" aria-hidden="true" />
      </button>
      <div
        :ref="(el) => (wrapperRef = el as HTMLElement)"
        :tabindex="0"
        :class="[
          'wrapper',
          {
            'passive-selection': !tableFocused,
            'zebra-stripes': zebraStripes,
            'horizontal-lines': horizontalLines,
            'vertical-lines': verticalLines,
            'header-brand': headerBrand,
            'cells-top': cellsTop,
            'edit-mode': props.editMode,
            'content-right': contentRight,
            'uniform-rows': props.rowSizing === 'uniform',
            'list-layout': props.layout === 'list',
            'is-fill': props.fill,
            'checkbox-selection': checkboxSelection,
            'drag-selecting': dragSelecting,
          },
        ]"
        @keydown="onTableKeydown"
        @focus.self="onWrapperFocus"
        @focusin="onWrapperFocusIn"
        @focusout="onWrapperFocusOut"
        @beforeinput.capture="noteEditStart"
        @change.capture="onCellChangeCapture"
        @pointerdown="onWrapperPointerDown"
      >
        <div
          :ref="(el) => (tableRef = el as HTMLElement)"
          :class="[
            'table',
            {
              'empty-collapsed': showEmptyState && !props.summary,
              'always-scrollbars': alwaysShowScrollbars,
              'overscroll-contain': props.overscrollY === 'contain',
            },
          ]"
          role="grid"
          :aria-label="i18nInjection.t('common.table.fullscreen.title')"
          :aria-rowcount="gridAriaRowCount"
          :aria-colcount="gridAriaColCount"
          :aria-multiselectable="checkboxSelection ? 'true' : undefined"
          :style="`--last-col-size: ${!!props.onCreate && !props.hideCreateButton ? 2 : 1};`"
          @wheel="onTableWheel"
        >
          <div
            v-if="!props.hideHeader"
            :ref="(el) => (tableHeadRef = el as HTMLElement)"
            class="thead"
            role="rowgroup"
          >
            <div
              class="tr"
              role="row"
              aria-rowindex="1"
              :style="{
                minHeight: props.headerRowHeight,
                maxHeight: props.headerRowHeight,
              }"
            >
              <div
                :ref="(el) => (firstColRef = el as HTMLElement)"
                class="th first-col"
                role="columnheader"
                aria-colindex="1"
              >
                <NxDropdownList
                  :is-open="isSettingsMenuOpen"
                  align="start"
                  fit-width
                  @close="isSettingsMenuOpen = false"
                >
                  <template #trigger="triggerProps">
                    <button
                      :ref="
                        (el) => {
                          bindSettings(el as HTMLElement);
                          triggerProps.triggerRef.value = el as HTMLElement;
                        }
                      "
                      :aria-haspopup="triggerProps['aria-haspopup']"
                      :aria-expanded="triggerProps['aria-expanded']"
                      :aria-controls="triggerProps['aria-controls']"
                      :aria-label="i18nInjection.t('common.table.menu.open')"
                      :title="i18nInjection.t('common.table.menu.open')"
                      tabindex="-1"
                      class="button"
                      @click="
                        () => {
                          isSettingsMenuOpen = !isSettingsMenuOpen;
                        }
                      "
                      @keydown="
                        (e) => {
                          triggerProps.onTriggerKeydown(e);
                          if (e.key === 'ArrowDown') return;
                          onVirtualColumnKeyDown(SETTINGS_ROW_ID, INDICATOR_COLUMN_ID, e);
                        }
                      "
                      @focusin="
                        () => {
                          deselectAll();
                          clearCursor();
                        }
                      "
                    >
                      <SettingsIcon :filled="true" />
                    </button>
                  </template>
                  <!-- Active filters live INSIDE the gear menu (overlay) — the old
                   bar above the table made the whole grid jump whenever
                   filters appeared or disappeared. -->
                  <div v-if="hasActiveFilters" class="menu-filter-panel">
                    <NxTableFilterChips
                      layout="menu"
                      :quick-filters="filterInfo"
                      :column-filters="columnFilters"
                      :advanced-filter="advancedFilter"
                      :column-meta="filterColumnMeta"
                      @remove-quick="removeQuickFilter"
                      @remove-condition="removeColumnCondition"
                      @clear-advanced="applyAdvancedFilter(null)"
                      @open-advanced="
                        () => {
                          isFilterWizardOpen = true;
                          isSettingsMenuOpen = false;
                        }
                      "
                    />
                  </div>
                  <NxDropdownListItem
                    v-if="hasActiveFilters"
                    @select="
                      () => {
                        resetAllFilters();
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.filter.reset_all') }}
                  </NxDropdownListItem>
                  <!-- Wizard / save live only in the filter-manage dialog — gear
                   keeps chips + manage entry so each action has one home. -->
                  <NxDropdownListItem
                    @select="
                      () => {
                        isFilterManageOpen = true;
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.manage_filters') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    v-if="props.csvFilenamePrefix"
                    @select="
                      () => {
                        isCsvExportDialogOpen = true;
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.csv_export') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    @select="
                      () => {
                        isColumnDialogOpen = true;
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.toggle_columns') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    v-if="rowSelectionEnabled"
                    @select="
                      () => {
                        selectAll();
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.select_all') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    v-if="rowSelectionEnabled && selectedIds.size > 0"
                    @select="
                      () => {
                        deselectAll();
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    {{ i18nInjection.t('common.table.menu.deselect') }} ({{ selectedIds.size }})
                  </NxDropdownListItem>
                  <slot name="settingsDropdown"></slot>
                  <NxDropdownListItem
                    @select="
                      () => {
                        isPreferencesOpen = true;
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.settings') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem @select="resetColumnWidths">
                    {{ i18nInjection.t('common.table.menu.reset_widths') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    v-if="!isFullscreen && !insideDialog"
                    @select="openFullscreen"
                  >
                    {{ i18nInjection.t('common.table.menu.fullscreen') }}
                  </NxDropdownListItem>
                  <NxDropdownListItem
                    @select="
                      () => {
                        resetLayout();
                        isSettingsMenuOpen = false;
                      }
                    "
                  >
                    {{ i18nInjection.t('common.table.menu.reset_layout') }}
                  </NxDropdownListItem>
                </NxDropdownList>
              </div>
              <div
                v-if="checkboxSelection"
                class="th select-col"
                role="columnheader"
                aria-colindex="2"
              >
                <NxTableSelectAllCheckbox
                  :state="selectAllState"
                  :label="selectAllAriaLabel"
                  :disabled="!hasSelectableRows"
                  @toggle="onSelectAllToggle"
                />
              </div>
              <div
                v-for="(columnId, idx) in userVisibleColumnOrder"
                :id="`th-${columnId}`"
                :key="columnId"
                :ref="(el) => setThRef(columnId, el)"
                :class="['th', cellAlignClasses.get(columnId)]"
                :style="[
                  cellSizeStyles.get(columnId),
                  {
                    cursor: columnConfigs.get(columnId)!.reorderable ? 'grab' : 'inherit',
                    anchorName: `--header-${uniqueId}-${idx}`,
                  },
                ]"
                :draggable="!isResizeActive && columnConfigs.get(columnId)!.reorderable"
                role="columnheader"
                :aria-colindex="userColumnAriaIndex(idx)"
                :aria-sort="headerAriaSort(columnId)"
                :tabindex="columnConfigs.get(columnId)!.sortable ? 0 : undefined"
                @click="
                  (e) => {
                    if (!columnConfigs.get(columnId)!.sortable) {
                      return;
                    }
                    onSort(columnId, e.ctrlKey || e.metaKey);
                  }
                "
                @keydown="(e) => onHeaderSortKeydown(e, columnId)"
                @dragstart="(e) => onColDragStart(e, columnId)"
                @dragend="onColDragEnd"
              >
                <component
                  :is="columnConfigs.get(columnId)!.headerCell"
                  v-if="columnConfigs.get(columnId)!.headerCell"
                ></component>
                <span v-else class="fallback-cell">
                  {{
                    columnConfigs.get(columnId)!.displayName ??
                    columnConfigs.get(columnId)!.columnId
                  }}
                </span>
                <!-- Funnel sits BEFORE the sort indicator: toggling the sort must
                 never shift the filter button (the indicator appears after it). -->
                <NxTableHeaderFilter
                  v-if="columnConfigs.get(columnId)!.filterable"
                  v-bind="filterColumnMeta(columnId)"
                  :value-source="filterValueSourceFor(columnId)"
                  :disabled="lockedColumns.has(columnId)"
                  :filter="columnFilters.get(columnId)"
                  :quick-values="filterInfo.get(columnId)"
                  @apply="(filter) => applyHeaderFilter(columnId, filter)"
                />
                <div
                  v-if="columnConfigs.get(columnId)!.sortable && sortOrderIndices.has(columnId)"
                  :class="['sort-indicator', sortInfo.get(columnId)]"
                >
                  {{ sortOrderIndices.get(columnId)! }}
                  <CaretUpIcon v-if="sortInfo.get(columnId) === 'desc'" filled />
                  <CaretDownIcon v-else filled />
                </div>
                <div
                  v-if="columnConfigs.get(columnId)!.resizeable"
                  class="resize-handle"
                  @click.stop.prevent
                  @mousedown="(e) => onResizeStart(e, columnId)"
                >
                  <div class="resize-handle-pill"></div>
                </div>
              </div>
              <div
                v-if="hasLastCol"
                :ref="(el) => (lastColRef = el as HTMLElement)"
                class="th last-col"
                role="columnheader"
                :aria-colindex="lastColumnAriaIndex"
              >
                <button
                  v-if="!!props.onCreate && !props.hideCreateButton"
                  :ref="(el) => (createButtonRef = el as HTMLButtonElement)"
                  class="button"
                  :class="{ 'is-covered': savedVisible }"
                  :aria-label="i18nInjection.t('common.actions.create')"
                  @click="
                    () => {
                      isCreationActive = true;
                      clearCursor();
                    }
                  "
                >
                  <AddSquareIcon :filled="true" />
                </button>
                <span
                  class="saved-check"
                  :class="{
                    'is-visible': savedVisible,
                    'over-create': !!props.onCreate && !props.hideCreateButton,
                  }"
                  data-testid="table-saved-check"
                  aria-hidden="true"
                >
                  <CheckmarkCircleIcon :filled="true" />
                </span>
                <span class="saved-announcement" role="status">
                  {{ savedVisible ? i18nInjection.t('common.table.saved') : '' }}
                </span>
              </div>

              <!-- Drop zones (overlays) for columns that are in between two columns (center to center) -->
              <template v-if="draggedColumn">
                <template
                  v-for="(columnId, idx) in columnDropZoneIds"
                  :key="`drop-zone-header-${idx}`"
                >
                  <!-- "columnId !== draggedColumn" since chrome will end the drag event if you render anything on top of it -->
                  <div
                    v-if="columnConfigs.get(columnId)!.reorderable && columnId !== draggedColumn"
                    class="col-drop-zone"
                    :style="{
                      left: `anchor(--header-${uniqueId}-${idx - 1} center, 0)`,
                      right: `anchor(--header-${uniqueId}-${idx} center, 0)`,
                    }"
                    @dragover="
                      (e) => {
                        if (columnConfigs.get(columnId)!.reorderable) {
                          e.preventDefault();
                        }
                      }
                    "
                    @dragenter.prevent.stop="() => onColDragEnter(columnId)"
                    @drop.prevent.stop="onColDrop"
                  ></div>
                </template>
              </template>

              <!-- Bridge drop-zone: covers the dragged column's source slot.
               Anchored to the columns adjacent to the dragged one (skipping
               over it, since its anchor follows the transform-translated
               preview). Only rendered after first dragenter (Chrome would
               otherwise cancel the drag). On enter, the preview-target is
               set to the dragged column itself — the watchEffect short-
               circuits on `dragged === target` and clears all offsets, so
               the dragged element visually snaps back to its source slot.
               Drop is a no-op. -->
              <div
                v-if="columnBridgeZone"
                class="col-drop-zone"
                :style="{
                  left:
                    columnBridgeZone.leftAnchorIdx === null
                      ? '0'
                      : `anchor(--header-${uniqueId}-${columnBridgeZone.leftAnchorIdx} center, 0)`,
                  right:
                    columnBridgeZone.rightAnchorIdx === null
                      ? '0'
                      : `anchor(--header-${uniqueId}-${columnBridgeZone.rightAnchorIdx} center, 0)`,
                }"
                @dragover.prevent
                @dragenter.prevent.stop="() => draggedColumn && onColDragEnter(draggedColumn)"
                @drop.prevent.stop
              ></div>
            </div>
            <div
              v-if="hasFilters && showQuickFilterRow"
              :class="['tr', 'filter-row']"
              role="row"
              aria-rowindex="2"
              :style="{
                minHeight: props.headerRowHeight,
                maxHeight: props.headerRowHeight,
              }"
            >
              <div class="th first-col" role="gridcell" aria-colindex="1">
                <button
                  v-if="hasFilters"
                  :ref="(el) => bindFilterRowIndicator(el as HTMLButtonElement | null)"
                  class="button"
                  tabindex="-1"
                  :aria-label="i18nInjection.t('common.table.filter.reset_all')"
                  :title="i18nInjection.t('common.table.filter.reset_all')"
                  @click="resetAllFilters"
                  @keydown="(e) => onVirtualColumnKeyDown(FILTER_ROW_ID, INDICATOR_COLUMN_ID, e)"
                >
                  <FilterDismissIcon filled />
                </button>
              </div>
              <div
                v-if="checkboxSelection"
                class="th select-col"
                role="gridcell"
                aria-colindex="2"
              ></div>
              <div
                v-for="columnId in userVisibleColumnOrder"
                :id="`filter-${columnId}`"
                :ref="(el) => setThFilterRef(columnId, el)"
                :key="`filter-${columnId}`"
                class="th"
                :class="cellAlignClasses.get(columnId)"
                :style="cellSizeStyles.get(columnId)"
                role="gridcell"
                :aria-colindex="userColumnAriaIndex(userVisibleColumnOrder.indexOf(columnId))"
                @click="
                  () => {
                    columnConfigs.get(columnId)!.filterable && clearCursor();
                  }
                "
              >
                <!-- Filter controls always use normal form-field geometry:
                     lookup/actions on the right in every column. Body cells
                     may mirror actions for right-aligned numeric content. -->
                <NxInputActionsAlignProvider align="end">
                  <component
                    :is="columnConfigs.get(columnId)!.filterCell"
                    v-if="
                      columnConfigs.get(columnId)!.filterable &&
                      columnConfigs.get(columnId)!.filterCell
                    "
                    v-bind="{
                      setFilterValue: (value: ValuesInMap<TableState<T>['filterValues']>) =>
                        onFilter(columnId, value),
                      disabled: lockedColumns.has(columnId),
                      registerFocus: getRegisterFocus(FILTER_ROW_ID, columnId),
                      focusAbove: getCellNav(FILTER_ROW_ID, columnId).focusAbove,
                      focusBelow: getCellNav(FILTER_ROW_ID, columnId).focusBelow,
                      focusLeft: getCellNav(FILTER_ROW_ID, columnId).focusLeft,
                      focusRight: getCellNav(FILTER_ROW_ID, columnId).focusRight,
                    }"
                  ></component>
                </NxInputActionsAlignProvider>
              </div>
              <div
                v-if="hasLastCol"
                class="th last-col"
                role="gridcell"
                :aria-colindex="lastColumnAriaIndex"
              >
                <button
                  :ref="(el) => bindFilterRowAction(el as HTMLButtonElement | null)"
                  class="button"
                  tabindex="-1"
                  :aria-label="filterModifierLabel"
                  :title="filterModifierLabel"
                  @click="
                    () => {
                      if (filterModifier === 'and') {
                        filterModifier = 'or';
                      } else {
                        filterModifier = 'and';
                      }
                      debouncedOnChange({
                        limit: paginationLimitInternal,
                        offset: 0,
                      });
                    }
                  "
                  @keydown="(e) => onVirtualColumnKeyDown(FILTER_ROW_ID, ROW_ACTION_COLUMN_ID, e)"
                >
                  <ShapeIntersectIcon v-if="filterModifier === 'and'" :filled="true" />
                  <ShapeUnionIcon v-else :filled="true" />
                </button>
              </div>
            </div>
          </div>
          <div v-show="!showEmptyState || !props.summary" class="tbody" role="rowgroup">
            <div
              v-if="isCreationActive"
              class="tr"
              role="row"
              :aria-rowindex="createRowAriaIndex"
              :style="rowBoxStyle()"
            >
              <div class="td first-col" role="gridcell" aria-colindex="1"></div>
              <div
                v-if="checkboxSelection"
                class="td select-col"
                role="gridcell"
                aria-colindex="2"
              ></div>
              <div
                v-for="columnId in userVisibleColumnOrder"
                :id="`td-create-${columnId}`"
                :key="`td-create-${columnId}`"
                class="td"
                :class="cellAlignClasses.get(columnId)"
                :data-nx-actions-align="
                  cellAlignClasses.get(columnId) === 'align-end' ? 'start' : undefined
                "
                :style="cellSizeStyles.get(columnId)"
                role="gridcell"
                :aria-colindex="userColumnAriaIndex(userVisibleColumnOrder.indexOf(columnId))"
              >
                <NxInputActionsAlignProvider
                  :align="cellAlignClasses.get(columnId) === 'align-end' ? 'start' : 'end'"
                >
                  <component
                    :is="columnConfigs.get(columnId)!.createCell"
                    v-bind="{
                      partialRowData: creationData,
                      setPartialRowData: (partialRowData: PartialOrNull<T>) =>
                        (creationData = { ...creationData, ...partialRowData }),
                    }"
                  ></component>
                </NxInputActionsAlignProvider>
              </div>
              <div
                v-if="hasLastCol"
                class="td last-col"
                role="gridcell"
                :aria-colindex="lastColumnAriaIndex"
              >
                <button
                  class="button"
                  :aria-label="i18nInjection.t('common.actions.save')"
                  @click="
                    async () => {
                      // Refused by the server: the consumer reports the reason
                      // (toast / banner). Keep the creation row open with what
                      // was typed so the user can correct it — clearing it here
                      // would throw the input away along with the message.
                      if (!(await createNewRow(creationData))) return;
                      creationData = {};
                      isCreationActive = false;
                    }
                  "
                >
                  <SaveIcon :filled="true" />
                </button>
                <button
                  class="button"
                  :aria-label="i18nInjection.t('common.actions.cancel')"
                  @click="
                    () => {
                      isCreationActive = false;
                      creationData = {};
                    }
                  "
                >
                  <DismissIcon :filled="true" />
                </button>
              </div>
            </div>

            <!-- Zero-height positioning context for the pagination handle. -->
            <div class="pagination-handle-anchor">
              <div
                v-if="!isPagesMode && props.data.length > 0 && estimate > 0"
                :ref="setPaginationHandleRef"
                :style="{
                  position: 'absolute',
                  top: `${offsetOf(Math.max(0, props.data.length - props.paginationTriggerRemainingItemsCount))}px`,
                  left: '0',
                  right: '0',
                  height: `${estimate}px`,
                }"
                class="pagination-handle"
              ></div>
            </div>

            <!-- Virtual list: top spacer. Reserves layout height for the rows
             above the rendered window so the scroll position is accurate. -->
            <div
              v-if="virtualWindow.topPad > 0"
              class="virtual-spacer"
              :style="{ height: virtualWindow.topPad + 'px' }"
              aria-hidden="true"
            ></div>

            <template
              v-for="{ row, rowIdx, rowId, isPlaceholder } in bodyRows"
              :key="`row-${rowId}`"
            >
              <!-- Group headers (groupBy) ride in the virtual slot of the row below. -->
              <NxTableGroupRow
                v-for="header in groupHeadersAt(rowIdx)"
                :key="`group-${header.key}`"
                :ref="(inst) => measureGroupHeader(header.key, inst)"
                :group="header.group"
                :count="header.count"
                :rows="header.rows"
                :ungrouped="header.ungrouped"
                :grid-row-index="groupHeaderAriaIndex(header)"
                :col-count="gridAriaColCount"
                :count-label="props.groupCountLabel(header.count)"
              >
                <template v-if="$slots['group-header']" #default="groupSlotProps">
                  <slot name="group-header" v-bind="groupSlotProps"></slot>
                </template>
              </NxTableGroupRow>
              <div
                :ref="(el) => measureRow(rowId, el as HTMLElement | null)"
                v-row-save-state="rowId"
                :class="[
                  'tr',
                  props.rowClass?.(row),
                  {
                    cursor: isCursor(rowId),
                    selected: rowSelectionEnabled && isSelected(rowId),
                    stripe: rowStripes(rowIdx),
                    'tr-placeholder': isPlaceholder,
                    'is-drop-target': !isPlaceholder && nativeRowDrop.isActive(row),
                  },
                ]"
                role="row"
                :draggable="!!props.onRowDragStart"
                :aria-rowindex="bodyRowAriaIndex(rowIdx)"
                :aria-hidden="isPlaceholder ? 'true' : undefined"
                :aria-selected="rowSelectionEnabled ? isSelected(rowId) : undefined"
                :aria-description="
                  nativeRowDrop.isActive(row) ? (props.rowDropLabel?.(row) ?? rowId) : undefined
                "
                :data-dragged="isRowInDragUnit(rowIdx)"
                :data-row-id="rowId"
                :data-row-selected="rowSelectionEnabled && isSelected(rowId)"
                :data-row-active="isCursor(rowId)"
                :style="[
                  rowTransformStyles.get(rowId),
                  rowBoxStyle(isPlaceholder),
                  rowStyleVars(row),
                ]"
                @click="
                  (e: MouseEvent) => {
                    if (!props.editMode && !listSelection) return;
                    if (isPlaceholder) return;
                    onRowClick(rowIdx, e);
                  }
                "
                @mousedown="(e: MouseEvent) => onRowMouseDown(e, row)"
                @contextmenu="(e: MouseEvent) => onRowContextMenu(e, rowIdx, row)"
                @dragstart="(e: DragEvent) => onConsumerRowDragStart(e, rowIdx)"
                @dragend="onConsumerRowDragEnd"
                @dragenter="nativeRowDrop.dragenter($event, row)"
                @dragover="nativeRowDrop.dragover($event, row)"
                @dragleave="nativeRowDrop.dragleave"
                @drop="nativeRowDrop.drop($event, row)"
              >
                <!-- Windowed/sparse shell: one light bar, no per-column edit widgets. -->
                <template v-if="isPlaceholder">
                  <div
                    class="td first-col"
                    role="gridcell"
                    aria-colindex="1"
                    :style="{ anchorName: `--row-${uniqueId}-${rowIdx}` }"
                  ></div>
                  <div v-if="checkboxSelection" class="td select-col" role="gridcell"></div>
                  <div
                    class="td td-placeholder-fill"
                    role="gridcell"
                    aria-hidden="true"
                    :style="{
                      width: userColumnsWidth,
                      flexGrow: columnGrowth.size ? 1 : undefined,
                    }"
                  >
                    <span class="placeholder-shimmer"></span>
                  </div>
                  <div v-if="hasLastCol" class="td last-col" role="gridcell"></div>
                </template>
                <template v-else>
                  <div
                    class="td first-col"
                    role="gridcell"
                    aria-colindex="1"
                    :style="{
                      cursor:
                        (props.onRowMoved && isRowMovableAt(rowIdx)) || props.onRowDragStart
                          ? 'grab'
                          : 'inherit',
                      anchorName: `--row-${uniqueId}-${rowIdx}`,
                    }"
                    :draggable="
                      !!props.onRowMoved && !props.onRowDragStart && isRowMovableAt(rowIdx)
                    "
                    @dragstart="(e) => onReorderDragStart(e, rowIdx)"
                    @dragend="onRowDragEnd"
                  >
                    <div
                      :ref="(el) => bindIndicatorAnchor(rowId, el as HTMLElement | null)"
                      :tabindex="
                        rowSelectionEnabled && isCursor(rowId, INDICATOR_COLUMN_ID) ? 0 : -1
                      "
                      class="indicator-anchor"
                      :role="rowSelectionEnabled && !checkboxSelection ? 'checkbox' : undefined"
                      :aria-checked="
                        rowSelectionEnabled && !checkboxSelection ? isSelected(rowId) : undefined
                      "
                      :aria-label="
                        rowSelectionEnabled && !checkboxSelection
                          ? rowSelectorAriaLabel(rowIdx, isSelected(rowId))
                          : undefined
                      "
                      data-no-row-select
                      @keydown="(e) => onVirtualColumnKeyDown(rowId, INDICATOR_COLUMN_ID, e)"
                      @focusin="
                        () => {
                          if (cursor === null) {
                            setCursor(rowId, INDICATOR_COLUMN_ID);
                          }
                        }
                      "
                    ></div>
                    <!-- expandableRows: the toggle takes the gutter's place of the cursor
                         caret; the active row still shows by its fill and edge marker. -->
                    <button
                      v-if="rowExpansionEnabled && isRowExpandable(row)"
                      :ref="(el) => bindExpandToggle(rowId, el as HTMLButtonElement | null)"
                      type="button"
                      class="expand-toggle"
                      tabindex="-1"
                      draggable="false"
                      :aria-expanded="isRowExpanded(rowId)"
                      :aria-controls="
                        isRowExpanded(rowId) ? `detail-${uniqueId}-${rowId}` : undefined
                      "
                      :aria-label="expandToggleAriaLabel(rowIdx, isRowExpanded(rowId))"
                      data-no-row-select
                      @click.stop="toggleRowExpanded(rowId)"
                      @keydown="(e) => onVirtualColumnKeyDown(rowId, EXPAND_COLUMN_ID, e)"
                    >
                      <ChevronRightIcon />
                    </button>
                    <span v-else-if="isCursor(rowId)" class="icon-stack row-caret">
                      <CaretRightIcon
                        v-if="cursor?.columnId === INDICATOR_COLUMN_ID"
                        filled
                        class="outer"
                      />
                      <CaretRightIcon
                        v-if="cursor?.columnId === INDICATOR_COLUMN_ID"
                        filled
                        class="gap"
                      />
                      <CaretRightIcon filled class="front" />
                    </span>
                  </div>
                  <div
                    v-if="checkboxSelection"
                    :ref="
                      (el) => setRenderedBodyCell(rowId, SELECT_COLUMN_ID, el as Element | null)
                    "
                    class="td select-col"
                    role="gridcell"
                    aria-colindex="2"
                    :data-row-id="rowId"
                    :data-column-id="SELECT_COLUMN_ID"
                    @mousedown="onSelectCellMouseDown"
                    @click.stop="(e: MouseEvent) => onRowCheckboxClick(rowIdx, e)"
                  >
                    <!-- The box sits in a filler: the cell's direct child stretches to the
                     cell width (see `.td > *`), the box itself must not. -->
                    <div class="select-box">
                      <NxCheckbox
                        :ref="(inst) => bindRowCheckbox(rowId, inst)"
                        :model-value="isSelected(rowId)"
                        :disabled="!isRowSelectable(row)"
                        :aria-label="rowCheckboxAriaLabel(row, rowIdx)"
                        @update:model-value="keepCheckboxControlled"
                        @keydown="
                          (e: KeyboardEvent) => onVirtualColumnKeyDown(rowId, SELECT_COLUMN_ID, e)
                        "
                      />
                    </div>
                  </div>
                  <div
                    v-for="(columnId, colIdx) in userVisibleColumnOrder"
                    :id="`td-${columnId}-${rowId}`"
                    :key="`td-${columnId}-${rowId}`"
                    :ref="
                      (ref) => {
                        ref ? tdRefs.set(columnId, ref as HTMLElement) : tdRefs.delete(columnId);
                        setRenderedBodyCell(rowId, columnId, ref as Element | null);
                      }
                    "
                    v-cell-save-state="[rowId, columnId]"
                    :data-dragged="columnId === draggedColumn"
                    :data-row-id="rowId"
                    :data-column-id="columnId"
                    role="gridcell"
                    :aria-colindex="userColumnAriaIndex(colIdx)"
                    :aria-readonly="
                      resolveCellMode(
                        columnConfigs.get(columnId)!,
                        props.editMode,
                        !!props.onEdit,
                        row
                      ) !== 'edit'
                    "
                    :class="[
                      'td',
                      { 'body-edit-cell': props.editMode },
                      cellLayoutClasses.get(columnId),
                      cellAlignClasses.get(columnId),
                      {
                        cursor: isCursor(rowId, columnId),
                        passive: !isKeyboardActive,
                      },
                    ]"
                    :data-nx-actions-align="
                      cellAlignClasses.get(columnId) === 'align-end' ? 'start' : undefined
                    "
                    :style="cellSizeStyles.get(columnId)"
                    @click.stop="
                      (e: MouseEvent) => {
                        navigationOnCellClickProxy(
                          {
                            rowId: rowId,
                            rowIdx,
                            columnId,
                            columnIdx: colIdx,
                          },
                          e
                        );
                      }
                    "
                  >
                    <!-- One NxLoadingWrapper per edit cell, so a save paints the cell it
                 belongs to instead of the whole grid. It adds no box of its own
                 (`display: contents`); save state is an overlay and reserves no idle
                 input width. Without `cellSaveStatus` nothing wraps.
                 Same `edit-inset` padding as the display `cell-anchor` so
                 lazyEditCells (cursor → edit, others → display) don't jump
                 vertical/horizontal inset when a row becomes active. An
                 `eagerEditCell` column keeps its edit cell in every row of a lazy
                 grid (`eager-edit` outside the cursor row). -->
                    <div
                      v-if="
                        resolveCellMode(
                          columnConfigs.get(columnId)!,
                          props.editMode,
                          !!props.onEdit,
                          row
                        ) === 'edit' &&
                        (!props.lazyEditCells ||
                          isCursor(rowId) ||
                          columnConfigs.get(columnId)!.eagerEditCell)
                      "
                      :class="[
                        'cell-edit',
                        cellLayoutClasses.get(columnId),
                        {
                          'edit-inset': props.editMode,
                          'eager-edit': props.lazyEditCells && !isCursor(rowId),
                        },
                      ]"
                      :inert="
                        !isCursor(rowId, columnId) &&
                        !columnConfigs.get(columnId)!.inactivePointerInteractive
                      "
                    >
                      <NxInputActionsAlignProvider
                        :align="cellAlignClasses.get(columnId) === 'align-end' ? 'start' : 'end'"
                        plain-cell
                      >
                        <NxConditionalWrapper
                          :wrap="!!props.cellSaveStatus"
                          :wrapper="NxLoadingWrapper"
                          :wrapper-props="cellSaveStatusProps(rowId, columnId)"
                        >
                          <component
                            :is="columnConfigs.get(columnId)!.editCell"
                            v-bind="{
                              rowData: row,
                              rowIndex: dataIndexOf(rowIdx),
                              onEdit: async (newValues: Partial<T>) => {
                                await props.onEdit!(
                                  {
                                    id: rowId,
                                    index: dataIndexOf(rowIdx),
                                  },
                                  newValues
                                );
                              },
                              registerFocus: getRegisterFocus(rowId, columnId),
                              focusAbove: getCellNav(rowId, columnId).focusAbove,
                              focusBelow: getCellNav(rowId, columnId).focusBelow,
                              focusLeft: getCellNav(rowId, columnId).focusLeft,
                              focusRight: getCellNav(rowId, columnId).focusRight,
                            }"
                          ></component>
                        </NxConditionalWrapper>
                      </NxInputActionsAlignProvider>
                    </div>
                    <component
                      :is="rowLinkOf(row, columnId) ? 'a' : 'div'"
                      v-else
                      :ref="
                        (el: unknown) =>
                          bindUserCellAnchor(rowId, columnId, el as HTMLElement | null)
                      "
                      tabindex="-1"
                      :class="[
                        'cell-anchor',
                        cellLayoutClasses.get(columnId),
                        { 'edit-inset': props.editMode, 'row-link': !!rowLinkOf(row, columnId) },
                      ]"
                      v-bind="rowLinkAttrs(row, columnId)"
                      @keydown="(e: KeyboardEvent) => onVirtualColumnKeyDown(rowId, columnId, e)"
                      @click="(e: MouseEvent) => onRowLinkClick(e, rowId, columnId, row)"
                      @mousedown="onCellMiddleDown"
                      @auxclick="(e: MouseEvent) => onCellAuxClick(e, rowId, columnId, row)"
                    >
                      <template
                        v-if="
                          resolveCellMode(
                            columnConfigs.get(columnId)!,
                            props.editMode,
                            !!props.onEdit,
                            row
                          ) !== 'blank'
                        "
                      >
                        <component
                          :is="columnConfigs.get(columnId)!.cell"
                          v-if="columnConfigs.get(columnId)!.cell"
                          v-bind="{ rowData: row, rowIndex: dataIndexOf(rowIdx) }"
                        ></component>
                        <span v-else class="fallback-cell">
                          {{ columnConfigs.get(columnId)!.toCsvString(row, dataIndexOf(rowIdx)) }}
                        </span>
                      </template>
                    </component>
                  </div>
                  <div
                    v-if="hasLastCol"
                    class="td last-col"
                    :data-row-id="rowId"
                    role="gridcell"
                    :aria-colindex="lastColumnAriaIndex"
                  >
                    <button
                      v-if="!!props.onDelete && isRowDeletableAt(rowIdx)"
                      :ref="(el) => bindRowActionButton(rowId, el as HTMLButtonElement | null)"
                      class="button"
                      tabindex="-1"
                      :aria-label="`${i18nInjection.t('common.actions.delete')} ${rowIdx + 1}`"
                      @click.stop="requestRowDeletion(rowIdx)"
                      @keydown="(e) => onVirtualColumnKeyDown(rowId, ROW_ACTION_COLUMN_ID, e)"
                    >
                      <DismissIcon :filled="true"></DismissIcon>
                    </button>
                  </div>
                </template>
              </div>
              <!-- An open row's detail (expandableRows): its own grid row right below,
                   in the row's virtual slot; the cell spans the visible table. -->
              <div
                v-if="rowExpansionEnabled && !isPlaceholder && isRowExpanded(rowId)"
                :id="`detail-${uniqueId}-${rowId}`"
                :ref="(el) => measureDetailRow(rowId, el as HTMLElement | null)"
                class="tr detail-row"
                role="row"
                :aria-rowindex="bodyRowAriaIndex(rowIdx) + 1"
                :data-detail-of="rowId"
              >
                <div
                  class="detail-cell"
                  role="gridcell"
                  aria-colindex="1"
                  :aria-colspan="gridAriaColCount"
                  :style="tableViewWidth ? { width: `${tableViewWidth}px` } : undefined"
                >
                  <slot
                    name="row-detail"
                    v-bind="{
                      row,
                      rowId,
                      rowIndex: dataIndexOf(rowIdx),
                      collapse: () => setRowExpanded(rowId, false),
                    }"
                  ></slot>
                </div>
              </div>
            </template>

            <!-- Virtual list: bottom spacer. Reserves layout height for the rows
             below the rendered window. -->
            <div
              v-if="virtualWindow.bottomPad > 0"
              class="virtual-spacer"
              :style="{ height: virtualWindow.bottomPad + 'px' }"
              aria-hidden="true"
            ></div>

            <!-- Headers of empty groups behind the last row (showEmptyGroups). -->
            <NxTableGroupRow
              v-for="header in groupTrailingHeaders"
              :key="`group-trailing-${header.key}`"
              :group="header.group"
              :count="header.count"
              :rows="header.rows"
              :ungrouped="header.ungrouped"
              :grid-row-index="groupHeaderAriaIndex(header)"
              :col-count="gridAriaColCount"
              :count-label="props.groupCountLabel(header.count)"
            >
              <template v-if="$slots['group-header']" #default="groupSlotProps">
                <slot name="group-header" v-bind="groupSlotProps"></slot>
              </template>
            </NxTableGroupRow>

            <!-- Drop zones (overlays) for rows that are in between two rows (center to center).
             Targets are pre-clamped by useRowDrag: with rowParentId set, zones over
             invalid areas snap to the nearest legal insertion point. Zones across the
             dragged unit are skipped (Chrome would end the drag if anything rendered on
             top of it); the bridge zone below covers that span. -->
            <template v-if="!!props.onRowMoved && !props.onRowDragStart && !waitForRowMove">
              <div
                v-for="zone in rowDropZones"
                :key="`drop-zone-row-${zone.bottomAnchorIdx}`"
                class="row-drop-zone"
                :style="{
                  top: `anchor(--row-${uniqueId}-${zone.topAnchorIdx} center, 0)`,
                  bottom: `anchor(--row-${uniqueId}-${zone.bottomAnchorIdx} center, 0)`,
                }"
                @dragover.prevent
                @dragenter.prevent.stop="() => onRowDragEnter(zone.target)"
                @drop.prevent.stop="onRowDrop"
              />
            </template>

            <!-- Bridge drop-zone: covers the dragged unit's source slot (the whole
             parent/child block when rowParentId is set). Anchored to the rows
             immediately above and below the unit (skipping over its translated
             preview position). Only rendered after first dragenter (Chrome
             would otherwise cancel the drag). On enter, the preview-target is
             set back to the unit start — the watchEffect short-circuits on
             `unit.start === targetIdx` and clears all offsets, so the dragged
             rows visually snap back to their source slot. Drop is a no-op. -->
            <div
              v-if="rowBridgeZone && !!props.onRowMoved && !props.onRowDragStart && !waitForRowMove"
              class="row-drop-zone"
              :style="{
                top:
                  rowBridgeZone.topAnchorIdx === null
                    ? '0'
                    : `anchor(--row-${uniqueId}-${rowBridgeZone.topAnchorIdx} center, 0)`,
                bottom:
                  rowBridgeZone.bottomAnchorIdx === null
                    ? '0'
                    : `anchor(--row-${uniqueId}-${rowBridgeZone.bottomAnchorIdx} center, 0)`,
              }"
              @dragover.prevent
              @dragenter.prevent.stop="onRowBridgeEnter"
              @drop.prevent.stop
            ></div>

            <div
              v-if="props.data.length === 0 && (!props.summary || !showEmptyState)"
              class="tr last-empty"
              :style="rowBoxStyle()"
            >
              <div
                class="td first-col"
                :style="{ anchorName: `--row-${uniqueId}-${props.data.length}` }"
              ></div>
              <div v-if="checkboxSelection" class="td select-col"></div>
              <div
                v-for="columnId in userVisibleColumnOrder"
                :key="`td-last-empty-${columnId}`"
                class="td"
                :data-dragged="columnId === draggedColumn"
                :style="cellSizeStyles.get(columnId)"
              ></div>
              <div v-if="hasLastCol" class="td last-col"></div>
            </div>
          </div>
          <div v-if="showEmptyState && props.summary" class="table-empty summary-empty">
            <component :is="renderEmptyBody" />
          </div>
          <div
            v-if="props.summary"
            :ref="(element) => (summaryRowRef = element as HTMLElement)"
            class="tr summary-row"
            role="row"
            :aria-rowindex="gridAriaRowCount"
            :aria-label="summaryLabelText"
            :aria-busy="props.summaryLoading || isLoading ? 'true' : undefined"
            :style="rowBoxStyle()"
          >
            <div
              class="td first-col summary-gutter"
              role="gridcell"
              aria-colindex="1"
              :aria-label="summaryLabelText"
            >
              <NxTooltip v-if="summaryLabelCollapsed" :text="summaryLabelText">
                <span class="summary-sigma" aria-hidden="true">Σ</span>
              </NxTooltip>
              <span
                :ref="setSummaryLabelRef"
                :class="['summary-label', { collapsed: summaryLabelCollapsed }]"
                aria-hidden="true"
              >
                {{ summaryLabelText }}
              </span>
            </div>
            <div
              v-if="checkboxSelection"
              class="td select-col"
              role="gridcell"
              aria-colindex="2"
            ></div>
            <div
              v-for="(columnId, idx) in userVisibleColumnOrder"
              :key="`summary-${columnId}`"
              :data-column-id="columnId"
              :data-summary-state="summaryCells.get(columnId)?.state"
              :class="[
                'td',
                cellLayoutClasses.get(columnId),
                cellAlignClasses.get(columnId),
                {
                  'summary-positive': summaryCells.get(columnId)?.sign === 'positive',
                  'summary-negative': summaryCells.get(columnId)?.sign === 'negative',
                },
              ]"
              :style="cellSizeStyles.get(columnId)"
              role="gridcell"
              :aria-colindex="userColumnAriaIndex(idx)"
              aria-readonly="true"
              :title="summaryExplanation(columnId)"
              :aria-label="summaryExplanation(columnId)"
            >
              <component
                :is="columnConfigs.get(columnId)!.summaryCell"
                v-if="
                  columnConfigs.get(columnId)!.summaryCell &&
                  summaryCells.get(columnId)?.state !== 'none'
                "
                v-bind="summaryCells.get(columnId)!"
              ></component>
              <span v-else class="fallback-cell">{{ summaryCells.get(columnId)?.text }}</span>
            </div>
            <div
              v-if="hasLastCol"
              class="td last-col"
              role="gridcell"
              :aria-colindex="lastColumnAriaIndex"
            ></div>
          </div>
          <div :ref="(el) => (detectorRef = el as HTMLElement)" class="scrollbar-detector"></div>
        </div>
        <!-- Shared empty state: fills the body area under the (still visible)
         header, identical on every table. #empty overrides the content. -->
        <div v-if="showEmptyState && !props.summary" class="table-empty">
          <component :is="renderEmptyBody" />
        </div>
        <div :class="['loading', { hidden: !isLoading }]">
          <div></div>
        </div>
        <span v-if="canDropRow" class="native-drop-status" role="status" aria-live="polite">
          {{ nativeRowDrop.label.value }}
        </span>
        <NxTablePager
          v-if="isPagesMode"
          :page="pagerPage"
          :page-size="paginationLimitInternal"
          :page-row-count="props.data.length"
          :total="props.totalCount"
          :page-sizes="props.paginationPageSizes"
          :disabled="isLoading"
          @change-page="goToPage"
          @change-page-size="changePageSize"
        />
      </div>
    </div>
  </Teleport>
  <NxTableColumnDialog
    v-model:open="isColumnDialogOpen"
    :initial-visibility="
      columnOrder.map((columnId) => {
        return {
          displayName: columnConfigs.get(columnId)!.displayName ?? columnId,
          visible: columnVisible.get(columnId)!,
          sticky: isColumnSticky(columnId),
        };
      })
    "
    :show-sticky="!!props.stickyColumnIds"
    @submit="applyColumnVisibility"
  />
  <NxTablePreferencesDialog
    v-model:open="isPreferencesOpen"
    v-model:scope="preferencesScope"
    :search-row-visible="showQuickFilterRow"
    :always-show-scrollbars="alwaysShowScrollbars"
    :zebra-stripes="chrome.zebraStripes"
    :zebra-fixed="props.zebra !== undefined"
    :horizontal-lines="horizontalLines"
    :vertical-lines="verticalLines"
    :row-density="rowDensity"
    :header-brand="headerBrand"
    :cells-top="cellsTop"
    :has-filter-row="hasFilters"
    :locked="lockedTableFields"
    :scoped="!!props.settingsKey"
    :own-count="ownTableSettingsCount"
    :tables-with-own="tablesWithOwn"
    @update:search-row-visible="onSearchRowVisibleUpdate"
    @update:always-show-scrollbars="onAlwaysShowScrollbarsUpdate"
    @update:zebra-stripes="onZebraStripesUpdate"
    @update:horizontal-lines="onHorizontalLinesUpdate"
    @update:vertical-lines="onVerticalLinesUpdate"
    @update:row-density="onRowDensityUpdate"
    @update:header-brand="onHeaderBrandUpdate"
    @update:cells-top="onCellsTopUpdate"
    @reset="onTablePreferencesReset"
    @apply-everywhere="onTablePreferencesApplyEverywhere"
  />
  <NxTableFilterManageDialog
    v-model:open="isFilterManageOpen"
    :has-active-filters="hasActiveFilters"
    :saved-filters="savedFilters"
    @open-wizard="
      () => {
        isFilterManageOpen = false;
        isFilterWizardOpen = true;
      }
    "
    @save-current="
      () => {
        isFilterManageOpen = false;
        isFilterSaveDialogOpen = true;
      }
    "
    @reset-filters="resetAllFilters"
    @apply-saved="applySavedFilter"
    @delete-saved="deleteFilter"
  />
  <NxTableFilterWizard
    v-model:open="isFilterWizardOpen"
    :columns="filterableColumnMeta"
    :initial="advancedFilter"
    @apply="applyAdvancedFilter"
    @save="onWizardSaveRequest"
  />
  <NxTableFilterSaveDialog
    v-model:open="isFilterSaveDialogOpen"
    :existing="savedFilters"
    @save="saveNamedFilter"
  />
  <NxTableCsvExportDialog
    v-model:open="isCsvExportDialogOpen"
    :initial-visibility="csvColumnVisibility"
    @export-click="exportCsv"
  />
  <NxConfirmDeleteDialog
    v-model:open="isDeleteDialogOpen"
    :selection-count="
      rowSelectionEnabled && pendingDelete?.inSelection ? deletableSelectedIds.length : 0
    "
    :text-replacements="{}"
    @confirmed="onDeleteDialogSubmit"
  />
  <div
    v-if="rowContextOpen"
    class="row-context-host"
    :style="{ left: `${rowContextX}px`, top: `${rowContextY}px` }"
  >
    <NxMenuDropdown :is-open="true" align="start" :fit-width="true" @close="closeRowContext">
      <template #trigger>
        <span class="row-context-anchor" aria-hidden="true" />
      </template>
      <template v-if="rowContextRow">
        <NxMenuItem @select="onOpenMenuSelect('here')">
          {{ i18nInjection.t('common.table.row_menu.open') }}
        </NxMenuItem>
        <template v-if="rowOpener">
          <NxMenuItem @select="onOpenMenuSelect('tab')">
            {{ i18nInjection.t('common.table.row_menu.open_tab') }}
          </NxMenuItem>
          <template v-if="rowOpenBeside !== false">
            <NxContextMenuSeparator v-if="rowOpenTiles?.length" />
            <NxMenuItem
              v-for="tile in rowOpenTiles ?? []"
              :key="tile.id"
              @select="onOpenMenuSelect({ tileId: tile.id })"
            >
              {{ i18nInjection.t('common.table.row_menu.open_in', { name: tile.label }) }}
            </NxMenuItem>
            <NxContextMenuSeparator />
            <NxMenuItem @select="onOpenMenuSelect('new-split')">
              {{ i18nInjection.t('common.table.row_menu.open_split') }}
            </NxMenuItem>
            <NxMenuItem @select="onOpenMenuSelect('workspace')">
              {{ i18nInjection.t('common.table.row_menu.open_workspace') }}
            </NxMenuItem>
          </template>
        </template>
        <NxContextMenuSeparator
          v-if="
            rowContextIdx != null &&
            (isRowMovableAt(rowContextIdx) || canOfferDeleteAt(rowContextIdx))
          "
        />
      </template>
      <NxMenuItem
        v-if="rowContextIdx != null && isRowMovableAt(rowContextIdx)"
        @select="onMoveToPositionMenuSelect"
      >
        {{ i18nInjection.t('common.table.row_menu.move_to_position') }}
      </NxMenuItem>
      <NxMenuItem
        v-if="rowContextIdx != null && canOfferDeleteAt(rowContextIdx)"
        @select="onDeleteMenuSelect"
      >
        {{ i18nInjection.t('common.table.row_menu.delete') }}
      </NxMenuItem>
    </NxMenuDropdown>
  </div>
  <NxTableMoveToPositionDialog
    v-model:open="moveToPosOpen"
    :max="moveToPosMax"
    @submit="applyMoveToPosition"
  />
</template>

<style scoped>
* {
  padding: 0;
  box-sizing: border-box;
  color: var(--nxt-table--fg, var(--nxt-fg));
}

/* ── nx-table logical tokens — map table roles onto the global --nxt-* contract ──
   Override any of these on a consumer to retheme the table without touching rules. */
.wrapper {
  /* text + type — dedicated table tokens so the schema designer can retune them */
  --nxt-table--fg: var(--nxt-table-cell-fg);
  --nxt-table--fg-muted: var(--nxt-table-cell-muted);
  --nxt-table--header-fg: var(--nxt-table-header-fg);
  --nxt-table--sort: var(--nxt-table-sort-icon);
  --nxt-table--size: var(--nxt-table-size);
  /* Width of the indicator and checkbox columns; pinned columns start behind them. */
  --nxt-table--gutter-w: calc(2.125em / 0.875);

  /* surfaces */
  --nxt-table--bg: var(--nxt-table-row-bg);
  --nxt-table--hover-bg: var(--nxt-table-row-hover-bg);
  --nxt-table--filter-bg: var(--nxt-table-filter-bg);

  --nxt-table--header-bg: var(
    --nxt-table-header-bg
  ); /* neutral chrome — was --nxt-brand-subtle-bg */
  /* header icons at rest (the column filter triggers) */
  --nxt-table--header-icon: var(--nxt-fg-subtle);
  /* the track of the always-visible scrollbars: the neutral header colour —
     the role token, so a brand-filled header never frames the table */
  --nxt-table--scrollbar-bg: var(--nxt-table-header-bg);
  /* where a body cell's content sits in a row taller than it (a title with
     its description): `center`, or `flex-start` under `table.cell_align:
     top`. Edit cells that lay out their own content read it too, so a cell
     does not move when it switches between display and edit. */
  --nxt-table--cell-valign: center;
  /* The space a body cell keeps above and below its content: the edit inset
     in an edit grid, the cell padding otherwise. (One line of body text,
     `--nxt-table--line`, is set on the body.) */
  --nxt-table--cell-inset: calc(var(--nxt-table--size) * 0.25 / 0.875);
  /* The first line of a cell under `table.cell_align: top`, set only there:
     `--nxt-table--first-line` is that line inside a cell's content,
     `--nxt-table--line-band` the band from the row's top through it (inset,
     line, inset). Badges, icon buttons and the row's gutters centre on them,
     so they line up with the first line of text beside them. Unset in the
     middle, so `var(--nxt-table--first-line, fallback)` keeps the centred
     layout there. */
  --nxt-table--first-line: initial;
  --nxt-table--line-band: initial;
  /* `rowStyle` `fontWeight` belongs to its own row: a table nested in a styled
     row's cell (an inline relation-picker dialog) starts without it. */
  --row-font-weight: initial;
  /* cursor / selected / dragged row */
  --nxt-table--row-active-bg: var(--nxt-table-row-active-bg);
  /* zebra stripe fill for even rows — the `.zebra-stripes` class (the user's
     `table.zebra` setting) sets --nxt-table--stripe-bg to the zebra token;
     without it the stripe equals the base row bg (invisible). */
  --nxt-table--stripe-bg: var(--nxt-table-row-bg);

  &.zebra-stripes {
    --nxt-table--stripe-bg: var(--nxt-table-zebra-bg);
  }
  &:not(.horizontal-lines) .tbody .tr {
    --row-border-bottom: 0;
  }
  &.vertical-lines .th:not(.last-col),
  &.vertical-lines .td:not(.last-col) {
    border-right: var(--nxt-hairline) solid var(--nxt-table--border);
  }
  /* `table.header: brand` — the header row filled with the brand colour, its
     text, sort marks, filter triggers and pinned-column rules in the colour
     on it. */
  &.header-brand {
    --nxt-table--header-bg: var(--nxt-brand-bg);
    --nxt-table--header-fg: var(--nxt-brand-fg);
    --nxt-table--sort: var(--nxt-brand-fg);
    --nxt-table--header-icon: color-mix(in srgb, var(--nxt-brand-fg) 75%, transparent);
    /* The summary row shares the brand fill; green or red ink would vanish on it. */
    --nxt-table--summary-positive: var(--nxt-brand-fg);
    --nxt-table--summary-negative: var(--nxt-brand-fg);
  }
  /* On the brand fill the header's controls hover in a veil of the colour on
     it (a light fill would swallow their light icons) and focus in that colour
     (a ring in the brand or accent colour would vanish on it). The label row
     only — the quick filter row below sits on its own neutral fill — and this
     table's own: a table nested in a cell keeps its look. */
  &.header-brand > .table > .thead > .tr:not(.filter-row) {
    --nxt-table--border: color-mix(in srgb, var(--nxt-brand-fg) 25%, transparent);
    --nxt-table--control-bg-hover: color-mix(in srgb, var(--nxt-brand-fg) 18%, transparent);
    --nxt-table--focus: var(--nxt-brand-fg);
  }
  /* `table.cell_align: top` — in a row taller than one line (a title with its
     description) every cell starts at the top instead of the middle. */
  &.edit-mode {
    --nxt-table--cell-inset: var(--nxt-space-xs);
  }
  /* Two 1lh product lines fit the 48px comfortable contract without being
     clipped; compact rows remain a single centered line. */
  &.edit-mode.uniform-rows {
    --nxt-table--cell-inset: var(--nxt-space-2xs);
  }
  &.cells-top {
    --nxt-table--cell-valign: flex-start;
  }
  /* on the body, where `--nxt-table--line` holds the cells' line — this
     table's own body, not one of a table nested in a cell */
  &.cells-top > .table > .tbody {
    --nxt-table--first-line: var(--nxt-table--line);
    --nxt-table--line-band: calc(var(--nxt-table--line) + 2 * var(--nxt-table--cell-inset));
  }
  & .tbody .td {
    align-items: var(--nxt-table--cell-valign);
  }

  /* grid lines */
  --nxt-table--border: var(--nxt-table-border); /* row + column separators, outer frame */

  /* cursor + selection accent */
  --nxt-table--cursor: var(--nxt-brand-bg); /* focused-cell ring + active-row marker */
  --nxt-table--save-success: var(--nxt-success-bg); /* saved check, top-right corner */
  --nxt-table--save-error: var(--nxt-danger-bg); /* failed-position ring */
  /* summary totals inked by direction: the role fill is the coloured ink, its
     `-fg` is the text ON that fill (white in light themes) */
  --nxt-table--summary-positive: var(--nxt-success-bg);
  --nxt-table--summary-negative: var(--nxt-danger-bg);
  --nxt-table--selected: var(--nxt-brand-bg); /* selected-row marker */
  --nxt-table--passive: color-mix(
    in srgb,
    var(--nxt-brand-bg) 50%,
    transparent
  ); /* dimmed marker when table unfocused */

  /* resize / drag handles */
  --nxt-table--handle: var(--nxt-border); /* resize + border pill */
  --nxt-table--handle-hover: var(--nxt-border-strong); /* pill on hover */

  /* header controls (buttons) */
  --nxt-table--control-bg: transparent;
  --nxt-table--control-bg-hover: var(--nxt-brand-subtle-bg);
  --nxt-table--focus: var(--nxt-focus-ring); /* button focus outline: the kit's ring */

  /* overlay */
  --nxt-table--overlay-bg: var(--nxt-brand-subtle-bg); /* "Lade Daten…" pill */
}
.th.first-col :deep(.nx-dropdown-anchor) {
  width: fit-content;
}
/* The gear stays centred even when the anchor is stretched anyway: a table in a
   dialog opened from a cell (Artikel auswählen) sits inside the outer table's
   `.cell-edit .nx-ghost-cell`, whose fill rules reach the anchor and win
   (BLP/nx#2819). */
.th.first-col .button {
  margin-inline: auto;
}

.row-context-host {
  position: fixed;
  z-index: var(--nxt-z-dropdown);
  width: 0;
  height: 0;
}
.row-context-anchor {
  display: block;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
}
.fs-shell:not(.is-fullscreen) {
  display: contents;
}
.fs-shell.is-fullscreen {
  position: fixed;
  inset: 0;
  z-index: var(--nxt-z-modal);
  display: flex;
  flex-direction: column;
  padding: var(--nxt-space-md);
  box-sizing: border-box;
}
.fs-scrim {
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--nxt-modal-overlay-bg) 88%, transparent);
  backdrop-filter: blur(calc(var(--nxt-size-unit) * 0.875));
  -webkit-backdrop-filter: blur(calc(var(--nxt-size-unit) * 0.875));
}
.fs-close {
  position: relative;
  z-index: var(--nxt-z-raised);
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  margin-bottom: var(--nxt-space-xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg-muted);
  box-shadow: var(--nxt-shadow-sm);
  cursor: pointer;
  line-height: 0;
}
.fs-close:hover {
  color: var(--nxt-fg);
  background: var(--nxt-surface-sunken-bg);
}
.fs-shell.is-fullscreen .wrapper {
  position: relative;
  z-index: var(--nxt-z-raised);
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  border-radius: var(--nxt-radius-md);
  box-shadow: var(--nxt-shadow-lg);
  overflow: hidden;
}
.wrapper {
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  position: relative;
  /* Filter chip bar + table stack vertically; the table takes the rest. */
  display: flex;
  flex-direction: column;
  /* Let .table own both axes — overflow-y:hidden here trapped horizontal scroll. */
  overflow: hidden;
  outline: none;
}
/* `fill`: grow into the container's free height and never past it, so the
   rows scroll inside .table instead of stretching the page. */
.wrapper.is-fill {
  flex: 1 1 auto;
  max-height: 100%;
}
.table {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  /* Both axes: wide positions grids need reliable overflow-x (trackpad / shift-wheel). */
  overflow: auto;
  /* The virtualizer owns scroll anchoring; native anchoring fights its window updates. */
  overflow-anchor: none;
  /* Contain sideways rubber-banding; allow vertical chain to the page scroller. */
  overscroll-behavior-x: contain;
  overscroll-behavior-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  /* Card surface (the baseline, per design.css): a rounded, framed table.
     overflow:auto clips the sticky header's top corners and the last row's
     bottom corners to the radius. Hairline flattens it below (I-555). */
  border: var(--nxt-hairline) solid var(--nxt-table--border);
  border-radius: var(--nxt-radius-md);
}
/* `overscrollY: 'contain'`: the wheel stops at the table's end (a working surface). */
.table.overscroll-contain {
  overscroll-behavior-y: contain;
}
[data-nx-surface='hairline'] .table {
  border: none;
  border-radius: 0;
}

/* Forced classic scrollbars (Einstellungen → „Scrollbars dauerhaft anzeigen“).
   Important: do NOT set scrollbar-width / scrollbar-color here — Chromium then
   ignores ::-webkit-scrollbar and falls back to macOS overlay bars (invisible
   until scroll). Styled webkit pseudos keep gutters permanently visible. */
.table.always-scrollbars {
  overflow-x: scroll;
  overflow-y: scroll;
  /* Reset Connect's html { scrollbar-width: thin } inheritance — any non-auto
     value disables ::-webkit-scrollbar and leaves macOS overlay bars. */
  scrollbar-width: auto;
  scrollbar-color: auto;
}
.table.always-scrollbars::-webkit-scrollbar {
  width: calc(var(--nxt-size-unit) * 0.75);
  height: calc(var(--nxt-size-unit) * 0.75);
  background: var(--nxt-table--scrollbar-bg);
}
.table.always-scrollbars::-webkit-scrollbar-track {
  background: var(--nxt-table--scrollbar-bg);
}
.table.always-scrollbars::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--nxt-fg-muted) 85%, transparent);
  border-radius: var(--nxt-radius-md);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-table--scrollbar-bg);
  min-height: var(--nxt-control-size-xs);
  min-width: var(--nxt-control-size-xs);
}
.table.always-scrollbars::-webkit-scrollbar-thumb:hover {
  background: var(--nxt-fg-muted);
}
.table.always-scrollbars::-webkit-scrollbar-corner {
  background: var(--nxt-table--scrollbar-bg);
}
/* Firefox: no webkit pseudos — solid non-transparent colors keep bars visible. */
@supports not selector(::-webkit-scrollbar) {
  .table.always-scrollbars {
    scrollbar-width: thin;
    scrollbar-color: var(--nxt-fg-muted) var(--nxt-table--scrollbar-bg);
  }
}

/* Empty state: the table collapses to its header, the shared empty block
   fills the remaining body area — continuing the card frame seamlessly. */
.table.empty-collapsed {
  flex: 0 0 auto;
  border-bottom: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.table.empty-collapsed .tbody {
  display: none;
}
.table-empty {
  flex: 1 1 auto;
  min-height: var(--nxt-area-size-lg);
  display: grid;
  place-items: center;
  border: var(--nxt-hairline) solid var(--nxt-table--border);
  border-top: none;
  border-radius: 0 0 var(--nxt-radius-md) var(--nxt-radius-md);
  background-color: var(--nxt-table--bg);
}
[data-nx-surface='hairline'] .table-empty {
  border: none;
  border-radius: 0;
}
.thead {
  /* box-shadow: var(--nxt-shadow-sm); */
  position: sticky;
  top: 0;
  z-index: calc(var(--nxt-z-above) + 1);
  color: var(--nxt-table--header-fg);
  font-weight: var(--nxt-font-weight-semibold);
  /* strong divider between header and body */

  & .tr {
    background-color: var(--nxt-table--header-bg);
    /* The scoped `*` rule pins every element to --nxt-table--fg, so the
       header text never inherited the colour above. Re-point the variable on
       the label row (not the filter row, which sits on the filter surface). */
    &:not(.filter-row) {
      --nxt-table--fg: var(--nxt-table--header-fg);
    }
    &:last-of-type {
      border-bottom: var(--nxt-hairline) solid var(--nxt-border-strong);
    }
    & > .first-col,
    & > .select-col,
    & > .last-col {
      background-color: var(--nxt-table--header-bg);
    }
  }
}
.col-drop-zone {
  position: absolute;
  top: 0;
  /* overflow out of .thead into .tbody so the entire table height is a drop target */
  height: 100vh;
  /* fallbacks */
  left: 0;
  right: 0;
}
.row-drop-zone {
  position: absolute;
  left: 0;
  right: 0;
  /* fallbacks */
  top: 0;
  bottom: 0;
  z-index: var(--nxt-z-above);
}
.thead,
.tbody {
  width: max-content;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  font-size: var(--nxt-table--size);
}
.table-empty.summary-empty {
  border: none;
  border-radius: 0;
}
.tr.summary-row {
  font-size: var(--nxt-table--size);
  position: sticky;
  bottom: 0;
  z-index: calc(var(--nxt-z-above) + 1);
  flex-shrink: 0;
  font-weight: var(--nxt-font-weight-bold);
  --nxt-table--fg: var(--nxt-table--header-fg);
  --row-bg: var(--nxt-table--header-bg);
  background-color: var(--nxt-table--header-bg);
  border-top: var(--nxt-hairline) solid var(--nxt-border-strong);
}
.tr.summary-row:hover {
  --row-bg: var(--nxt-table--header-bg);
}
.summary-row > .td {
  background-color: inherit;
}
.summary-row > .td.align-end {
  text-align: end;
}
.summary-row > .summary-positive {
  --nxt-table--fg: var(--nxt-table--summary-positive);
}
.summary-row > .summary-negative {
  --nxt-table--fg: var(--nxt-table--summary-negative);
}
/* The row's caption (see `measureSummaryLabel`): laid from where the data
   columns start over the leading empty summary cells, above the pinned ones,
   in the row's fill so the run reads as one cell. Above `.td.first-col` in
   weight, which sits later and would put the gutter level with a pinned
   column again — that column then painted over the caption's start. */
.tr.summary-row > .td.summary-gutter {
  z-index: calc(var(--nxt-z-above) + 1);
}
.summary-label {
  position: absolute;
  inset-block: 0;
  left: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding-inline: calc(0.625em / 0.875);
  white-space: nowrap;
  overflow: hidden;
  background-color: inherit;
  pointer-events: none;
}
.summary-label.collapsed {
  visibility: hidden;
}
.tbody {
  flex-grow: 1;
  /* Body cells read as data in regular weight; the header carries the weight. */
  font-weight: var(--nxt-font-weight-regular);
  position: relative;
  /* One line of body text (see the registration below). */
  --nxt-table--line: 1lh;
}
/* Registered so `1lh` resolves on the body, at the cells' font and line
   height, and inherits as a length — a badge or button with its own font size
   still gets the cells' line. */
@property --nxt-table--line {
  syntax: '<length>';
  inherits: true;
  initial-value: 0px;
}
.pagination-handle-anchor {
  position: relative;
  height: 0;
  flex-shrink: 0;
}
.virtual-spacer {
  flex-shrink: 0;
}
/* Windowed/sparse: one light shell instead of N heavy edit cells. */
.tr-placeholder {
  pointer-events: none;
  opacity: 0.55;
}
/* As wide as the columns (`userColumnsWidth`), so its row-action cell stands
   where the real rows' does. */
.td-placeholder-fill {
  flex: 0 0 auto;
  min-width: 0;
  align-items: center;
  padding-inline: var(--nxt-space-sm);
}
.placeholder-shimmer {
  display: block;
  height: 0.7em;
  width: min(40%, var(--nxt-panel-width-lg));
  border-radius: var(--nxt-radius-sm);
  /* `--nxt-bg-subtle` / `--nxt-bg-muted` do not exist — those are `--nx-*` names
     with the new prefix, so only the hex fallbacks ever rendered (I-771). The
     sweep is a border-weight grey brightening to the sunken surface. */
  background: linear-gradient(
    90deg,
    var(--nxt-border) 0%,
    var(--nxt-surface-sunken-bg) 50%,
    var(--nxt-border) 100%
  );
  background-size: 200% 100%;
  animation: nx-placeholder-shimmer 1.2s ease-in-out infinite;
}
@keyframes nx-placeholder-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
.tr {
  /* Per-row overridable borders: consumers set --row-border-top/-bottom via the
     `rowStyle` prop. Defaults keep the original look (no top border, 1px bottom
     separator). Color is fixed to the table border token. */
  border-top: var(--row-border-top, 0) solid var(--nxt-table--border);
  border-bottom: var(--row-border-bottom, var(--nxt-hairline)) solid var(--nxt-table--border);
  /* Per-row weight: consumers set --row-font-weight via the `rowStyle` prop
     (`fontWeight`). Without it the row inherits its group's weight (semibold
     header, regular body); the summary row's own rule outranks this one. */
  font-weight: var(--row-font-weight, inherit);
  /* min-height: calc(2.5em / 0.875);
  max-height: calc(2.5em / 0.875); */
  display: flex;
  flex-direction: row;
  /* Wide grids: row must be as wide as its columns (not viewport-clamped).
     Without max-content, flex cells shrink and .table never gets overflow-x. */
  width: max-content;
  min-width: 100%;

  &.last-empty {
    opacity: 0.5;
    flex-grow: 1;
    max-height: unset;
  }

  /* Row base fill via a local var so zebra can swap it per-row while the
     active-row rule below (which sets background-color directly) always wins.
     `--row-bg-override` is the consumer seam (set by the `rowStyle` prop): it
     replaces base/zebra but is intentionally NOT referenced by :hover, so hover
     still wins over a custom row color. */
  --row-bg: var(--row-bg-override, var(--nxt-table--bg));
  background-color: var(--row-bg);
  /* Direct children only: a nested NxTable (e.g. the relation-picker modal,
     which is an inline <dialog> and stays a DOM descendant of the cell it was
     opened from) shares this component's scope hash, so a descendant selector
     would repaint the inner table's sticky columns from the outer row. */
  & > .first-col,
  & > .select-col,
  & > .last-col {
    background-color: var(--row-bg);
  }

  /* Zebra stripe — even body rows (2nd, 4th…); fill is --nxt-table--stripe-bg. */
  &.stripe {
    --row-bg: var(--row-bg-override, var(--nxt-table--stripe-bg));
  }

  /* Hover via --row-bg (ignoring --row-bg-override) so it overrides a custom row
     color, while the active-row rule (direct background-color) still wins. */
  &:hover {
    --row-bg: var(--nxt-table--hover-bg);
  }

  &[data-dragged='true'],
  &.cursor,
  &.selected {
    background-color: var(--nxt-table--row-active-bg);

    & > .first-col,
    & > .select-col,
    & > .last-col {
      background-color: var(--nxt-table--row-active-bg);
    }
  }
  /* cursor listed last so the keyboard cursor wins when a row is both.
     An inset shadow, not a border: the first column is a fixed-width border-box,
     so a border that only exists on selection narrowed it and shifted its
     content on every (multi-)select click. */
  &.selected > .first-col {
    box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--selected);
  }
  &.cursor > .first-col {
    box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--cursor);
  }
}
/* layout="list": one full-width card per row (mail inbox). The gutters go, the
   column fills the row, and the selection marker moves to the row edge. */
.wrapper.list-layout .tr {
  width: 100%;
}
/* The cards take the table's width and cut their text with an ellipsis: the
   grid's max-content body would size every row to its longest nowrap line. */
.wrapper.list-layout .tbody {
  width: 100%;
}
.wrapper.list-layout .tr > .first-col,
.wrapper.list-layout .tr > .select-col,
.wrapper.list-layout .tr > .last-col {
  display: none;
}
.wrapper.list-layout .td {
  flex: 1 1 auto;
  padding: 0;
}
.wrapper.list-layout .tbody > .tr.selected {
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--selected);
}
.wrapper.list-layout .tbody > .tr.cursor {
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--cursor);
}
.wrapper.list-layout.passive-selection .tbody > .tr.cursor,
.wrapper.list-layout.passive-selection .tbody > .tr.selected {
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--passive);
}
.wrapper.uniform-rows .tbody > .tr {
  flex: 0 0 auto;
  /* clip, never hidden: `hidden` makes the row a scroll container, and the
     sticky first/last columns then stick to the row instead of the table —
     the row-action (delete) column scrolled out of view horizontally. */
  overflow: clip;
}
.wrapper.uniform-rows .tbody > .tr > .td {
  min-height: 0;
  overflow: hidden;
}
/* Centres a row's box in the checkbox column and makes the whole cell its
   click target. */
.select-box {
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
/* Drag selection: no text selection and no text cursor while rows are marked. */
.wrapper.drag-selecting .tbody {
  user-select: none;
  cursor: default;
}
/* Checkbox lists: the indicator and checkbox columns read as one gutter, and
   a ticked row gets a lighter fill than the active row — ticking marks rows for
   a bulk action, it does not activate them. Only the active row keeps the left
   marker. */
.wrapper.checkbox-selection .first-col {
  border-right: none;
}
.wrapper.checkbox-selection .tbody > .tr.selected:not(.cursor):not([data-dragged='true']) {
  --row-ticked-bg: color-mix(in srgb, var(--nxt-table--row-active-bg) 55%, var(--nxt-table--bg));
  background-color: var(--row-ticked-bg);

  & > .first-col,
  & > .select-col,
  & > .last-col {
    background-color: var(--row-ticked-bg);
  }
  & > .first-col {
    box-shadow: none;
  }
}
/* A record target is not a row-reorder insertion zone. The inset frame adds
   no size and stays visible over selection, hover and sticky gutter cells. */
.tbody > .tr.is-drop-target {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
.native-drop-status {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
/* The expand toggle (expandableRows) sits in the indicator gutter, at the table's edge. */
.expand-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(1.5em / 0.875);
  height: calc(1.5em / 0.875);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-table--fg-muted);
  cursor: pointer;

  & > svg {
    width: 1em;
    height: 1em;
    transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
  }
  &[aria-expanded='true'] > svg {
    transform: rotate(90deg);
  }
  &:hover {
    background: var(--nxt-table--hover-bg);
    color: var(--nxt-table--fg);
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
  }
}
@media (prefers-reduced-motion: reduce) {
  .expand-toggle > svg {
    transition: none;
  }
}
/* An open row's detail: the consumer's content below its row, as wide as the
   visible table while the grid scrolls sideways. No hover, stripe or selection
   fill — it is no data row. */
.tbody > .tr.detail-row,
.tbody > .tr.detail-row:hover {
  --row-bg: var(--nxt-table--bg);
}
.detail-cell {
  position: sticky;
  left: 0;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  font-weight: var(--nxt-font-weight-regular);
}
/* Group header rows (groupBy) are titles, not data rows: no hover, stripe or
   selection fill, and no separator — the header draws its own rule line. */
.tbody > .tr.group-row,
.tbody > .tr.group-row:hover {
  --row-bg: var(--nxt-table--bg);
  --row-border-bottom: 0;
}
/* same as above (s. @indicator) but with .wrapper.passive-selection root-wrapper */
.wrapper.passive-selection .tr.cursor > .first-col {
  color: var(--nxt-table--passive);
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--passive);
  & svg {
    color: var(--nxt-table--passive);
  }
}
.wrapper.passive-selection .tr.selected > .first-col {
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-table--passive);
}
.fallback-cell {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
/* View-mode cell focus anchor. Fills the td so clicks on cell padding
   land focus inside the table (keeping `tableFocused` true). The visual
   cursor is shown via `.td.cursor` on the parent — this anchor itself
   stays unstyled.
   Flex + center matches `.nx-ghost-cell` so a display-only value (Gesamt)
   sits on the same vertical band as neighboring edit inputs when the row
   is taller than one line (Bezeichnung title + description). */
.cell-anchor {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  outline: none;
  display: flex;
  flex-direction: column;
  justify-content: var(--nxt-table--cell-valign);
  align-items: stretch;
}
.cell-anchor > * {
  min-width: 0;
  max-width: 100%;
}
/* A row link (`rowHref`): the cell anchor is an <a>, drawn as the plain cell it was. */
a.cell-anchor.row-link {
  color: inherit;
  text-decoration: none;
  cursor: inherit;
}
/* Edit host: same flex footprint as `.cell-anchor` so lazyEditCells can
   swap display↔edit without changing how the td packs its content.
   Always a column flex so `align-items: stretch` is the cell WIDTH — a row
   flex left native inputs at the default ~20ch. */
.cell-edit {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
}
.cell-edit.layout-row,
.cell-edit.layout-column {
  justify-content: var(--nxt-table--cell-valign);
}
/* Edit/create/filter *fields* fill the column. Scoped to `.nx-ghost-cell` so
   content-sized widgets (Typ chip + gear) are not stretched to 100% and
   crushed. Create/filter inputs without a ghost cell still fill, except
   `[data-nx-cell-fit='content']`. */
.cell-edit :deep(.nx-ghost-cell .input-wrapper),
.cell-edit :deep(.nx-ghost-cell .nx-dropdown-anchor),
.cell-edit :deep(.nx-ghost-cell .fake-input),
.cell-edit :deep(.nx-ghost-cell input:not([type='checkbox']):not([type='radio'])),
.cell-edit :deep(.nx-ghost-cell textarea) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
/* Shared edit-mode inset — MUST be identical on display and edit hosts.
   Previously only `.cell-anchor` got the padding, so the active (cursor)
   row's edit widgets sat tighter against the cell edge than the passive
   display rows. */
.cell-anchor.edit-inset,
.cell-edit.edit-inset {
  padding: var(--nxt-table--cell-inset) var(--nxt-space-sm);
}
/* In edit-mode the display/edit hosts own the one canonical inset. Keeping the
   outer td padding as well doubled the gutter and made lazy display→edit swaps
   appear to move even though both hosts had identical padding. */
.td.body-edit-cell {
  padding: 0;
}
/* `table.cell_align: top`: the row's gutters (marker, checkbox, row actions)
   centre on the first line's band instead of the row's middle, level with
   the text beside them. Not in the list layout, which has no gutters. */
.wrapper.cells-top:not(.list-layout)
  > .table
  > .tbody
  > .tr
  > :is(.first-col, .select-col, .last-col) {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: var(--nxt-table--line-band);
  align-content: start;
  align-items: center;
  justify-content: center;
}
.thead .filter-row {
  background-color: var(--nxt-table--filter-bg);
  /* Its fields are empty search fields: under a field look without a frame at
     rest (data-nx-input: flat) they vanished on the row's fill, and the
     resting border is too faint on it. The row frames them in the fields'
     stronger border, the one a field shows on hover (I-2510). */
  --nxt-input-border: var(--nxt-border-strong);
  & > .first-col,
  & > .select-col,
  & > .last-col {
    background-color: var(--nxt-table--filter-bg);
  }
  .th {
    overflow: visible;
  }
}
.th,
.td {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: calc(0.25em / 0.875) calc(0.625em / 0.875);
  /* Fixed column widths must not collapse when the viewport is narrower than
     the sum of columns — that was the half-scroll symptom on positions. */
  flex-shrink: 0;
  box-sizing: border-box;
  &[data-dragged='true'] {
    background-color: var(--nxt-table--row-active-bg);
  }
  &.first-col {
    padding: 0;
    justify-content: center;
    /* background-color: var(--nxt-table--header-bg); */
    min-width: var(--nxt-table--gutter-w);
    max-width: var(--nxt-table--gutter-w);
    position: sticky;
    left: 0;
    z-index: var(--nxt-z-above);
    /* inward-facing border of the left sticky column */
    border-right: var(--nxt-hairline) solid var(--nxt-table--border);
    /* box-shadow: var(--nxt-shadow-sm); */
  }
  /* Checkbox column (rowSelectionMode="checkbox"): as wide as the indicator
     column and pinned right behind it. */
  &.select-col {
    padding: 0;
    justify-content: center;
    min-width: var(--nxt-table--gutter-w);
    max-width: var(--nxt-table--gutter-w);
    position: sticky;
    left: var(--nxt-table--gutter-w);
    z-index: var(--nxt-z-above);
    border-right: var(--nxt-hairline) solid var(--nxt-table--border);
    /* Shift+click marks a range; it must not paint a text selection too. */
    user-select: none;
  }
  /* actions */
  &.last-col {
    padding: 0;
    justify-content: center;
    /* background-color: var(--nxt-table--header-bg); */
    /* 34px (the gutter) for last-col-size 1 and 54px=34px+20px=34px+1.25em/0.875 for last-col-size 2 */
    --width: calc(var(--nxt-table--gutter-w) + (var(--last-col-size) - 1) * calc(1.25em / 0.875));
    min-width: var(--width);
    max-width: var(--width);
    gap: calc(var(--nxt-space-2xs) / 0.875);
    /* At the row's far end, at the table's right edge however wide the table
       is — on a wide screen with room between it and the last column
       (BLP/nx#2556, approved by Jens on the issue; I-2510 had put it right
       after the last column). Sticky, so while the table scrolls sideways it
       stays in view at the right edge, over the cells — then with an edge
       (`.content-right` below). */
    margin-left: auto;
    position: sticky;
    right: 0;
    z-index: var(--nxt-z-above);
    /* inward-facing border of the right sticky column */
    border-left: var(--nxt-hairline) solid var(--nxt-table--border);
  }
}
/* While cells run on under the pinned row-action column, its edge casts a soft
   shadow onto them: it reads as pinned to the table's edge, not as a cell
   hanging in the middle of the figures (I-2510). */
.wrapper.content-right > .table > :is(.thead, .tbody) > .tr > .last-col {
  box-shadow: calc(-1 * var(--nxt-space-xs)) 0 var(--nxt-space-sm) calc(-1 * var(--nxt-space-xs))
    color-mix(in srgb, var(--nxt-fg) 14%, transparent);
}
.th:has(.resize-handle) {
  overflow: visible;
  position: relative;
}
/* The cursor cell's ring is for finding your place while moving through the
   grid by keyboard. It had grown to 2px/72% with the green save flash on the
   cell (I-1010); that flash moved to the header check (I-2172), so the ring is
   back to its quiet 1px — and a field being edited drops it altogether: its own
   caret shows where the focus is, a frame around it only looked heavy
   (BLP/nx#2236). */
.td.cursor {
  position: relative;
  box-shadow: inset 0 0 0 var(--nxt-hairline)
    color-mix(in srgb, var(--nxt-table--cursor) 50%, transparent);
  &.passive {
    box-shadow: inset 0 0 0 var(--nxt-hairline)
      color-mix(in srgb, var(--nxt-table--cursor) 20%, transparent);
  }
  &:has(:is(input, textarea, select, [contenteditable='true']):focus) {
    box-shadow: none;
  }
}
/* A successful save is not painted on the cell: one check flashes in the
   top-right header cell instead (`showSaved`), opposite the settings gear, at
   the cell's right edge. A create button fills that cell already, so there the
   check takes the button's place for the flash instead of half covering it; the
   button stays in place, focusable and clickable. */
.th.last-col .saved-check {
  position: absolute;
  top: 50%;
  right: var(--nxt-space-2xs);
  display: inline-flex;
  color: var(--nxt-table--save-success);
  opacity: 0;
  transform: translateY(-50%);
  transition: opacity 300ms ease-out;
  pointer-events: none;
}
.th.last-col .saved-check.is-visible {
  opacity: 1;
  transition-duration: 120ms;
}
.th.last-col .saved-check.over-create {
  right: 50%;
  transform: translate(50%, -50%);
}
.th.last-col > .button {
  transition: opacity 300ms ease-out;
}
.th.last-col > .button.is-covered {
  opacity: 0;
  transition-duration: 120ms;
}
.th.last-col .saved-announcement {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
/* A failed aggregate position write belongs to the row. Paint one stable ring
   above sticky and regular cells instead of inserting a red button into the
   editor that happened to initiate the write. */
.tr[data-row-save-status='error'] {
  position: relative;
}
.tr[data-row-save-status='error']::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: calc(var(--nxt-z-above) + 2);
  pointer-events: none;
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-table--save-error);
}
.td.layout-row {
  flex-direction: row;
  align-items: center;
}
.td.layout-column {
  flex-direction: column;
  /* Stretch so the edit input fills the column. A shrink-wrapped <input>
     otherwise stays ~20ch with empty space to the next column. */
  align-items: stretch;
  justify-content: center;
}
/* Create-row and any other direct cell payload (no .cell-edit host) must
   fill too — same contract as the edit host. */
.td:not(.first-col):not(.last-col) > *,
.filter-row .th:not(.first-col):not(.last-col) > * {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.td:not(.first-col):not(.last-col) :deep(.nx-ghost-cell .input-wrapper),
.td:not(.first-col):not(.last-col) :deep(.nx-ghost-cell .nx-dropdown-anchor),
.td:not(.first-col):not(.last-col) :deep(.nx-ghost-cell .fake-input),
.filter-row .th:not(.first-col):not(.last-col) :deep(.nx-ghost-cell .input-wrapper),
.filter-row .th:not(.first-col):not(.last-col) :deep(.input-wrapper) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
/* Create-row fields are often a bare input (no ghost cell). Fill those, but
   never content-fit widgets (Typ chip). */
.td:not(.first-col):not(.last-col) :deep(.input-wrapper) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.td :deep([data-nx-cell-fit='content']),
.cell-edit :deep([data-nx-cell-fit='content']) {
  width: auto;
  max-width: 100%;
  min-width: max-content;
  /* Grow on the cell's block axis so Typ/chip sits mid-row; hug width. */
  flex: 1 1 auto;
  align-self: start;
}
.td :deep([data-nx-cell-fit='content'] .input-wrapper),
.td :deep([data-nx-cell-fit='content'] .nx-dropdown-anchor),
.td :deep([data-nx-cell-fit='content'] .nx-popover-anchor),
.td :deep([data-nx-cell-fit='content'] .fake-input),
.cell-edit :deep([data-nx-cell-fit='content'] .input-wrapper),
.cell-edit :deep([data-nx-cell-fit='content'] .nx-dropdown-anchor),
.cell-edit :deep([data-nx-cell-fit='content'] .nx-popover-anchor),
.filter-row :deep([data-nx-cell-fit='content'] .input-wrapper) {
  width: auto;
  max-width: 100%;
  min-width: max-content;
  flex: 0 0 auto;
}

/* Column alignment (align="end"): right-align the header label, the display
   cell text and the edit-input text so numeric/currency columns read down the
   column. Header label and display cell are NxTable's own elements; the edit
   input lives in a child component, hence :deep. */
.th.align-end .fallback-cell {
  margin-left: auto;
  text-align: right;
}
.td.align-end .cell-anchor {
  text-align: right;
}
.th.align-end :deep(input),
.td.align-end :deep(input) {
  text-align: right;
}
.th.align-end :deep(.fake-input),
.td.align-end :deep(.fake-input) {
  text-align: right;
}
/* Push the cell content to the right edge along the flex main/cross axis so the
   alignment shows regardless of the column's layout: in `layout-row` the cells
   flow horizontally (justify). In `layout-column` the host already stretches
   to the cell width; `text-align: right` on the input does the rest — do not
   shrink-wrap with flex-end or the edit field stays ~20ch wide. */
.td.align-end.layout-row {
  justify-content: flex-end;
}
.td.align-end.layout-column {
  align-items: stretch;
}

.resize-handle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: var(--nxt-z-raised);
  /* Fat hit-zone kept fully inside the cell. It can't straddle the border:
     each header carries a `transform` (column-reorder offset), which makes
     every cell its own stacking context, so anything escaping right renders
     under the next column and stops receiving pointer events. The pill is
     then nudged onto the boundary purely visually. */
  width: calc(0.75em / 0.875); /* 12px */
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: col-resize;
}
.resize-handle-pill {
  width: calc(0.1875em / 0.875); /* 3px */
  height: 50%;
  background-color: var(--nxt-table--handle);
  border-radius: var(--nxt-radius-full);
  /* sit the pill on the column boundary (the cell's right edge / border) */
  transform: translateX(50%);
  /* only reveal the pill while hovering the column header */
  opacity: 0;
  transition:
    opacity 0.1s ease,
    background-color 0.1s ease;
}
.th:hover .resize-handle-pill {
  opacity: 1;
}
.resize-handle:hover .resize-handle-pill {
  background-color: var(--nxt-table--handle-hover);
}
.sort-indicator {
  margin-left: auto;
  font-size: var(--nxt-table--size);
  min-width: 2ch;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: var(--nxt-table--sort);
  &.asc svg {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) translateY(calc(100% - 0.25em));
    color: var(--nxt-table--sort);
  }
  &.desc svg {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-100% + 0.25em));
    color: var(--nxt-table--sort);
  }
}
.pagination-handle {
  background-color: transparent;
  pointer-events: none;
}
.loading {
  font-weight: var(--nxt-font-weight-medium);
  position: sticky;
  width: 100%;
  bottom: 0;
  height: 0;
  margin-top: auto;
  & > div {
    position: relative;
  }
  & > div::before {
    content: 'Lade Daten...';
    position: absolute;
    bottom: 1em;
    left: 50%;
    background-color: var(--nxt-table--overlay-bg);
    padding: var(--nxt-space-xs);
    border-radius: var(--nxt-radius-sm);
    width: fit-content;
    transition: all var(--nxt-motion-slow) var(--nxt-ease-in-out);
    pointer-events: none;
  }
  &.hidden > div::before {
    bottom: 0;
    transform: translateY(100%);
    visibility: hidden;
  }
}
/* Active-filter chips section at the top of the gear menu. */
.menu-filter-panel {
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  margin-bottom: var(--nxt-space-2xs);
}

.button {
  font-size: var(--nxt-font-size-md);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background-color: var(--nxt-table--control-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25em;
  height: 1.25em;
  padding: 0.125em;
  cursor: pointer;
  outline-color: var(--nxt-table--focus);
  &:hover {
    background-color: var(--nxt-table--control-bg-hover);
  }
}

.scrollbar-detector {
  width: 100%;
  height: 100%;
  background-color: transparent;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.icon-stack {
  position: relative;
  display: inline-flex;
}
.icon-stack > * {
  display: block;
  transform-origin: center;
}
.icon-stack .outer,
.icon-stack .gap {
  position: absolute;
  inset: 0;
}
.icon-stack .outer {
  transform: scale(1.8);
  z-index: 0;
}
.icon-stack .gap {
  transform: scale(1.5);
  color: var(--nxt-table--bg) !important;
  z-index: var(--nxt-z-raised);
}
.icon-stack .front {
  position: relative;
  z-index: var(--nxt-z-above);
}
.tr:has(> .last-col :focus-visible) > .first-col .row-caret .outer,
.tr:has(> .last-col :focus-visible) > .first-col .row-caret .gap {
  display: none;
}
</style>
